<template>
  <!-- 2026-07-23: tightened page chrome — was mt-4/mb-4 + roomy draft/stepper
       gaps that left large empty bands under the navbar title. -->
  <div class="invoice-create-manual mt-0 mb-2">
    <!-- Duplicate-from-source loading banner -->
    <b-alert
      v-if="duplicateLoading"
      variant="primary"
      show
      class="mb-1 d-flex align-items-center"
    >
      <b-spinner small class="mr-50" />
      <span>Loading invoice to duplicate for next month…</span>
    </b-alert>
    <b-alert
      v-if="duplicateSourceId && !duplicateLoading && !hasSupportingDocuments"
      variant="warning"
      show
      class="mb-1"
    >
      <feather-icon icon="AlertCircleIcon" size="14" class="mr-50" />
      Duplicated from <strong>{{ duplicateSourceId }}</strong> — no source file was copied.
      You can still Create; attach a PDF/PNG/JPG/TXT on Payment if you have one.
    </b-alert>

    <!-- Draft restore banner -->
    <b-alert
      v-if="showDraftBanner"
      variant="info"
      show
      dismissible
      class="invoice-draft-banner mb-1"
      @dismissed="discardDraft"
    >
      <div class="d-flex justify-content-between align-items-center flex-wrap">
        <span class="invoice-draft-banner__msg mr-2">
          <strong>Unsaved Draft Found</strong> — unfinished invoice for
          <strong>{{ _pendingDraft && _pendingDraft.formData && _pendingDraft.formData.client }}</strong>. Resume it?
        </span>
        <div class="flex-shrink-0">
          <b-button size="sm" variant="primary" @click="restoreDraft" class="mr-1">Resume Draft</b-button>
          <b-button size="sm" variant="outline-secondary" @click="discardDraft">Discard</b-button>
        </div>
      </div>
    </b-alert>

    <!-- Auto-save status indicator (server-primary; session = API unavailable) -->
    <div v-if="lastSavedAt" class="text-right mb-50">
      <small class="text-muted">
        <feather-icon icon="CheckIcon" size="12" class="mr-25" />
        Draft auto-saved at {{ lastSavedAt.toLocaleTimeString() }}
        <span v-if="draftPersistSource === 'session'" class="text-warning"> (this session only — server draft API required)</span>
      </small>
    </div>

    <!--
      2026-07-20: converted the previous click-to-open "Preview Invoice"
      modal into a PERSISTENT split view — the user explicitly wants to see
      "how it will look" continuously while filling in the form, not behind
      a button each time. Left column = GenericInvoicePreview (sticky live
      panel); right column = the wizard itself. Always live/visible on normal
      desktop widths (>=lg / 992px). Below that breakpoint a true side-by-side
      split isn't practical, so it degrades to a collapsible panel the user
      can toggle open in place, rather than forcing a modal.
    -->
    <b-row>
      <!-- 2026-07-23: preview-first 8/4 split — live preview sticky on the left
           at 8/12 (wider); wizard steps get the narrower column on the right.
           Mobile stacks with preview (toggle) on top. -->
      <!-- ── Live preview panel ── -->
      <b-col lg="8">
        <!-- Desktop/tablet-landscape (>=lg): permanently visible, sticky so it
             stays in view while the (often taller) form column scrolls. -->
        <div class="d-none d-lg-block live-preview-sticky">
          <div class="d-flex align-items-center justify-content-between preview-toolbar-row">
            <div class="d-flex align-items-center">
              <feather-icon icon="EyeIcon" size="14" class="text-primary mr-50" />
              <span class="small font-weight-bold text-uppercase text-muted">Live Preview</span>
              <b-badge variant="light-info" class="ml-1 preview-live-badge">Updates as you type</b-badge>
            </div>
            <div class="d-flex align-items-center flex-wrap preview-toolbar-controls">
              <!-- Style + Line items live here (2026-07-23) — both shape the
                   preview/PDF; line-item columns used to sit buried in the
                   wizard step question "What line items…?". Labels stay
                   distinct so "Reliance" style ≠ "Reliance" columns. -->
              <small class="text-muted mr-50 flex-shrink-0">Style</small>
              <b-form-select
                v-model="effectivePreviewStyle"
                :options="previewStyleSelectOptions"
                size="sm"
                class="preview-style-select mr-1"
              />
              <small class="text-muted mr-50 flex-shrink-0">Line items</small>
              <!-- :value + @change (not v-model): BV FormSelect re-emits
                   `input` whenever its value prop is updated from outside.
                   Multiple toolbar selects shared one v-model and each
                   sibling re-fired onTemplateSelected → duplicate toasts. -->
              <b-form-select
                :value="effectiveLineItemPreset"
                :options="lineItemPresetSelectOptions"
                size="sm"
                class="preview-line-items-select mr-50"
                :disabled="templateLoading"
                @change="onToolbarLineItemPresetChange"
              />
              <b-spinner v-if="templateLoading" small class="mr-50" />
              <b-button
                aria-label="Edit Columns"
                variant="link"
                size="sm"
                class="text-primary p-0 mr-50 flex-shrink-0"
                @click="openLineItemsConfig('edit')"
              >
                <feather-icon icon="SettingsIcon" size="14" class="mr-25" />Columns
              </b-button>
              <b-button
                aria-label="Download Invoice"
                variant="outline-primary"
                size="sm"
                class="flex-shrink-0"
                :disabled="downloadingPdf"
                @click="downloadInvoicePdf('previewPanelRoot')"
              >
                <b-spinner v-if="downloadingPdf" small />
                <feather-icon v-else icon="DownloadIcon" size="16" />
              </b-button>
            </div>
          </div>
          <div class="live-preview-scroll">
            <!-- A4 stage: true 210×297mm sheet inside, scaled to panel width
                 (transform only — no font-size shrink / layout reflow). -->
            <div ref="previewPanelStage" class="a4-preview-stage">
              <div class="a4-preview-fit" :style="previewPanelFitStyle">
                <div class="a4-preview-scaler" :style="previewPanelScalerStyle">
                  <generic-invoice-preview
                    ref="previewPanelRoot"
                    :template="previewTemplateForDisplay"
                    :document-type="previewDocumentType"
                    :bill-from="previewBillFrom"
                    :bill-to="previewBillTo"
                    :invoice-meta="previewInvoiceMeta"
                    :status="previewInvoiceStatus"
                    :description="formData.description"
                    :line-items="previewLineItemsForGeneric"
                    :totals="previewTotalsForGeneric"
                    :bank-details="previewBankDetailsForGeneric"
                    :logo-url="companyLogoPreview"
                    :columns="lineItemValidationColumns"
                    :columns-table2="usesSeparateTableColumns ? lineItemValidationColumnsTable2 : null"
                    :show-decimals="showDecimals"
                    :show-currency-symbol="showCurrencySymbol"
                    :use-thousand-separators="useThousandSeparators"
                    :use-second-line-items-table="formData.useSecondLineItemsTable"
                    :line-item-table-heading1="formData.lineItemTableHeading1"
                    :line-item-table-heading2="formData.lineItemTableHeading2"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Narrow viewports (<lg): a true side-by-side split isn't practical,
             so this degrades to a collapsible panel the user can toggle open
             in place — still no modal required for the default "see the
             preview while filling the form" experience. -->
        <div class="d-lg-none mb-3">
          <b-button
            block
            variant="outline-primary"
            size="sm"
            @click="showMobilePreview = !showMobilePreview"
          >
            <feather-icon :icon="showMobilePreview ? 'ChevronUpIcon' : 'EyeIcon'" size="14" class="mr-50" />
            {{ showMobilePreview ? 'Hide' : 'Show' }} Live Preview
          </b-button>
          <b-collapse v-model="showMobilePreview" class="mt-2">
            <div class="d-flex justify-content-end align-items-center flex-wrap mb-2 preview-toolbar-controls">
              <small class="text-muted mr-50">Style</small>
              <b-form-select
                v-model="effectivePreviewStyle"
                :options="previewStyleSelectOptions"
                size="sm"
                class="preview-style-select mr-1"
              />
              <small class="text-muted mr-50">Line items</small>
              <b-form-select
                :value="effectiveLineItemPreset"
                :options="lineItemPresetSelectOptions"
                size="sm"
                class="preview-line-items-select mr-50"
                :disabled="templateLoading"
                @change="onToolbarLineItemPresetChange"
              />
              <b-button
                variant="link"
                size="sm"
                class="text-primary p-0"
                aria-label="Edit Columns"
                @click="openLineItemsConfig('edit')"
              >
                <feather-icon icon="SettingsIcon" size="14" class="mr-25" />Columns
              </b-button>
            </div>
            <div ref="previewMobileStage" class="a4-preview-stage">
              <div class="a4-preview-fit" :style="previewMobileFitStyle">
                <div class="a4-preview-scaler" :style="previewMobileScalerStyle">
                  <generic-invoice-preview
                    ref="previewMobileRoot"
                    :template="previewTemplateForDisplay"
                    :document-type="previewDocumentType"
                    :bill-from="previewBillFrom"
                    :bill-to="previewBillTo"
                    :invoice-meta="previewInvoiceMeta"
                    :status="previewInvoiceStatus"
                    :description="formData.description"
                    :line-items="previewLineItemsForGeneric"
                    :totals="previewTotalsForGeneric"
                    :bank-details="previewBankDetailsForGeneric"
                    :logo-url="companyLogoPreview"
                    :columns="lineItemValidationColumns"
                    :columns-table2="usesSeparateTableColumns ? lineItemValidationColumnsTable2 : null"
                    :show-decimals="showDecimals"
                    :show-currency-symbol="showCurrencySymbol"
                    :use-thousand-separators="useThousandSeparators"
                    :use-second-line-items-table="formData.useSecondLineItemsTable"
                    :line-item-table-heading1="formData.lineItemTableHeading1"
                    :line-item-table-heading2="formData.lineItemTableHeading2"
                  />
                </div>
              </div>
            </div>
          </b-collapse>
        </div>
      </b-col>

      <b-col lg="4">
        <!-- Wizard progress -->
        <div class="px-0 preview-toolbar-row">
          <div class="d-flex justify-content-between align-items-center flex-wrap">
            <div class="flex-grow-1 mr-50">
              <!-- Compact step label on xs/sm and again at lg+ (form is only
                   4/12 wide there — five full labels won't fit). Full label
                   strip only on md–<lg when the form stacks full-width. -->
              <div class="small text-muted mb-25 d-md-none d-lg-block">
                Step {{ currentStep + 1 }} of {{ wizardStepLabels.length }}: <strong class="text-primary">{{ wizardStepLabels[currentStep] }}</strong>
              </div>
              <div class="d-none d-md-flex d-lg-none justify-content-between small text-muted mb-25">
                <span v-for="(label, idx) in wizardStepLabels" :key="label" :class="{ 'text-primary font-weight-bold': currentStep === idx }">
                  {{ idx + 1 }}. {{ label }}
                </span>
              </div>
              <b-progress :value="currentStep + 1" :max="wizardStepLabels.length" height="4px" variant="primary" />
            </div>
            <!-- Secondary "zoom" affordance — the live panel to the left (or
                 the collapsible one above, on narrow screens) is now the
                 primary way to see the preview, but a bigger full-screen
                 rendering is still occasionally handy, so this stays as a
                 shortcut into the same modal from any step. -->
            <b-button
              aria-label="Full-screen preview"
              variant="outline-secondary"
              size="sm"
              class="flex-shrink-0"
              @click="showPreviewModal = true"
            >
              <feather-icon icon="Maximize2Icon" size="16" />
            </b-button>
          </div>
        </div>
    <b-card no-body class="shadow-sm border-0 overflow-visible invoice-wizard-card">
      <b-card-body class="py-2 px-2 px-md-3">
        <b-form @submit.prevent="onSubmit">

          <!-- Step 0: Doc Type -->
        <wizard-doc-type-step
          v-if="currentStep === 0"
          :form-data="formData"
          @next="goToClient"
          @cancel="$router.go(-1)"
        />

          <!-- Step 1: Client -->
        <wizard-client-step
          v-if="currentStep === 1"
          :form-data="formData"
          :errors="error"
          :clients="clients"
          :client-master-data="clientMasterData"
          :client-state-options="clientStateOptions"
          :client-legal-name-options="clientLegalNameOptions"
          :client-master-loading="clientMasterLoading"
          :legal-name-is-fallback="legalNameIsFallback"
          @next="goToSattvaLocation"
          @back="currentStep = 0"
          @client-selected="onClientSelected"
          @client-state-selected="onClientStateSelected"
          @legal-name-selected="onLegalNameSelected"
          @add-client="showAddClientModal = true"
        />


        <!-- Step 2: Sattva GST / Location -->
        <wizard-sattva-location-step
          v-if="currentStep === 2"
          :form-data="formData"
          :errors="error"
          :locations="locations"
          :sattva-details-loading="sattvaDetailsLoading"
          :sattva-details-unavailable="sattvaDetailsUnavailable"
          :sattva-save-loading="sattvaSaveLoading"
          @back="currentStep = 1"
          @next="goToLineItems"
          @location-selected="onLocationSelected"
          @save-company-details="onSaveSattvaCompanyDetails"
        />

        <!-- Step 3: Line Items -->
        <wizard-line-items-step
          v-if="currentStep === 3"
          :form-data="formData"
          :categories="categories"
          :vertical-options="verticalOptions"
          :line-items="lineItems"
          :effective-line-item-fields="effectiveLineItemFields"
          :line-item-column-specs="lineItemValidationColumns"
          :line-item-column-specs-table2="lineItemValidationColumnsTable2"
          :line-item-errors="lineItemRowErrors"
          :line-item-error-summary="lineItemErrorSummary"
          :selected-candidate-ids="selectedCandidateIds"
          :sac-code-options="sacCodeOptions"
          :is-candidate-category="isCandidateCategory"
          :is-for-candidates="isForCandidates"
          :is-rpo-template="isRpoTemplate"
          :active-template="activeTemplate"
          :active-template-name="activeTemplateName"
          :visible-calc-rows="visibleCalcRows"
          :subtotal="subtotal"
          :gst-amount="gstAmount"
          :tds-amount="calculatedTdsAmount"
          :total-amount="totalAmount"
          :can-proceed="canProceedPastLineItems"
          :template-loading="templateLoading"
          :show-decimals="showDecimals"
          :show-currency-symbol="showCurrencySymbol"
          :use-thousand-separators="useThousandSeparators"
          :gst-type-options="gstTypeOptions"
          @back="currentStep = 2"
          @next="goToMoreDetails"
          @show-decimals-changed="showDecimals = $event"
          @show-currency-symbol-changed="showCurrencySymbol = $event"
          @use-thousand-separators-changed="useThousandSeparators = $event"
          @add-row="addNewRow"
          @remove="removeLineItem"
          @move-up="moveLineItemUp"
          @move-down="moveLineItemDown"
          @duplicate="duplicateLineItem"
          @update-amount="recomputeLineItem"
          @update-rpo-amount="recomputeLineItem"
          @recompute-row="recomputeLineItem"
          @tag-candidate="openTagCandidateManual"
          @open-candidate-modal="openCandidateModal"
          @manage-columns="openLineItemsConfig('edit')"
          @open-line-items-config="openLineItemsConfig"
          @second-table-columns-same-change="onSecondTableColumnsSameChange"
          @candidate-option-changed="onCandidateOptionChange"
          @category-selected="onCategorySelected"
        />

        <!-- Step 4: More Details -->
        <wizard-more-details-step
          v-if="currentStep === 4"
          :form-data="formData"
          :errors="error"
          :billing-month-options="billingMonthOptions"
          :active-template-name="activeTemplateName"
          :template-is-custom="templateIsCustom"
          :template-loading="templateLoading"
          :payment-terms-options="paymentTermsOptions"
          :gst-type-options="gstTypeOptions"
          :active-template="activeTemplate"
          :invoice-number-loading="invoiceNumberLoading"
          :invoice-number-is-fallback="invoiceNumberIsFallback"
          :sac-code-select-options="sacCodeSelectOptions"
          :lut-no-options="lutNoOptions"
          @back="currentStep = 3"
          @next="goToPayment"
          @invoice-id-edited="onInvoiceIdEdited"
          @terms-selected="onPaymentTermsSelected"
          @regenerate-invoice-number="refreshInvoiceNumber(true)"
        />

        <!-- Step 5: Payment & Submit -->
        <wizard-payment-step
          v-if="currentStep === 5"
          :form-data="formData"
          :tds-amount="calculatedTdsAmount"
          :credit-period-options="creditPeriodOptions"
          :tds-percentage-options="tdsPercentageOptions"
          :loading="loading"
          :is-valid="isFormValid"
          :show-decimals="showDecimals"
          :invoice-files="invoiceFiles"
          @back="currentStep = 4"
          @cancel="$router.go(-1)"
          @preview="showPreviewModal = true"
          @submit="onSubmit"
          @files-changed="onSupportingFilesChanged"
          @file-changed="onSupportingFileChanged"
        />

        <!-- Add Candidates Modal (2026-07-22 polish pass) — real data from
             the ATS's own Candidates module via candidateService (same
             getCandidateJoinedListReadyForInvoice endpoint the older,
             simpler CreateInvoice.vue used for this exact purpose — see
             searchCandidates()/addSelectedCandidates() below). Given a
             proper title/footer (was hide-header/hide-footer with its
             buttons just floating in the body) for consistency with every
             other modal in this flow after the "modal size and CSS" pass
             on Manage Columns et al. -->
        <b-modal
          id="candidate-selection-modal"
          v-model="showCandidateModal"
          title="Add Candidates from ATS"
          size="lg"
          @hidden="onCandidateAtsModalHidden"
        >
          <p class="text-muted small mb-2">Search real candidates with status "Joined" for this client and category, then select one or more to add as line items.</p>
          <b-form-group label="Search Candidate Name" label-for="candidate-search" class="mb-2">
            <b-input-group size="sm">
              <b-input-group-prepend>
                <span class="input-group-text"><feather-icon icon="SearchIcon" size="14" /></span>
              </b-input-group-prepend>
              <b-form-input id="candidate-search" v-model="candidateSearchName" placeholder="Type a candidate name to filter…" @input="searchCandidates" />
            </b-input-group>
          </b-form-group>
          <b-table :items="candidateList" :fields="candidateModalFields" striped hover responsive small class="mb-2 candidate-modal-table">
            <template #cell(selected)="{ item }">
              <b-form-checkbox :checked="item.selected" @change="toggleCandidateSelection(item)" />
            </template>
            <template #cell(revenue)="{ item }">{{ formatCurrency(item.revenue || item.base_amount || 0) }}</template>
          </b-table>
          <b-alert v-if="candidateList.length === 0 && !loading" variant="info" show class="small py-2 px-3 mb-0">
            <feather-icon icon="InfoIcon" size="13" class="mr-1" />No candidates with status "Joined" found for this client/category.
          </b-alert>
          <div v-if="loading" class="text-center py-3"><b-spinner small /></div>
          <template #modal-footer>
            <span class="small text-muted mr-auto">{{ candidateList.filter(c => c.selected).length }} selected</span>
            <b-button variant="secondary" size="sm" @click="showCandidateModal = false">Cancel</b-button>
            <b-button variant="primary" size="sm" @click="addSelectedCandidates" :disabled="!hasSelectedCandidates">Add Selected</b-button>
          </template>
        </b-modal>

        <!-- Line Items Configuration — Preview (column list + sample table)
             + Edit Columns (former Manage Columns editor). -->
        <line-items-config-modal
          :visible.sync="showColumnConfigModal"
          :initial-tab="columnConfigModalTab"
          :preset-value="effectiveLineItemPreset"
          :preset-options="lineItemPresetSelectOptions"
          :preset-label="activeTemplateName"
          :preset-loading="templateLoading"
          :preview-columns="lineItemConfigModalColumns"
          :sample-rows="lineItemConfigSampleRows"
          :using-live-data="lineItemConfigUsingLiveData"
          :show-decimals="showDecimals"
          :forced-computed-keys="forcedComputedColumnKeys"
          @show="onLineItemsConfigShow"
          @hide="onManageColumnsHide"
          @preset-change="onConfigModalPresetChange"
        >
          <template #table-selector>
            <div
              v-if="usesSeparateTableColumns"
              class="d-flex align-items-center flex-wrap mb-2 licm-table-selector"
            >
              <small class="text-muted mr-50 font-weight-bold text-uppercase">Edit columns for</small>
              <b-form-radio-group
                v-model="columnConfigEditTable"
                :options="columnConfigEditTableOptions"
                buttons
                button-variant="outline-primary"
                size="sm"
                @change="onColumnConfigEditTableChange"
              />
            </div>
          </template>
          <template #edit>
            <p class="text-muted small edit-cols-intro">
              Rename, drag to reorder, or retype columns. Use <strong>Visible</strong> to show/hide in the line items table and preview, <strong>Required</strong> for validation, and <strong>Computed</strong> for a formula (off = enter Amount / Base Amount manually).
              <template v-if="usesSeparateTableColumns">
                Currently editing <strong>{{ columnConfigEditTable === 2 ? (formData.lineItemTableHeading2 || 'Table 2') : (formData.lineItemTableHeading1 || 'Table 1') }}</strong>.
              </template>
            </p>

            <b-form-group label="Add Column" label-for="new-col-select" class="edit-cols-add-group mb-1">
              <b-input-group size="sm">
                <b-form-select id="new-col-select" v-model="newColumnToAdd" :options="availableColumnsToAdd" value-field="key" text-field="label" size="sm" class="flex-grow-1" />
                <b-input-group-append>
                  <b-button
                    variant="primary"
                    size="sm"
                    @click="addColumn"
                    :disabled="!newColumnToAdd || (newColumnToAdd === '__custom__' && !customColumnLabel.trim())"
                  >
                    <feather-icon icon="PlusIcon" size="14" class="mr-50" />Add
                  </b-button>
                </b-input-group-append>
              </b-input-group>
              <div v-if="newColumnToAdd === '__custom__'" class="d-flex align-items-center flex-wrap mt-1 custom-col-add-row">
                <b-form-input
                  v-model="customColumnLabel"
                  size="sm"
                  class="flex-grow-1 custom-col-name-input"
                  placeholder="New column name, e.g. Overtime Hours"
                  @keyup.enter.native="addColumn"
                />
                <b-form-select
                  v-model="customColumnType"
                  :options="columnTypeSelectOptions"
                  size="sm"
                  class="ml-1 custom-col-type-select"
                  aria-label="Custom column data type"
                />
              </div>
            </b-form-group>

            <details class="formula-hint-details mb-2">
              <summary class="formula-hint-summary">
                <feather-icon icon="InfoIcon" size="12" class="formula-hint-icon" />
                Formula help
              </summary>
              <div class="formula-hint-box">
                <span>A <strong>Computed</strong> column uses any editable formula referencing other columns by <code>key</code> (e.g. <code>Round(unitPrice / 26, 2)</code>, <code>round(presentDays / 26, 2)</code>, <code>quantity * unitPrice</code>). Supports Excel-style <code>Round()</code>/<code>round()</code>, <code>abs()</code>, <code>floor()</code>, <code>ceil()</code>, <code>min()</code>, <code>max()</code>, comparisons, and conditionals — <code>quantity &gt; 10 ? rate * 0.9 : rate</code> or <code>iff(quantity &gt; 10, rate * 0.9, rate)</code>. Turn <strong>Computed</strong> off to type Amount / Base Amount by hand.</span>
              </div>
            </details>

            <b-alert :show="hasColumnConfigErrors" variant="danger" class="small py-1 px-2 mb-1">
              <feather-icon icon="AlertCircleIcon" size="14" class="mr-1" />Fix the column name/formula issue(s) below before continuing.
            </b-alert>

            <div class="column-config-list manage-columns-modal">
              <div v-if="editableColumnConfig.length" class="column-config-header" aria-hidden="true">
                <span class="column-config-h-reorder" />
                <span class="column-config-h-name">Column</span>
                <span class="column-config-h-type">Data type</span>
                <div class="column-config-h-toggles">
                  <span>Visible</span>
                  <span>Required</span>
                  <span>Computed</span>
                </div>
                <span class="column-config-h-delete" />
              </div>
              <draggable
                v-if="editableColumnConfig.length"
                v-model="draggableColumnConfig"
                handle=".column-drag-handle"
                ghost-class="column-config-row-ghost"
                drag-class="column-config-row-drag"
                :animation="180"
                @end="onColumnConfigDragEnd"
              >
                <div v-for="(col, idx) in editableColumnConfig" :key="col.key" class="column-config-row">
                  <div class="column-row-main">
                    <div class="column-reorder-group">
                      <span class="column-drag-handle" aria-label="Drag to reorder">
                        <feather-icon icon="MenuIcon" size="14" />
                      </span>
                    </div>
                    <div class="column-name-group">
                      <div class="column-name-line">
                        <b-form-input
                          :value="col.label"
                          size="sm"
                          class="column-name-input"
                          :state="columnLabelIssues[idx] ? false : null"
                          placeholder="Column name"
                          @input="onColumnHeaderLabelInput(col, $event)"
                        />
                        <small class="text-muted column-key-hint"><code>{{ col.key }}</code></small>
                      </div>
                      <small v-if="columnLabelIssues[idx]" class="text-danger d-block column-error-text">{{ columnLabelIssues[idx] }}</small>
                    </div>
                    <span class="column-setting-group column-type-group">
                      <b-form-select
                        :value="col.type"
                        :options="columnTypeSelectOptions"
                        size="sm"
                        class="column-type-select"
                        aria-label="Data type"
                        @change="onColumnTypeSelect(col, $event)"
                      />
                    </span>
                    <div class="column-toggle-group">
                      <div class="column-toggle-item">
                        <b-form-checkbox
                          :checked="col.visible !== false"
                          switch
                          class="column-visible-toggle"
                          aria-label="Visible"
                          @change="setColumnVisible(col, $event)"
                        />
                        <span class="column-toggle-label">Visible</span>
                      </div>
                      <div class="column-toggle-item">
                        <b-form-checkbox
                          v-model="col.required"
                          switch
                          class="column-required-toggle"
                          aria-label="Required"
                        />
                        <span class="column-toggle-label">Required</span>
                      </div>
                      <div class="column-toggle-item">
                        <b-form-checkbox
                          :checked="isColumnComputed(col)"
                          switch
                          class="column-computed-toggle"
                          aria-label="Computed"
                          @change="setColumnComputed(col, $event)"
                        />
                        <span class="column-toggle-label">Computed</span>
                      </div>
                    </div>
                    <b-button
                      variant="outline-danger"
                      size="sm"
                      class="column-delete-btn"
                      aria-label="Delete column"
                      @click="removeColumn(idx)"
                    >
                      <feather-icon icon="Trash2Icon" size="14" />
                    </b-button>
                  </div>

                  <div v-if="isColumnComputed(col)" class="column-formula-row">
                    <b-input-group size="sm">
                      <b-input-group-prepend>
                        <span class="input-group-text small">ƒ(x) =</span>
                      </b-input-group-prepend>
                      <b-form-input
                        v-model="col.formula"
                        size="sm"
                        :state="columnFormulaIssues[idx] ? false : null"
                        placeholder="e.g. Round(unitPrice / 26, 2) or presentDays / 26"
                        @input="onColumnFormulaInput(col)"
                      />
                    </b-input-group>
                    <small v-if="columnFormulaIssues[idx]" class="text-danger d-block column-error-text">{{ columnFormulaIssues[idx] }}</small>
                    <small v-else class="text-muted d-block column-formula-hint">
                      Editable expression using column keys. Turn Computed off to enter this value manually.
                    </small>
                  </div>
                </div>
              </draggable>
              <div v-if="editableColumnConfig.length === 0" class="text-muted small text-center py-3">No columns configured yet.</div>
            </div>

            <div class="edit-cols-reset-row mt-1">
              <b-button variant="outline-secondary" size="sm" @click="resetColumnConfig">
                <feather-icon icon="RotateCcwIcon" size="14" class="mr-50" />Reset to Default
              </b-button>
            </div>
          </template>
        </line-items-config-modal>

        </b-form>
      </b-card-body>
    </b-card>
      </b-col>
    </b-row>

    <!-- Full-screen Invoice Preview Modal — secondary "zoom" affordance now
         that the compact live panel above is the default/always-visible way
         to see the preview (see the "Full-screen" button next to the wizard
         progress bar, and the persistent panel itself). Same GenericInvoicePreview,
         same live formData/lineItems-derived props, just rendered bigger. -->
    <b-modal
      id="invoice-preview-modal"
      v-model="showPreviewModal"
      title="Invoice Preview"
      size="xl"
      scrollable
      modal-class="invoice-preview-modal-shell"
      content-class="invoice-preview-modal-content"
      body-class="invoice-preview-modal-body"
      footer-class="justify-content-between"
      @hidden="showPreviewModal = false"
    >
      <div class="d-flex justify-content-end align-items-center flex-wrap mb-2 preview-toolbar-controls">
        <small class="text-muted mr-50">Style</small>
        <b-form-select
          v-model="effectivePreviewStyle"
          :options="previewStyleSelectOptions"
          size="sm"
          class="preview-style-select mr-1"
        />
        <small class="text-muted mr-50">Line items</small>
        <b-form-select
          :value="effectiveLineItemPreset"
          :options="lineItemPresetSelectOptions"
          size="sm"
          class="preview-line-items-select mr-50"
          :disabled="templateLoading"
          @change="onToolbarLineItemPresetChange"
        />
        <b-button
          variant="link"
          size="sm"
          class="text-primary p-0"
          aria-label="Edit Columns"
          @click="openLineItemsConfig('edit')"
        >
          <feather-icon icon="SettingsIcon" size="14" class="mr-25" />Columns
        </b-button>
      </div>
      <b-alert v-if="!isFormValid" show variant="warning" class="small py-2 px-3 mb-3">
        <feather-icon icon="AlertCircleIcon" size="14" class="mr-1" />
        {{ formValidationMessage }}
      </b-alert>
      <b-alert v-else-if="!hasSupportingDocuments" show variant="info" class="small py-2 px-3 mb-3">
        <feather-icon icon="InfoIcon" size="14" class="mr-1" />
        No supporting document attached — Create will still save the invoice on the current API.
      </b-alert>
      <!-- Same A4 stage + transform fit as the sticky panel — true page
           geometry, scaled to the modal body width (not a stretched box). -->
      <div class="live-preview-modal-content">
        <div ref="previewModalStage" class="a4-preview-stage a4-preview-stage--modal">
          <div class="a4-preview-fit" :style="previewModalFitStyle">
            <div class="a4-preview-scaler" :style="previewModalScalerStyle">
              <generic-invoice-preview
                v-if="showPreviewModal"
                ref="previewModalRoot"
                :template="previewTemplateForDisplay"
                :document-type="previewDocumentType"
                :bill-from="previewBillFrom"
                :bill-to="previewBillTo"
                :invoice-meta="previewInvoiceMeta"
                :status="previewInvoiceStatus"
                :description="formData.description"
                :line-items="previewLineItemsForGeneric"
                :totals="previewTotalsForGeneric"
                :bank-details="previewBankDetailsForGeneric"
                :logo-url="companyLogoPreview"
                :columns="lineItemValidationColumns"
                :columns-table2="usesSeparateTableColumns ? lineItemValidationColumnsTable2 : null"
                :show-decimals="showDecimals"
                :show-currency-symbol="showCurrencySymbol"
                :use-thousand-separators="useThousandSeparators"
                :use-second-line-items-table="formData.useSecondLineItemsTable"
                :line-item-table-heading1="formData.lineItemTableHeading1"
                :line-item-table-heading2="formData.lineItemTableHeading2"
              />
            </div>
          </div>
        </div>
      </div>
      <template #modal-footer>
        <b-button
          variant="outline-primary"
          class="mr-auto"
          :disabled="downloadingPdf"
          @click="downloadInvoicePdf('previewModalRoot')"
        >
          <b-spinner v-if="downloadingPdf" small class="mr-50" />
          <feather-icon v-else icon="DownloadIcon" size="14" class="mr-50" />
          Download Invoice
        </b-button>
        <b-button variant="outline-secondary" @click="showPreviewModal = false">Back to Edit</b-button>
        <b-button variant="primary" @click="confirmSubmitFromPreview" :disabled="loading">
          <b-spinner v-if="loading" small class="mr-1" />Submit Invoice
        </b-button>
      </template>
    </b-modal>

    <!-- Add Client Modal -->
    <add-client-modal
      v-model="showAddClientModal"
      :saving="addClientSaving"
      @submit="onCreateClient"
    />

    <!-- After Add/Tag from ATS: pick which text column receives the candidate name -->
    <b-modal
      id="candidate-mention-column-modal"
      v-model="showCandidateMentionColumnModal"
      title="Mention candidate in Line Items"
      size="sm"
      centered
      @hidden="onCandidateMentionColumnModalHidden"
    >
      <p class="small invoice-modal-helper mb-2">Which column should show the candidate name?</p>
      <b-form-group label="Line Items column" label-for="candidate-mention-column" class="mb-0">
        <b-form-select
          id="candidate-mention-column"
          v-model="pendingCandidateMentionColumnKey"
          :options="candidateNameColumnSelectOptions"
          size="sm"
        />
      </b-form-group>
      <template #modal-footer>
        <b-button variant="secondary" size="sm" @click="cancelCandidateMentionColumn">Cancel</b-button>
        <b-button
          variant="primary"
          size="sm"
          :disabled="!pendingCandidateMentionColumnKey"
          @click="confirmCandidateMentionColumn"
        >
          Confirm
        </b-button>
      </template>
    </b-modal>

    <!-- After successful submit: download PDF + upload signed copy (server-persisted) -->
    <b-modal
      id="invoice-post-submit-modal"
      v-model="showPostSubmitModal"
      title="Invoice submitted"
      size="lg"
      centered
      no-close-on-backdrop
      no-close-on-esc
      modal-class="invoice-submitted-modal"
      @hidden="onPostSubmitModalHidden"
    >
      <b-alert variant="success" show class="small py-2 px-3">
        Invoice <strong>{{ createdInvoiceLabel }}</strong> was created successfully
        <span v-if="createdInvoiceStatus"> (status: {{ createdInvoiceStatus }})</span>.
        Next: download the PDF, sign it offline, then upload the signed copy — uploads are saved on the server.
      </b-alert>

      <!-- Keep a frozen preview available so Download can regenerate the PDF
           even after the create form is cleared. -->
      <div v-if="showPostSubmitModal && postSubmitPreviewSource" class="mb-3 a4-preview-stage a4-preview-stage--modal invoice-post-submit-preview">
        <div class="a4-preview-fit" :style="previewModalFitStyle">
          <div class="a4-preview-scaler" :style="previewModalScalerStyle">
            <generic-invoice-preview
              ref="postSubmitPreviewRoot"
              :template="postSubmitPreviewSource.template"
              :document-type="postSubmitPreviewSource.documentType"
              :bill-from="postSubmitPreviewSource.billFrom"
              :bill-to="postSubmitPreviewSource.billTo"
              :invoice-meta="postSubmitPreviewSource.invoiceMeta"
              :status="postSubmitPreviewSource.status"
              :description="postSubmitPreviewSource.description"
              :line-items="postSubmitPreviewSource.lineItems"
              :totals="postSubmitPreviewSource.totals"
              :bank-details="postSubmitPreviewSource.bankDetails"
              :logo-url="postSubmitPreviewSource.logoUrl"
              :columns="postSubmitPreviewSource.columns"
              :columns-table2="postSubmitPreviewSource.columnsTable2"
              :show-decimals="postSubmitPreviewSource.showDecimals"
              :show-currency-symbol="postSubmitPreviewSource.showCurrencySymbol"
              :use-thousand-separators="postSubmitPreviewSource.useThousandSeparators"
              :use-second-line-items-table="postSubmitPreviewSource.useSecondLineItemsTable"
              :line-item-table-heading1="postSubmitPreviewSource.lineItemTableHeading1"
              :line-item-table-heading2="postSubmitPreviewSource.lineItemTableHeading2"
            />
          </div>
        </div>
      </div>

      <signed-invoice-workflow
        v-if="createdInvoiceKey"
        :invoice-id="createdInvoiceKey"
        :invoice="createdInvoiceRecord"
        :client-pdf-provider="providePostSubmitPdf"
        :show-tooltips="false"
        @signed-uploaded="onPostSubmitSignedUploaded"
      />

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="goToDashboardAfterSubmit">
          Go to Invoice Tracker
        </b-button>
        <b-button variant="primary" @click="goToDashboardAfterSubmit">
          Done
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormSelect,
  BFormCheckbox,
  BFormFile,
  BTable,
  BRow,
  BCol,
  BSpinner,
  BAlert,
  BModal,
  VBModal,
  BFormRadioGroup,
  BInputGroup,
  BInputGroupAppend,
  BInputGroupPrepend,
  BListGroup,
  BListGroupItem,
  BDropdown,
  BDropdownItem,
  BButtonGroup,
  BBadge,
  BProgress,
  BCollapse,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import moment from 'moment'
import clientService from '@/services/client-service'
import locationService from '@/services/location-service'
import invoiceConfigService, {
  isClientMasterDataIncomplete,
  normalizeClientMasterDataList,
  extractClientStates,
  findClientEntityByState,
  extractClientLegalNames,
  findClientEntityByLegalName,
  filterClientEntitiesByLegalName,
  findClientEntityByLegalNameAndState,
  emptyInvoiceClientInfo,
  getInvoicePrefixForVertical,
  getNextInvoiceNumber,
} from '@/services/invoice-config-service'
import invoiceService, { formatInvoiceApiError } from '@/services/invoice-service'
import enhancedInvoiceService from '@/services/enhanced-invoice-service'
import invoiceDraftService from '@/services/invoice-draft-service'
import candidateService from '@/services/candidate-service'
// Fixed default Sattva logo — the same branding asset used app-wide (see
// themeConfig.js's appLogoImage) — attached automatically to invoices
// instead of a per-invoice user upload (removed per product direction).
import sattvaDefaultLogo from '@/assets/images/logo/logo.svg'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  DEFAULT_TEMPLATE,
  RPO_TEMPLATE,
  HEADCOUNT_TEMPLATE,
  CONTRACT_STAFFING_TEMPLATE,
  BAJAJ_TEMPLATE,
  RELIANCE_TEMPLATE,
  LINE_ITEM_PRESETS,
  buildCalcContext,
  evaluateCalcRules,
  recomputeRow,
  getVisibleCalcRows,
  normalizeLineItemForApi,
  applyTemplateDefaults,
  findHeadcountCalcColumns,
  findUnknownFormulaReferences,
  formulaReferencesKey,
  validateFormulaSyntax,
  resolveSacHsnCode,
  resolveLutNo,
  migrateLineItemSacHsn,
  isComputedColumn,
  isTitleSelectColumn,
  isOneLineItemsStructure,
  SAC_HSN_API_FIELD,
  LUT_API_FIELD,
} from '@/utils/invoice-calculation-engine'
import {
  getLineItemColumnTypeOptions,
  resolveColumnType,
  getDefaultColumnType,
  isSystemColumnType,
  observeLineItemColumnTypes,
} from '@/utils/invoice-column-types'
import {
  persistableColumn,
  persistableColumnConfig,
  persistablePresetColumnStore,
  applyColumnTypeChange,
  toggleColumnComputed,
  usesSacSelectWidget,
  displayFormula,
  stashAndTakePresetColumns,
  resolveCreateValidationColumns,
  buildCustomColumn,
  validateInvoiceLineItemCell,
} from '@/utils/invoice-create-columns'
import draggable from 'vuedraggable'
import LineItemsConfigModal from './components/modals/LineItemsConfigModal.vue'
import WizardDocTypeStep from './components/wizard/WizardDocTypeStep.vue'
import WizardClientStep from './components/wizard/WizardClientStep.vue'
import WizardSattvaLocationStep from './components/wizard/WizardSattvaLocationStep.vue'
import WizardLineItemsStep from './components/wizard/WizardLineItemsStep.vue'
import WizardMoreDetailsStep from './components/wizard/WizardMoreDetailsStep.vue'
import WizardPaymentStep from './components/wizard/WizardPaymentStep.vue'
import AddClientModal from './components/modals/AddClientModal.vue'
import GenericInvoicePreview from './components/GenericInvoicePreview.vue'
import SignedInvoiceWorkflow from './components/SignedInvoiceWorkflow.vue'
import { exportElementToPdf } from '@/utils/pdf-export'
import {
  buildAutoInvoiceName as buildAutoInvoiceNameUtil,
  buildInvoiceDownloadFilename,
  getInvoiceFileSizeError,
  getInvoiceFileTypeError,
  INVOICE_FILE_TYPE_NOT_ALLOWED_MESSAGE,
  isInvoiceProdMulterAllowed,
} from '@/utils/invoice-filename'
import { formatCurrency as formatCurrencyUtil } from '@/utils/currency-format'
import { purgeLegacyTemplatePreferenceKeys } from '@/utils/invoice-last-template-preference'
import { extractSupportingDocuments, loadSupportingDocAsFile } from '@/utils/invoice-file-loader'
import {
  buildBillingMonthOptions,
  currentBillingMonthValue,
  clampBillingMonthToCurrentOrPast,
} from '@/utils/invoice-date-calculator'

/** Opt-in: multipart `companyLogo` on create (backend may 500 on logo pipeline). */
const INVOICE_CREATE_INCLUDE_LOGO = process.env.VUE_APP_INVOICE_CREATE_INCLUDE_LOGO === 'true'

/** Default Notes text when formData.includeMsmeTerms is enabled (MSME note). */
const MSME_INVOICE_TERMS = [
  '1) We are MSME Registered Entity vide No. UDYAM-MH-26-0202228',
  '2) Credit period for the invoice clearance is maximum 45 days, after which 18% p.a. interest will be levied as per MSME Act.',
].join('\n')

export default {
  name: 'InvoiceCreateManual',
  components: {
    BCard, BCardBody, BButton, BForm, BFormGroup, BFormInput, BFormTextarea,
    BFormSelect, BFormCheckbox, BFormFile, BTable, BRow, BCol, BSpinner,
    BAlert, BModal, VBModal, BFormRadioGroup, BInputGroup, BInputGroupAppend, BInputGroupPrepend,
    BListGroup, BListGroupItem, BDropdown, BDropdownItem, BButtonGroup, BBadge, BProgress, BCollapse,
    vSelect, flatPickr, ToastificationContent, LineItemsConfigModal, draggable,
    WizardDocTypeStep, WizardClientStep, WizardSattvaLocationStep,
    WizardLineItemsStep, WizardMoreDetailsStep, WizardPaymentStep, AddClientModal, GenericInvoicePreview,
    SignedInvoiceWorkflow,
  },
  directives: {
    'b-modal': VBModal,
  },
  data() {
    return {
      currentStep: 0,
      loading: false,
      duplicateLoading: false,
      duplicateSourceId: '',
      duplicateSupportCopied: false,
      activeTemplate: { ...DEFAULT_TEMPLATE },
      templateLoading: false,
      lastSavedAt: null,
      draftPersistSource: null, // 'api' | 'session' — null until first save/load
      showDraftBanner: false,
      _pendingDraft: null,
      _draftSaveInFlight: false,
      _draftSessionWarned: false,
      loadedTemplateRecord: null,
      wizardStepLabels: ['Doc Type', 'Client', 'Sattva GST / Location', 'Line Items', 'More Details', 'Payment'],
      formData: {
        client: null,
        template: null,
        documentType: 'Tax Invoice',
        invoiceName: '',
        location: null,
        category: null,
        vertical: null,
        billingMonth: null,
        legalEntityId: null,
        clientState: null,
        invoiceClientInfo: {
          clientName: '',
          legalName: '',
          clientGST: '',
          clientAddress: '',
          clientState: '',
          placeOfSupply: '',
          clientPhone: ''
        },
        invoiceSattvaInfo: {
          companyName: '',
          gst: '',
          pan: '',
          address: '',
          bankName: '',
          accountName: '',
          // BUG FIX (2026-07-22): previewBankDetailsForGeneric (below) has
          // always read `invoiceSattvaInfo.accountNumber`/`.accountType` —
          // both a real part of getSattvaCompanyDetails()'s normalized
          // response (see normalizeSattvaCompanyDetails() in
          // invoice-config-service.js) — but neither field existed anywhere
          // on this object, so the live preview/PDF's "Details for Payment"
          // silently always showed the hardcoded default account number/
          // type, never the actual selected location's, even once that
          // lookup succeeded. Declared here (and populated in
          // onLocationSelected/WizardSattvaLocationStep.vue's manual
          // override) so real data actually has somewhere to go.
          accountNumber: '',
          accountType: '',
          ifscCode: '',
          logoUrl: '',
          lutNo: '',
          sacCode: null,
        },
        invoiceId: '',
        invoiceDate: moment().format('YYYY-MM-DD'),
        paymentTerms: null,
        dueDate: null,
        poNumber: '',
        gstType: '',
        description: '',
        isCollectNPay: false,
        payDate: null,
        creditPeriod: null,
        tdsApplicable: false,
        tdsPercentage: null,
        hasPackageCode: false,
        packageCode: '',
        useSecondLineItemsTable: false,
        lineItemTableHeading1: '',
        lineItemTableHeading2: '',
        // When two tables are on: true = shared columns (default / back-compat).
        secondTableColumnsSame: true,
        // Opt-in: when true, preview/PDF inject MSME_INVOICE_TERMS as Notes;
        // when false, Notes stay hidden (no leftover template termsText).
        includeMsmeTerms: false,
      },
      lineItems: [],
      invoiceFile: null, // primary supporting document (back-compat)
      invoiceFiles: [], // multiple supporting documents
      // Candidate selection
      isForCandidates: null, // null = not answered, true = yes, false = no
      selectedCandidates: [],
      tagCandidateLineIndex: null,
      // Mention-in-Line-Items column picker (after Add/Tag from ATS)
      showCandidateMentionColumnModal: false,
      pendingCandidateMentionColumnKey: null,
      _candidateMentionColumnResolve: null,
      // True while Add Selected is closing ATS and running the mention prompts
      _atsApplyingSelection: false,
      // Candidate modal (Add Candidates from ATS — also used by Tag)
      showCandidateModal: false,
      showPreviewModal: false,
      // Collapsible live-preview toggle for <lg viewports — see the split-view
      // layout at the top of the template (persistent sticky panel on wide
      // screens, this collapsible instead of a modal on narrow ones).
      showMobilePreview: false,
      // "Download Invoice" (html2canvas -> jsPDF) in-flight flag — shared by
      // both download buttons (sticky panel + full-screen modal) since only
      // one can realistically be clicked at a time.
      downloadingPdf: false,
      // Post-submit: keep user on page for Download + Upload signed invoice
      showPostSubmitModal: false,
      createdInvoiceKey: null,
      createdInvoiceRecord: null,
      createdInvoiceStatus: null,
      _postSubmitNavigating: false,
      // Frozen preview props so the post-submit modal/PDF stay correct after
      // we clear the create wizard form (req: clear after successful create).
      postSubmitPreviewFreeze: null,
      _suppressDraftSave: false,
      // A4 fit-scale state for live preview stages (panel / mobile / modal).
      // Each holds { scale, widthPx, heightPx } measured from the true
      // 210mm sheet; CSS transform scales the sheet to the stage width
      // without reflowing typography (see syncA4PreviewFit).
      previewPanelFit: { scale: 1, widthPx: 794, heightPx: 1123 },
      previewMobileFit: { scale: 1, widthPx: 794, heightPx: 1123 },
      previewModalFit: { scale: 1, widthPx: 794, heightPx: 1123 },
      showColumnConfigModal: false,
      // 0 = Preview tab, 1 = Edit Columns (LineItemsConfigModal)
      columnConfigModalTab: 0,
      dynamicColumnConfig: null,
      /** Table 2 column config when secondTableColumnsSame === false. */
      dynamicColumnConfigTable2: null,
      /** Which table’s columns the Edit Columns modal is editing (1|2). */
      columnConfigEditTable: 1,
      // Bumped on any deep Edit Columns change so previewTemplateForDisplay /
      // lineItemValidationColumns always re-emit (Vue 2 can miss nested
      // label edits for consumers that only tracked the array reference).
      columnConfigEpoch: 0,
      _preserveTable2OnPreset: false,
      // Snapshot of columnConfigEpoch when the Line Items Config modal opened
      // — used so Done only toasts when something actually changed.
      columnEpochAtModalOpen: 0,
      newColumnToAdd: null,
      customColumnLabel: '',
      customColumnType: 'text',
      // Per-preset Manage Columns snapshots so Standard custom types survive
      // a detour through Reliance and back (same session + draft + next visit).
      columnConfigByPreset: {},
      addableColumnPresets: [
        { key: 'costCenter', label: 'Cost Center', type: 'text' },
        { key: 'projectCode', label: 'Project Code', type: 'text' },
        { key: 'remarks', label: 'Remarks', type: 'text' },
        { key: 'discount', label: 'Discount', type: 'number' }
      ],
      candidateSearchName: '',
      candidateList: [],
      candidateModalFields: [
        { key: 'selected', label: 'Select' },
        { key: 'candidate_name', label: 'Candidate Name', sortable: true },
        { key: 'position_name', label: 'Position', sortable: true },
        { key: 'client', label: 'Client', sortable: true },
        { key: 'revenue', label: 'Amount', sortable: true }
      ],
      selectedCandidateIds: [],
      clients: [],
      locations: [],
      categories: [
        { title: 'Recruitment' },
        { title: 'Training' },
        { title: 'Anti Fraud' },
        { title: 'Contractual' },
        { title: 'Payroll' },
        { title: 'BGV' },
        { title: 'Miscellanous' }
      ],
      verticalOptions: [
        { title: 'Service Industry' },
        { title: 'Infra Industry' }
      ],
      gstTypeOptions: [
        { title: 'SEZ', label: 'SEZ' },
        { title: 'IGST', label: 'IGST' },
        { title: 'SGST-CGST', label: 'SGST-CGST' }
      ],
      creditPeriodOptions: [],
      paymentTermsOptions: [],
      tdsPercentageOptions: [],
      // Line-item SAC/HSN creatable-select options ({text,value} for vue-select).
      sacCodeOptions: [],
      // Header-level SAC/HSN creatable-select options ({title,value} shape).
      sacCodeSelectOptions: [],
      // Creatable-select options for the "LUT No." field — there's no backend
      // "list of LUT numbers" endpoint; this just gets seeded with whatever
      // lutNo the Sattva company-details lookup returns for the selected
      // location (if any), plus anything the user types in this session.
      lutNoOptions: [],
      billingMonthOptions: [],
      clientMasterData: null,
      clientEntities: [],
      clientStateOptions: [],
      clientLegalNameOptions: [],
      // True when master data had no legal_name values and we fell back to
      // using the client name as the sole Legal Name option.
      legalNameIsFallback: false,
      clientMasterLoading: false,
      showAddClientModal: false,
      addClientSaving: false,
      invoiceNumberLoading: false,
      _invoiceNumberVerticalAtGen: null,
      // 'backend-counter' | 'frontend-computed-fallback' | 'frontend-computed-fallback-default'
      invoiceNumberSource: null,
      _invoiceNumberFallbackNotified: false,
      // True once the user has typed a non-empty Invoice # themselves —
      // refreshInvoiceNumber() then backs off auto-filling on Vertical
      // change until this is cleared (by blanking the field, or by an
      // explicit Regenerate click, which always wins).
      invoiceIdManuallyEdited: false,
      _restoringDraft: false,
      templateOptions: [],
      manualTemplateId: null,
      // "Show decimals" toggle (see WizardLineItemsStep.vue's Amount Summary
      // switch) — defaults to true (2 decimal places / paise shown) so this
      // is a pure opt-in addition that doesn't change the pre-existing
      // always-2-decimal display unless the user explicitly turns it off.
      showDecimals: true,
      showCurrencySymbol: true,
      useThousandSeparators: true,
      // Live-preview "Style" override (2026-07-22) — lets a user pick a
      // different layout.style (see GenericInvoicePreview.vue's styleName())
      // for THIS invoice's preview/PDF without touching activeTemplate.layout
      // itself. null = "no override yet, just show whatever the active
      // template's own layout.style already says" (see
      // effectivePreviewStyle/previewTemplateForDisplay below). Deliberately
      // never reset on template switch (unlike dynamicColumnConfig) — style
      // is a pure cosmetic skin, not tied to a template's column/field
      // structure, so a user's chosen look is treated as a standing
      // preference for the rest of this invoice-creation session rather
      // than something that needs re-picking every time they change
      // template/columns.
      previewStyleOverride: null,
      // Same values/labels as TemplateEditor.vue's layoutStyleOptions —
      // kept in sync by hand (there are only a handful and they rarely
      // change) so this inline picker never drifts from what the full
      // editor offers. 'contract-staffing' / 'bajaj' / 'reliance' added
      // 2026-07-23 — see GenericInvoicePreview.vue's matching
      // `.invoice-style-*` design systems.
      previewStyleSelectOptions: [
        { value: 'modern', text: 'Modern' },
        { value: 'corporate', text: 'Corporate' },
        { value: 'compact', text: 'Compact' },
        { value: 'contract-staffing', text: 'Contract Staffing' },
        { value: 'bajaj', text: 'Old Template' },
        { value: 'reliance', text: 'Reliance' },
      ],
      sattvaMasterData: null,
      sattvaDetailsLoading: false,
      sattvaDetailsUnavailable: false,
      /** Numeric /locations id for the selected Sattva office (for master upsert). */
      resolvedSattvaLocationId: null,
      sattvaSaveLoading: false,
      /** Last sattva save fingerprint — avoids duplicate PUTs for the same payload. */
      _sattvaSaveLastPayloadKey: null,
      /** Canonical /locations master (numeric ids) — kept even when dropdown uses sattva list. */
      masterLocations: [],
      gstRates: {
        sgst: 9,
        cgst: 9,
        igst: 18
      },
      error: {
        client: '',
        legalName: '',
        clientState: '',
        location: '',
        category: '',
        vertical: '',
        invoiceId: '',
        invoiceDate: '',
        gstType: ''
      }
    }
  },
  computed: {
    createdInvoiceLabel() {
      const rec = this.createdInvoiceRecord || {}
      return rec.invoice_id || rec.invoice_number || this.formData.invoiceId || this.createdInvoiceKey || '—'
    },
    lineItemTemplateType() {
      return invoiceConfigService.getClientInvoiceLineItemTemplate(
        this.formData.client,
        this.formData.category
      )
    },
    // BUG FIX (2026-07-21): both of these used to read
    // `this.activeTemplate.lineItemColumns` directly with no fallback — any
    // template record missing that array entirely (e.g. a custom template
    // saved via TemplateManager.vue without a line-item column config, or a
    // backend response that only partially matched the expected shape) threw
    // a TypeError the instant it became the active template. That crashed
    // these computeds and, with them, the whole Line Items step AND the live
    // preview (both depend on isRpoTemplate/effectiveLineItemFields), which
    // is exactly the "some fields if selected then it is not visible
    // invoice" symptom — matches the `|| []` guard already used by the
    // sibling lineItemValidationColumns computed below.
    isRpoTemplate() {
      return (this.activeTemplate.lineItemColumns || []).some((c) => c.key === 'employeeId')
    },
    effectiveLineItemFields() {
      const templateCols = (this.activeTemplate.lineItemColumns || []).filter(
        (col) => col.visible !== false,
      )

      if (this.dynamicColumnConfig && this.dynamicColumnConfig.length > 0) {
        const moveCol = templateCols.find((c) => c.type === 'move')
        const actionsCol = templateCols.find((c) => c.type === 'actions')
        const dataCols = this.dynamicColumnConfig
          .filter((c) => c && c.visible !== false)
          .map((c) => ({
            key: c.key,
            label: (c.label || c.key).toUpperCase(),
            thClass: 'small text-uppercase text-muted' + (c.type === 'number' || c.type === 'currency' ? ' text-right' : ''),
            tdClass: (c.type === 'number' || c.type === 'currency' ? 'text-right ' : '') + 'align-middle',
            sortable: false,
          }))
        const result = []
        if (moveCol) result.push(this._colSpecToField(moveCol))
        result.push(...dataCols)
        if (actionsCol) result.push(this._colSpecToField(actionsCol))
        return result
      }

      return templateCols.map((col) => this._colSpecToField(col))
    },
    /**
     * Manage Columns type dropdown — catalog + any type already stored on
     * a column/template. Unknown stored types stay selectable (never dropped
     * just because they are not in a JS enum).
     */
    columnTypeSelectOptions() {
      const extras = []
      const pushTypes = (list) => {
        (list || []).forEach((c) => {
          if (c && c.type != null && c.type !== '') extras.push(c.type)
        })
      }
      pushTypes(this.getActiveColumnConfigArray())
      pushTypes(this.dynamicColumnConfig)
      pushTypes(this.dynamicColumnConfigTable2)
      pushTypes(this.activeTemplate && this.activeTemplate.lineItemColumns)
      return getLineItemColumnTypeOptions(extras)
    },
    editableColumnConfig() {
      return this.getActiveColumnConfigArray() || []
    },
    /** Writable proxy for Edit Columns drag-reorder (table 1 or 2). */
    draggableColumnConfig: {
      get() {
        return this.getActiveColumnConfigArray() || []
      },
      set(val) {
        this.setActiveColumnConfigArray(val)
      },
    },
    usesSeparateTableColumns() {
      return !!(this.formData.useSecondLineItemsTable && this.formData.secondTableColumnsSame === false)
    },
    columnConfigEditTableOptions() {
      const h1 = String(this.formData.lineItemTableHeading1 || '').trim() || 'Table 1'
      const h2 = String(this.formData.lineItemTableHeading2 || '').trim() || 'Table 2'
      return [
        { text: h1, value: 1 },
        { text: h2, value: 2 },
      ]
    },
    availableColumnsToAdd() {
      const existing = new Set((this.getActiveColumnConfigArray() || []).map(c => c.key))
      const opts = this.addableColumnPresets.filter(p => !existing.has(p.key))
      // Re-offer removed template columns (e.g. Amount after delete) so they
      // can be added back without going through Custom Field.
      const SYSTEM_TYPES = new Set(['move', 'actions'])
      const seen = new Set(opts.map((p) => p.key))
      const fromTemplate = (this.activeTemplate.lineItemColumns || [])
        .filter((c) => c && c.key && !existing.has(c.key) && !SYSTEM_TYPES.has(c.type) && !seen.has(c.key))
        .map((c) => {
          seen.add(c.key)
          return {
            key: c.key,
            label: c.label || c.key,
            type: resolveColumnType(c.type),
            required: !!c.required,
            formula: c.formula || '',
          }
        })
      // "+ Custom Field…" (2026-07-22) — the fixed presets above cover
      // common needs but can't cover a client-specific field a user needs
      // for exactly this invoice; picking this reveals a plain text input
      // (see the modal template's customColumnLabel field) instead of
      // forcing them out to the full Template Editor just to add one field.
      return [{ key: null, label: '-- Select to add --' }, ...opts, ...fromTemplate, { key: '__custom__', label: '+ Custom Field…' }]
    },
    // Column names must be non-empty and unique across the whole config —
    // parallel array to editableColumnConfig, one message (or falsy) per row.
    columnLabelIssues() {
      const cols = this.editableColumnConfig
      return cols.map((col, idx) => {
        const label = String(col.label || '').trim()
        if (!label) return 'Column name is required'
        const dupIdx = cols.findIndex(
          (c2, i2) => i2 !== idx && String(c2.label || '').trim().toLowerCase() === label.toLowerCase(),
        )
        return dupIdx !== -1 ? 'Duplicate column name' : ''
      })
    },
    // Same "parallel array, one message per row" shape as columnLabelIssues
    // above, for the inline formula field a column shows once marked
    // "Computed" (see setColumnComputed) — non-empty, doesn't reference
    // itself (a formula referencing its own column would only ever see its
    // OWN previous value, never converge, and just look "stuck" — worth
    // catching explicitly rather than leaving a user to puzzle over it),
    // and only references other columns' real keys (or a math helper),
    // reusing the exact same findUnknownFormulaReferences()/
    // formulaReferencesKey() tokenizer the calculation engine itself is
    // built around, not a separate check invented here. Messages spell out
    // exactly what to do next (not just "invalid formula") since this is
    // most people's first exposure to formula authoring in this app.
    columnFormulaIssues() {
      const cols = this.editableColumnConfig
      const validKeys = cols.map((c) => c.key)
      return cols.map((col) => {
        if (!this.isColumnComputed(col)) return ''
        const formula = String(col.formula || '').trim()
        // Headcount engine can still derive Base Amount without a formula string.
        if (!formula && this.isHeadcountEngineColumn(col)) return ''
        if (!formula) return 'Enter a formula, or turn "Computed" back off for this column.'
        if (formulaReferencesKey(formula, col.key)) {
          return `A formula can't reference its own column ("${col.key}") — it would never update correctly.`
        }
        const unknown = findUnknownFormulaReferences(formula, validKeys)
        if (unknown.length > 0) {
          const word = unknown.length > 1 ? 'keys' : 'key'
          return `Unknown column ${word}: ${unknown.join(', ')} — check the "Key" shown under each column name above for typos.`
        }
        return validateFormulaSyntax(formula)
      })
    },
    hasColumnConfigErrors() {
      return this.columnLabelIssues.some(Boolean) || this.columnFormulaIssues.some(Boolean)
    },
    // Keys the headcount engine still treats as Auto — only while Computed
    // is on. A user type change / Computed OFF must not keep an Auto badge.
    forcedComputedColumnKeys() {
      const cols = this.lineItemConfigModalColumns || []
      const hc = findHeadcountCalcColumns(cols)
      if (!hc) return []
      const keys = []
      const amountCol = cols.find((c) => c && c.key === hc.amountKey)
      if (hc.amountKey && isComputedColumn(amountCol)) keys.push(hc.amountKey)
      const mtdCol = cols.find((c) => c && c.key === hc.monthToDayKey)
      if (hc.monthToDayKey && hc.rateCardKey && isComputedColumn(mtdCol)) keys.push(hc.monthToDayKey)
      return keys
    },
    // Prefer live invoice rows that have meaningful data; else demo samples.
    lineItemConfigUsingLiveData() {
      const rows = this.lineItemConfigPreviewSourceRows
      return rows.some((row) => this.lineItemRowHasMeaningfulData(row))
    },
    /** Rows for the columns modal sample — scoped to the table being edited when separate. */
    lineItemConfigPreviewSourceRows() {
      const items = this.lineItems || []
      if (!this.usesSeparateTableColumns) return items
      const group = Number(this.columnConfigEditTable) === 2 ? 2 : 1
      return items.filter((row) => (Number(row.tableGroup) === 2 ? 2 : 1) === group)
    },
    lineItemConfigSampleRows() {
      const cols = this.lineItemConfigModalColumns
      if (this.lineItemConfigUsingLiveData) {
        return this.lineItemConfigPreviewSourceRows
          .filter((row) => this.lineItemRowHasMeaningfulData(row))
          .slice(0, 3)
          .map((row) => recomputeRow({ ...row }, cols))
      }
      return this.buildLineItemSampleRows(cols)
    },
    /** Columns shown in Line Items Config modal (follows Table 1/2 toggle). */
    lineItemConfigModalColumns() {
      return this.getValidationColumnsForTable(this.columnConfigEditTable)
    },
    // The actual set of data columns currently rendered in the line items
    // table — prefers dynamicColumnConfig (Manage Columns) when the user has
    // customized it, otherwise the active template's own columns. Dynamic
    // columns (built by getBaseColumnConfig()/addColumn()) don't always carry
    // `type`/`required`, so those are backfilled from the underlying template
    // column of the same key when available. This is the single source of
    // truth for both rendering (generic cell fallback in
    // WizardLineItemsStep.vue) and validation below, so what the user sees
    // editable is exactly what gets validated.
    lineItemValidationColumns() {
      // Depend on epoch so Rename/Reorder while Edit Columns is open always
      // invalidates this computed (and thus the invoice preview columns prop).
      void this.columnConfigEpoch
      return this.resolveValidationColumnsFromSource(this.dynamicColumnConfig)
    },
    lineItemValidationColumnsTable2() {
      void this.columnConfigEpoch
      if (!this.usesSeparateTableColumns) return this.lineItemValidationColumns
      // Separate columns: Table 2 uses only its own config (never Table 1).
      return this.resolveValidationColumnsFromSource(this.dynamicColumnConfigTable2)
    },
    // Per-row map of { [columnKey]: errorMessage } — empty object means the
    // row is fully valid. SR No-type columns are excluded since they're
    // auto-numbered from row position (see syncSrNoColumns()) and are always
    // valid by construction.
    lineItemRowErrors() {
      return this.lineItems.map((item) => {
        const cols = this.getValidationColumnsForRow(item).filter(
          (c) => c.visible !== false && !this.isSrNoColumnKey(c.key, c.label),
        )
        const rowErrors = {}
        cols.forEach((col) => {
          const value = usesSacSelectWidget(col)
            ? resolveSacHsnCode(item)
            : (item ? (item[col.key] != null && String(item[col.key]).trim() !== '' ? item[col.key] : (col.key === 'itemName' ? item.title : item[col.key])) : undefined)
          // Title is optional on the Standard / 1 Line Items structure.
          const colForValidation = isTitleSelectColumn(col, cols) ? { ...col, required: false } : col
          const err = this.validateLineItemCell(colForValidation, value)
          if (err) rowErrors[col.key] = err
        })
        return rowErrors
      })
    },
    hasLineItemErrors() {
      return this.lineItemRowErrors.some((r) => Object.keys(r).length > 0)
    },
    // Flat, human-readable list for the "fix these before continuing" banner.
    lineItemErrorSummary() {
      const out = []
      this.lineItemRowErrors.forEach((rowErrors, idx) => {
        const item = this.lineItems[idx]
        const cols = this.getValidationColumnsForRow(item)
        Object.entries(rowErrors).forEach(([key, msg]) => {
          const col = cols.find((c) => c.key === key)
          out.push(`Row ${idx + 1}: ${(col && col.label) || key} — ${msg}`)
        })
      })
      return out
    },
    // The Sattva logo is a fixed default asset now — never user-selected — so
    // this just resolves whichever the backend gave us for this location, or
    // the app's own bundled branding logo (same one used in the nav bar).
    companyLogoPreview() {
      return this.formData.invoiceSattvaInfo.logoUrl || sattvaDefaultLogo
    },
    // Prefer frozen post-submit snapshot; fall back to live preview props.
    postSubmitPreviewSource() {
      return this.postSubmitPreviewFreeze || null
    },
    // Header-level default SAC / HSN Code (see WizardMoreDetailsStep).
    defaultSacCode() {
      const sac = resolveSacHsnCode(this.formData.invoiceSattvaInfo)
      if (sac) return sac
      return (this.sacCodeOptions && this.sacCodeOptions[0]) ? this.sacCodeOptions[0].value : null
    },
    // Getter/setter pair so the inline Style <b-form-select> in the live-
    // preview toolbar always shows the ACTUAL currently-rendered style —
    // the override once set, or the active template's own default before
    // the user has touched it. Setting a style that shares an id with a
    // built-in line-item preset (reliance / bajaj / contract-staffing)
    // also applies that column layout — see syncLineItemsFromStyle().
    effectivePreviewStyle: {
      get() {
        const s = this.previewStyleOverride || (this.activeTemplate.layout && this.activeTemplate.layout.style) || 'bajaj'
        // Minimal was removed from the style picker — treat legacy value as default.
        return s === 'minimal' ? 'bajaj' : s
      },
      set(val) {
        this.previewStyleOverride = val === 'minimal' ? 'bajaj' : val
        // Style pick updates preference when Line items are already pinned
        // (or when Style maps to a matching Line-items preset — see sync).
        this.syncLineItemsFromStyle(this.previewStyleOverride)
      },
    },
    // Line-items column preset in the Live Preview toolbar (moved out of
    // the wizard step 2026-07-23). '' in the select = Auto (client/category
    // match); stored as manualTemplateId === null.
    // Read-only computed — toolbar selects use :value + @change so BV's
    // localValue→input re-emit cannot re-enter onTemplateSelected.
    effectiveLineItemPreset() {
      return this.manualTemplateId == null ? '' : this.manualTemplateId
    },
    // Same shape as previewStyleSelectOptions for the toolbar <b-form-select>.
    lineItemPresetSelectOptions() {
      const builtIns = LINE_ITEM_PRESETS.map((p) => ({ value: p.id, text: p.name }))
      const saved = (this.templateOptions || []).map((t) => ({
        value: t.id,
        text: t.name || `Saved ${t.id}`,
      }))
      return [
        { value: '', text: 'Auto (recommended)' },
        ...builtIns,
        ...saved,
      ]
    },
    // Per-invoice Style override, layered onto a COPY of activeTemplate
    // rather than mutating activeTemplate.layout.style directly (2026-07-22)
    // — same "override, don't rewrite the template" convention
    // lineItemValidationColumns/dynamicColumnConfig already established for
    // Manage Columns. Mutating in place would be actively dangerous here,
    // not just an inconsistency: DEFAULT_TEMPLATE/RPO_TEMPLATE in
    // invoice-calculation-engine.js are frozen, but `layout` itself is a
    // shared, unfrozen NESTED object — RPO_TEMPLATE.layout is literally the
    // same object reference as DEFAULT_TEMPLATE.layout, and activeTemplate
    // starts as a shallow `{ ...DEFAULT_TEMPLATE }` copy that still points
    // at that same shared layout object. Writing activeTemplate.layout.style
    // directly would silently corrupt that shared, module-level singleton
    // for every future invoice/template in this session, not just the
    // current one. Passed to every <generic-invoice-preview> instance below
    // (sticky panel / mobile collapsible / full-screen modal) in place of
    // activeTemplate, so all three stay in sync with each other and with
    // whichever Style select the user actually touched.
    //
    // BUG FIX (2026-07-23): also merge the LIVE Edit Columns list
    // (lineItemValidationColumns / dynamicColumnConfig) into
    // lineItemColumns. Previously this returned activeTemplate's static
    // preset columns only — so when GenericInvoicePreview fell back to
    // template.lineItemColumns (or b-table reused headers by key), Rename /
    // Reorder / Add / Remove in Edit Columns never showed on the invoice
    // preview even though the wizard table updated. Preset switches still
    // worked because they replace activeTemplate entirely.
    previewTemplateForDisplay() {
      void this.columnConfigEpoch
      const tmpl = this.activeTemplate || {}
      const baseLayout = tmpl.layout || {}
      const styleOverride = this.previewStyleOverride && this.previewStyleOverride !== 'minimal'
        ? this.previewStyleOverride
        : null
      const includeMsme = !!this.formData.includeMsmeTerms
      // Always clone layout so MSME on/off never mutates the shared template
      // singleton, and so toggle-off can clear leftover termsText.
      let layout = { ...baseLayout }
      if (styleOverride) layout.style = styleOverride
      else if (baseLayout.style === 'minimal') layout.style = 'bajaj'
      if (includeMsme) {
        layout.termsText = MSME_INVOICE_TERMS
        layout.showTerms = true
      } else {
        layout.termsText = ''
        layout.showTerms = false
      }
      return {
        ...tmpl,
        layout,
        lineItemColumns: this.buildPreviewLineItemColumns(),
      }
    },
    // ── "Preview Invoice" modal mapping ──────────────────────────────────
    // These feed <generic-invoice-preview> (the same component TemplateManager.vue
    // uses for its own template preview) with the LIVE in-progress wizard state.
    // Each one falls back to the same sample/dummy values GenericInvoicePreview
    // itself defaults to when a prop is omitted, so a half-filled invoice still
    // previews as a complete, realistic-looking document instead of a blank/
    // dashed-out one — the user explicitly asked for "how it will look", not
    // "what's missing".
    previewDocumentType() {
      return this.formData.documentType || 'Tax Invoice'
    },
    previewBillFrom() {
      const s = this.formData.invoiceSattvaInfo || {}
      return {
        companyName: s.companyName || 'Sattva Human Private Limited',
        address: s.address || 'Office, 1st Floor, Ruia Chambers, Hadapsar, Pune 411028',
        gstin: s.gst || '27ABDCS0586N1ZT',
        pan: s.pan || 'ABDCS0586N',
        sacCode: this.defaultSacCode || '',
        lutNo: resolveLutNo(s) || '',
      }
    },
    previewBillTo() {
      const c = this.formData.invoiceClientInfo || {}
      return {
        name: c.legalName || c.clientName || this.formData.client || 'Sample Client Pvt Ltd',
        address: c.clientAddress || 'Client address',
        gstin: c.clientGST || '29XXXXX0000X1Z5',
      }
    },
    previewInvoiceMeta() {
      const c = this.formData.invoiceClientInfo || {}
      const poNumber = String(this.formData.poNumber || '').trim()
      // Prefer explicit toggle + value; also accept a non-empty packageCode
      // alone so a stale hasPackageCode=false (e.g. old b-collapse v-model
      // wipe) does not hide a code the user already typed.
      const rawPkg = String(this.formData.packageCode || '').trim()
      const packageCode = (this.formData.hasPackageCode || !!rawPkg) ? rawPkg : ''
      // Status is for workflow/list only — never changes invoiceId. Create
      // wizard defaults to Raised; post-submit / edit paths may pass Revised.
      // Printable preview does not render a REVISED badge.
      const status = this.formData.status || this.createdInvoiceStatus || ''
      return {
        invoiceId: this.formData.invoiceId || 'SH000',
        invoiceDate: this.formData.invoiceDate || moment().format('YYYY-MM-DD'),
        placeOfSupply: c.placeOfSupply || c.clientState || this.formData.location || 'Maharashtra',
        // "PO / Ref Number #" (WizardMoreDetailsStep.vue) — single source for
        // both concepts; GenericInvoicePreview shows it only when filled.
        poNumber,
        packageCode,
        // Feeds GenericInvoicePreview's billingPeriodLabel — only actually
        // rendered by templates that opt in via layout.showBillingPeriodNote
        // (e.g. the "Headcount Billing (Month-to-Day)" built-in), so this is
        // harmless to always pass through regardless of active template.
        billingMonth: this.formData.billingMonth || '',
        status,
      }
    },
    // Explicit status prop for GenericInvoicePreview (same source as meta).
    previewInvoiceStatus() {
      return this.formData.status || this.createdInvoiceStatus || ''
    },
    // The calc engine only produces a single combined `gst` figure (see
    // invoice-calculation-engine.js's buildCalcContext) — split it into the
    // sgst/cgst/igst breakdown GenericInvoicePreview renders, using the same
    // gstType logic the engine itself used to pick the rate in the first place.
    previewTotalsForGeneric() {
      const gst = this.gstAmount || 0
      const gstType = this.formData.gstType
      let sgst = 0
      let cgst = 0
      let igst = 0
      if (gstType === 'IGST') {
        igst = gst
      } else if (gstType !== 'SEZ' && gstType !== 'sez') {
        sgst = parseFloat((gst / 2).toFixed(2))
        cgst = gst - sgst
      }
      return {
        subtotal: this.subtotal || 0,
        sgst,
        cgst,
        igst,
        total: this.totalAmount || 0,
        reverseCharge: 0,
      }
    },
    previewBankDetailsForGeneric() {
      const s = this.formData.invoiceSattvaInfo || {}
      return {
        bankName: s.bankName || 'HDFC Bank',
        accountName: s.accountName || s.companyName || 'Sattva Human Private Limited',
        accountNumber: s.accountNumber || '50200046864261',
        ifscCode: s.ifscCode || 'HDFC0000486',
        accountType: s.accountType || 'Current Account',
      }
    },
    previewLineItemsForGeneric() {
      if (this.lineItems && this.lineItems.length) return this.lineItems
      // Before any real row exists, build a dummy row shaped to whatever
      // columns are ACTUALLY configured right now (lineItemValidationColumns
      // — same dynamicColumnConfig-aware source as the real table) rather
      // than a hardcoded 4-field shape — so even the empty-state preview
      // reflects Manage Columns customizations instead of silently reverting
      // to the original default template's columns.
      const cols = this.lineItemValidationColumns
      if (!cols.length) {
        return [{ srNo: 1, itemName: 'Manpower Supply', title: 'Manpower Supply', quantity: 1, unitPrice: 19582, amount: 19582 }]
      }
      const sampleMoney = {
        unitPrice: 19582,
        rate: 19582,
        rateCard: 39790,
        ctc: 600000,
        amount: 19582,
        baseAmount: 19582,
        monthToDayConversion: 1326,
        taxableValue: 19582,
        igstAmount: 3524.76,
        lineTotal: 23106.76,
      }
      const row = {}
      cols.forEach((c) => {
        const k = c.key
        if (k === 'itemName' || k === 'title' || k === 'description' || k === 'candidateName' || k === 'employeeName') {
          row[k] = k === 'candidateName' || k === 'employeeName' ? 'Sample Candidate' : 'Manpower Supply'
        } else if (k === 'quantity' || k === 'totalHeadCount') {
          row[k] = 1
        } else if (k === 'presentDays' || k === 'totalBillableDays') {
          row[k] = 22
        } else if (k === 'dayToMonthRatio') {
          row[k] = 1
        } else if (k === 'payoutRate' || k === 'igstRate' || c.type === 'percentage') {
          row[k] = k === 'payoutRate' ? 8.33 : 18
        } else if (Object.prototype.hasOwnProperty.call(sampleMoney, k)) {
          row[k] = sampleMoney[k]
        } else if (c.type === 'currency' || c.type === 'computed') {
          row[k] = 19582
        } else if (c.type === 'number') {
          row[k] = 1
        } else if (c.type === 'sac-select' || k === 'sacCode') {
          row[k] = this.defaultSacCode || '998511'
        } else {
          row[k] = ''
        }
      })
      if (isOneLineItemsStructure(cols)) {
        row.title = row.title || row.itemName || 'Manpower Supply'
      } else if (!Object.prototype.hasOwnProperty.call(row, 'title')) {
        row.title = ''
      }
      return [recomputeRow(row, cols)]
    },
    // Step 0 (Client) gate — progressive Client → Legal Name → State.
    // Legal Name is required once options exist (always after a successful
    // master-data load, including the client-name fallback). State is
    // required only when the selected legal entity has state options.
    isClientStepValid() {
      if (!this.formData.client) return false
      if (this.clientLegalNameOptions.length > 0 && !this.formData.invoiceClientInfo.legalName) {
        return false
      }
      if (this.clientStateOptions.length > 0) {
        return !!this.formData.clientState
      }
      return true
    },
    // Step 1 (Sattva GST / Location) gate.
    isLocationStepValid() {
      return !!this.formData.location
    },
    activeTemplateName() {
      const name = (this.loadedTemplateRecord && this.loadedTemplateRecord.name)
        || this.activeTemplate.name
        || 'Standard'
      // Auto mode still resolves to a concrete preset — show both so the
      // wizard badge explains why columns look the way they do.
      if (this.manualTemplateId == null) return `Auto · ${name}`
      return name
    },
    templateIsCustom() {
      return !!(this.loadedTemplateRecord && this.loadedTemplateRecord.id)
    },
    invoiceNumberIsFallback() {
      return !!(this.invoiceNumberSource && String(this.invoiceNumberSource).indexOf('frontend-computed-fallback') === 0)
    },
    // Step 3 (More Details) gate — Category/Vertical (moved here from the
    // old "Who & Where" section) plus the former "Invoice Details"/
    // "Reference Codes" fields. Invoice # is auto-generated (see
    // refreshInvoiceNumber()) rather than manually typed, so it's always
    // correct-by-construction once generated — just require it to be
    // non-empty here. The old `.includes('SH')` check is kept implicitly
    // true for both prefixes (SH/SHI) but is no longer the thing we
    // validate against, since a manually-editable free-text field no
    // longer exists for known verticals (WizardMoreDetailsStep.vue shows
    // it read-only).
    isMoreDetailsValid() {
      const coreOk = !!(
        this.formData.category &&
        this.formData.vertical &&
        this.formData.invoiceId &&
        this.formData.invoiceDate &&
        this.formData.gstType
      )
      if (!coreOk) return false
      const extraFields = (this.activeTemplate.headerFields || []).filter(
        (f) => !['invoiceDate', 'invoiceId', 'paymentTerms', 'dueDate', 'poNumber', 'gstType', 'description', 'billingMonth'].includes(f.key)
          && f.visible !== false
          && f.required,
      )
      return extraFields.every((f) => {
        const val = this.formData[f.key]
        return val !== undefined && val !== null && val !== ''
      })
        && (!this.formData.hasPackageCode || !!String(this.formData.packageCode || '').trim())
    },
    // (2026-07-22): "Add Candidates" applies when category is Recruitment
    // OR Contractual, AND vertical is "Service Industry" specifically (not
    // "Infra Industry") — see getInvoicePrefixForVertical()'s jsdoc for
    // where that exact vertical string comes from. Vertical is required
    // alongside category so this doesn't fire for a Recruitment/Contractual
    // invoice under Infra, and doesn't fire before Vertical is even picked.
    isCandidateCategory() {
      return (this.formData.category === 'Recruitment' || this.formData.category === 'Contractual')
        && this.formData.vertical === 'Service Industry'
    },
    // Step 2 (Line Items) gate — kept the same name/logic as before the
    // reordering (only its position in the flow changed: it now gates the
    // move from Line Items → More Details instead of Line Items → Payment).
    canProceedPastLineItems() {
      if (!this.formData.category || !this.formData.vertical) return false
      // BUG FIX (2026-07-17): this used to only require ONE row (`.some(...)`)
      // to be valid, which meant a table with e.g. 3 rows — 1 filled in
      // correctly and 2 left blank/garbage — would still let the user
      // proceed, silently carrying the 2 broken rows all the way to
      // submission. Now requires every row to pass validation (see
      // lineItemRowErrors/hasLineItemErrors above, which is column-driven —
      // works for the standard template, RPO template, and any
      // Manage-Columns-customized set of columns alike), and that at least
      // one row exists.
      const hasValidLineItems = this.lineItems.length > 0 && !this.hasLineItemErrors
      if (!this.isCandidateCategory) {
        return hasValidLineItems
      }
      if (this.isForCandidates === false) {
        return hasValidLineItems
      }
      if (this.isForCandidates === true) {
        return this.selectedCandidates.length > 0 && hasValidLineItems
      }
      return false
    },
    calcResults() {
      const ctx = buildCalcContext(
        this.lineItems,
        this.formData,
        this.activeTemplate.taxConfig || {},
        this.gstRates,
      )
      return evaluateCalcRules(this.activeTemplate.calculationRules || [], ctx)
    },
    visibleCalcRows() {
      return getVisibleCalcRows(this.activeTemplate.calculationRules || [], this.calcResults)
    },
    subtotal() {
      return this.calcResults.subtotal || 0
    },
    gstAmount() {
      return this.calcResults.gst || 0
    },
    calculatedTdsAmount() {
      return this.calcResults.tds || 0
    },
    totalAmount() {
      return this.calcResults.total || 0
    },
    calculateDueDate() {
      if (this.formData.payDate && this.formData.creditPeriod) {
        const payDate = moment(this.formData.payDate)
        const creditPeriod = parseFloat(this.formData.creditPeriod) || 0
        if (payDate.isValid() && creditPeriod > 0) {
          return payDate.add(creditPeriod, 'days').format('YYYY-MM-DD')
        }
      }
      return ''
    },
    isFormValid() {
      return this.isClientStepValid && this.isLocationStepValid && this.canProceedPastLineItems && this.isMoreDetailsValid
    },
    formValidationMessage() {
      if (this.hasColumnConfigErrors) {
        const formulaErr = (this.columnFormulaIssues || []).find(Boolean)
        const labelErr = (this.columnLabelIssues || []).find(Boolean)
        return formulaErr || labelErr || 'Fix the column name or formula in Manage Columns before submitting.'
      }
      if (!this.lineItems || this.lineItems.length === 0) {
        return 'Please add at least one line item.'
      }
      if (this.hasLineItemErrors) {
        const first = (this.lineItemErrorSummary || [])[0]
        const extra = this.lineItemErrorSummary.length > 1
          ? ` (+${this.lineItemErrorSummary.length - 1} more)`
          : ''
        return first ? `${first}${extra}` : 'Please fix the line items table before submitting.'
      }
      if (!this.canProceedPastLineItems) {
        return 'Complete the Line Items step (category, vertical, and valid rows) before submitting.'
      }
      if (!this.isClientStepValid) return 'Select a client before submitting.'
      if (!this.isLocationStepValid) return 'Select a Sattva location before submitting.'
      if (!this.isMoreDetailsValid) return 'Complete required fields on More Details (invoice #, date, GST type, category, vertical).'
      return 'Please complete all required fields.'
    },
    hasSupportingDocuments() {
      if (this.invoiceFiles && this.invoiceFiles.length) return true
      return !!this.invoiceFile
    },
    canSubmitInvoice() {
      return this.isFormValid
    },
    hasSelectedCandidates() {
      return this.candidateList.some(candidate => candidate.selected === true)
    },
    // Text/name columns the user can choose when mentioning a candidate in Line Items
    candidateNameColumnOptions() {
      const NON_TEXT = new Set(['number', 'currency', 'computed', 'percentage', 'sac-select', 'date'])
      const cols = this.usesSeparateTableColumns
        ? [...(this.lineItemValidationColumns || []), ...(this.lineItemValidationColumnsTable2 || [])]
        : (this.lineItemValidationColumns || [])
      const seen = new Set()
      return cols.filter((col) => {
        if (!col || !col.key || seen.has(col.key)) return false
        seen.add(col.key)
        if (this.isSrNoColumnKey(col.key, col.label)) return false
        if (NON_TEXT.has(col.type)) return false
        return true
      })
    },
    candidateNameColumnSelectOptions() {
      const opts = this.candidateNameColumnOptions.map((c) => ({ value: c.key, text: c.label || c.key }))
      return [{ value: null, text: 'Select a column…' }, ...opts]
    },
    // A4 fit-box styles — outer box is the *scaled* footprint so scroll
    // height matches what the user sees; inner scaler holds the true A4 sheet.
    previewPanelFitStyle() { return this.a4FitBoxStyle(this.previewPanelFit) },
    previewPanelScalerStyle() { return this.a4ScalerStyle(this.previewPanelFit) },
    previewMobileFitStyle() { return this.a4FitBoxStyle(this.previewMobileFit) },
    previewMobileScalerStyle() { return this.a4ScalerStyle(this.previewMobileFit) },
    previewModalFitStyle() { return this.a4FitBoxStyle(this.previewModalFit) },
    previewModalScalerStyle() { return this.a4ScalerStyle(this.previewModalFit) },
  },
  watch: {
    showMobilePreview(val) {
      if (!val) return
      this.$nextTick(() => {
        this.observeA4Stage('previewMobileStage', 'previewMobileFit', 'previewMobileRoot')
      })
    },
    showPreviewModal(val) {
      if (!val) return
      this.$nextTick(() => {
        this.observeA4Stage('previewModalStage', 'previewModalFit', 'previewModalRoot')
      })
    },
    // Re-measure when preview content height changes (line items, style, etc.)
    previewLineItemsForGeneric: {
      deep: true,
      handler() {
        this.$nextTick(() => this.syncAllA4PreviewFits())
      },
    },
    // Edit Columns mutates nested label/type/formula fields — bump epoch so
    // the invoice preview (and modal Preview tab) re-read the live column list.
    // Also debounce-persist column layout onto the server draft.
    dynamicColumnConfig: {
      deep: true,
      handler() {
        this.columnConfigEpoch += 1
        this.$nextTick(() => this.syncAllA4PreviewFits())
        if (typeof this._debouncedSaveDraft === 'function') this._debouncedSaveDraft()
      },
    },
    dynamicColumnConfigTable2: {
      deep: true,
      handler() {
        this.columnConfigEpoch += 1
        this.$nextTick(() => this.syncAllA4PreviewFits())
        if (typeof this._debouncedSaveDraft === 'function') this._debouncedSaveDraft()
      },
    },
    effectivePreviewStyle() {
      this.$nextTick(() => this.syncAllA4PreviewFits())
    },
    lineItemTemplateType: {
      handler(newVal, oldVal) {
        // BUG FIX (2026-07-22): this reshape only ever knows about two
        // shapes — 'rpo' (employeeId/employeeName/doj/bu/geo/baseAmount) or
        // plain generic (itemName/quantity/unitPrice/amount) — driven purely
        // by the client+category heuristic (getClientInvoiceLineItemTemplate),
        // completely independent of whichever template is ACTUALLY active.
        // Once a user explicitly picks a template via the gallery/inline
        // selector (manualTemplateId set — e.g. the new "Headcount Billing"
        // built-in, or any custom template with its own column set), a later
        // Category change that flips this rpo/generic heuristic used to
        // still fire and clobber every line item into one of these two
        // hardcoded shapes, discarding fields like rateCard/presentDays/
        // totalHeadCount entirely and wiping any Manage Columns
        // customization (dynamicColumnConfig) — silent data loss with no
        // warning. The manual override already wins over auto-detection
        // everywhere else (see loadTemplateForClient/onTemplateSelected), so
        // it should win here too: skip this auto-reshape entirely while one
        // is active.
        if (this.manualTemplateId != null) return
        if (oldVal && newVal && oldVal !== newVal) {
          this.dynamicColumnConfig = null
          this.dynamicColumnConfigTable2 = null
        }
        if (oldVal && newVal && oldVal !== newVal && this.lineItems.length > 0) {
          const defaultSac = this.defaultSacCode
          this.lineItems = this.lineItems.map(item => {
            if (newVal === 'rpo') {
              return {
                employeeId: item.employeeId || item.cid || '',
                employeeName: item.employeeName || item.itemName || '',
                doj: item.doj || null,
                bu: item.bu || '',
                geo: item.geo || '',
                baseAmount: parseFloat(item.baseAmount || item.amount || item.unitPrice || 0),
                amount: parseFloat(item.baseAmount || item.amount || item.unitPrice || 0),
                cid: item.cid,
                jid: item.jid
              }
            }
            return {
              itemName: item.itemName || item.title || item.employeeName || '',
              title: item.title || item.itemName || item.employeeName || '',
              sacCode: resolveSacHsnCode(item) || defaultSac,
              quantity: item.quantity || 1,
              unitPrice: parseFloat(item.unitPrice || item.baseAmount || item.amount || 0),
              amount: parseFloat(item.amount || item.baseAmount || 0),
              cid: item.cid,
              jid: item.jid
            }
          })
        }
      }
    },
    'formData.vertical'(newVal, oldVal) {
      // Skip while a draft/prefill is being applied wholesale — that flow
      // restores a previously-generated invoiceId (and possibly a
      // previously-valid candidate selection) and shouldn't trigger a fresh
      // auto-generation or candidate reset that overwrites either.
      if (this._restoringDraft) return
      // 2026-07-22: Vertical is one half of isCandidateCategory's
      // Category+Vertical gate — see resetCandidateSelectionIfNotEligible().
      // Only Category changes used to trigger this reset, so switching
      // Vertical away from "Service Industry" while candidates were already
      // selected left them stranded in state that isCandidateCategory (and
      // therefore the whole "Is this for candidates?" UI) no longer agreed
      // applied.
      this.resetCandidateSelectionIfNotEligible()
      if (!newVal || newVal === oldVal) return
      this.refreshInvoiceNumber()
    },
    'formData.useSecondLineItemsTable'(enabled) {
      if (this._restoringDraft) return
      if (!enabled) {
        this.formData.secondTableColumnsSame = true
        this.dynamicColumnConfigTable2 = null
        this.columnConfigEditTable = 1
      }
    },
    'formData.invoiceDate'() {
      if (this.formData.paymentTerms) {
        this.onPaymentTermsSelected(this.formData.paymentTerms)
      }
    },
    // Keep invoiceName in sync with client / doc type / invoice # / billing month.
    // Always regenerate (no manual override pattern in this flow).
    'formData.client'() { this.syncAutoInvoiceName() },
    'formData.documentType'() { this.syncAutoInvoiceName() },
    'formData.invoiceId'() { this.syncAutoInvoiceName() },
    'formData.billingMonth'() { this.syncAutoInvoiceName() },
    'formData.invoiceClientInfo.clientName'() { this.syncAutoInvoiceName() },
    formData: {
      deep: true,
      handler() {
        if (this._suppressDraftSave) return
        this._debouncedSaveDraft()
      },
    },
    lineItems: {
      deep: true,
      handler() {
        if (this._suppressDraftSave) return
        this._debouncedSaveDraft()
      },
    },
    previewStyleOverride() { this._debouncedSaveDraft() },
    manualTemplateId() { this._debouncedSaveDraft() },
  },
  async created() {
    this._debouncedSaveDraft = this._buildDebouncedSaveDraft()
    await this.loadInitialData()
    this.generateBillingMonthOptions()
    const duplicateFrom = this.$route.query && this.$route.query.duplicateFrom
    if (duplicateFrom) {
      // Duplicate wins over unsaved draft — user explicitly asked to clone.
      await this.hydrateFromDuplicateSource(duplicateFrom)
    } else {
      // Restores server draft; one-time migrates then purges legacy
      // `invoice_draft_v1` (see invoiceDraftService.consumeLegacyLocalDraft).
      await this._restoreDraftIfAvailable()
      if (!this.showDraftBanner) {
        await this.applyRouteQueryPrefill()
      }
    }
    // Purge retired template/preset browser keys (draft key already handled).
    try {
      purgeLegacyTemplatePreferenceKeys()
      localStorage.removeItem('crm_invoice_templates_v1')
      localStorage.removeItem('crm_invoice_last_line_items_preset')
    } catch (e) { /* ignore */ }
  },
  mounted() {
    this.initA4PreviewObservers()
    this.$nextTick(() => this.syncAllA4PreviewFits())
  },
  beforeDestroy() {
    this.teardownA4PreviewObservers()
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this._onA4PreviewWindowResize)
    }
  },
  methods: {
    a4FitBoxStyle(fit) {
      const scale = (fit && fit.scale) || 1
      const w = (fit && fit.widthPx) || 794
      const h = (fit && fit.heightPx) || 1123
      return {
        width: `${Math.round(w * scale)}px`,
        height: `${Math.round(h * scale)}px`,
        margin: '0 auto',
        position: 'relative',
      }
    },
    a4ScalerStyle(fit) {
      const scale = (fit && fit.scale) || 1
      const w = (fit && fit.widthPx) || 794
      return {
        width: `${w}px`,
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
      }
    },
    initA4PreviewObservers() {
      this._onA4PreviewWindowResize = () => this.syncAllA4PreviewFits()
      window.addEventListener('resize', this._onA4PreviewWindowResize)
      // Desktop panel is always mounted at >=lg; mobile/modal bind via watchers.
      this.observeA4Stage('previewPanelStage', 'previewPanelFit', 'previewPanelRoot')
    },
    teardownA4PreviewObservers() {
      ;(this._a4PreviewObservers || []).forEach(({ ro }) => {
        try { ro.disconnect() } catch (e) { /* ignore */ }
      })
      this._a4PreviewObservers = []
    },
    observeA4Stage(stageRef, fitKey, sheetRef) {
      if (typeof ResizeObserver === 'undefined') return
      this._a4PreviewObservers = this._a4PreviewObservers || []
      // Drop any prior observer for this stage ref.
      this._a4PreviewObservers = this._a4PreviewObservers.filter((entry) => {
        if (entry.stageRef === stageRef) {
          try { entry.ro.disconnect() } catch (e) { /* ignore */ }
          return false
        }
        return true
      })
      const stageEl = this.$refs[stageRef]
      if (!stageEl) return
      const ro = new ResizeObserver(() => {
        this.syncA4PreviewFit(stageRef, fitKey, sheetRef)
      })
      ro.observe(stageEl)
      const sheetComp = this.$refs[sheetRef]
      const sheetEl = sheetComp && sheetComp.$el
      if (sheetEl) ro.observe(sheetEl)
      this._a4PreviewObservers.push({ ro, stageRef, fitKey, sheetRef })
      this.syncA4PreviewFit(stageRef, fitKey, sheetRef)
    },
    syncA4PreviewFit(stageRef, fitKey, sheetRef) {
      const stageEl = this.$refs[stageRef]
      const sheetComp = this.$refs[sheetRef]
      const sheetEl = sheetComp && sheetComp.$el
      if (!stageEl || !sheetEl) return
      const stageW = stageEl.clientWidth
      if (stageW <= 0) return
      const widthPx = sheetEl.offsetWidth || 794
      const heightPx = sheetEl.offsetHeight || 1123
      // Leave a tiny inset so the page shadow isn't clipped by the stage.
      const available = Math.max(120, stageW - 8)
      const scale = Math.min(1, available / widthPx)
      const next = { scale, widthPx, heightPx }
      const prev = this[fitKey]
      if (
        prev
        && Math.abs(prev.scale - next.scale) < 0.001
        && prev.widthPx === next.widthPx
        && prev.heightPx === next.heightPx
      ) {
        return
      }
      this[fitKey] = next
    },
    syncAllA4PreviewFits() {
      this.syncA4PreviewFit('previewPanelStage', 'previewPanelFit', 'previewPanelRoot')
      if (this.showMobilePreview) {
        this.syncA4PreviewFit('previewMobileStage', 'previewMobileFit', 'previewMobileRoot')
      }
      if (this.showPreviewModal) {
        this.syncA4PreviewFit('previewModalStage', 'previewModalFit', 'previewModalRoot')
      }
    },
    // Shared with WizardLineItemsStep.vue/WizardPaymentStep.vue/
    // GenericInvoicePreview.vue — see src/utils/currency-format.js.
    // 2026-07-22 BUG FIX: the candidate-selection modal's "Amount" column
    // called `formatCurrency(...)` directly in its template (`#cell(revenue)`
    // below) as if it were an instance method here, but it was never
    // imported/defined on THIS component after formatCurrency was
    // extracted out into the shared util — threw "formatCurrency is not a
    // function" the moment that modal's table rendered. Wiring it through
    // the shared `showDecimals` toggle for consistency with every other
    // amount display in this wizard.
    formatCurrency(val) {
      return formatCurrencyUtil(val, this.showDecimals ? 2 : 0)
    },
    async loadInitialData() {
      try {
        const [
          clientsRes, locationsRes, sattvaLocationsRes, creditPeriodsRes, tdsRes,
          gstRes, sacRes, paymentTermsRes, templatesRes,
        ] = await Promise.all([
          clientService.getClients(),
          locationService.getAll(),
          // Sattva Location dropdown source — see getSattvaLocationOptions()'s
          // jsdoc: only resolves non-null once the dedicated Sattva-company-
          // details table/endpoint is actually live; null means "keep using
          // the existing /locations list below" (handled after this block).
          invoiceConfigService.getSattvaLocationOptions(),
          invoiceConfigService.getCreditPeriods(),
          invoiceConfigService.getTDSPercentages(),
          invoiceConfigService.getGSTRates(),
          invoiceConfigService.getSACCodes(),
          invoiceConfigService.getPaymentTerms(),
          invoiceConfigService.listTemplates({ status: 'active' }),
        ])

        if (clientsRes && clientsRes.data) {
          const arr = Array.isArray(clientsRes.data) ? clientsRes.data : []
          this.clients = arr.map(c => (typeof c === 'string' ? { title: c } : { title: c.client_name || c.name || c.title || c.client || 'N/A' }))
        }
        if (locationsRes && locationsRes.data) {
          const raw = Array.isArray(locationsRes.data)
            ? locationsRes.data
            : (Array.isArray(locationsRes.data.data) ? locationsRes.data.data : [])
          this.masterLocations = this.normalizeLocationOptions(raw)
          this.locations = this.masterLocations.slice()
        }
        if (sattvaLocationsRes && sattvaLocationsRes.status === 'OK' && sattvaLocationsRes.data && sattvaLocationsRes.data.length) {
          // Dedicated Sattva-company-details table is live — use it as the
          // canonical "Sattva Location" dropdown source, but enrich missing
          // numeric ids from /locations so "Pune" still resolves locationId.
          this.locations = this.enrichLocationIdsFromMaster(
            this.normalizeLocationOptions(sattvaLocationsRes.data),
          )
          console.info('[InvoiceCreateManual] Sattva Location dropdown sourced from dedicated sattva-company-details table.')
        }
        if (templatesRes && templatesRes.status === 'OK' && Array.isArray(templatesRes.data)) {
          this.templateOptions = templatesRes.data
          if (templatesRes._source === 'session') {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Templates not loaded from server',
                text: 'Template API unavailable — custom templates will not persist across refresh. Backend /invoice/config/templates is required.',
                icon: 'AlertCircleIcon',
                variant: 'warning',
              },
            })
          }
        }
        if (creditPeriodsRes && creditPeriodsRes.status === 'OK') {
          // Coerce to numbers so b-form-select matches master-data
          // credit_period values applied via applyCreditPeriodFromMaster().
          this.creditPeriodOptions = creditPeriodsRes.data.map(p => {
            const days = parseInt(p, 10)
            return {
              text: `${Number.isNaN(days) ? p : days} days`,
              value: Number.isNaN(days) ? p : days,
            }
          })
        }
        if (tdsRes && tdsRes.status === 'OK') {
          // Backend currently returns an empty list here (genuinely not
          // configured yet, not a fetch error) — fall back to a disabled
          // placeholder so the native <select> (WizardPaymentStep.vue)
          // doesn't render as a bare, seemingly-broken empty box.
          this.tdsPercentageOptions = tdsRes.data.length
            ? tdsRes.data.map(p => ({ text: `${p}%`, value: p }))
            : [{ text: 'No TDS percentages configured yet', value: null, disabled: true }]
        }
        if (gstRes && gstRes.status === 'OK') {
          this.gstRates = gstRes.data
        }
        if (sacRes && sacRes.status === 'OK' && sacRes.data) {
          this.sacCodeOptions = sacRes.data.map(code => ({
            text: `${code.code} - ${code.description}`,
            value: code.code
          }))
          // Same data, {title,value} shape for the header-level SAC / HSN Code
          // creatable-select (WizardMoreDetailsStep / InvoiceToFromSection).
          // Line items use sacCodeOptions ({text,value}) with the same
          // taggable/push-tags pattern in WizardLineItemsStep.
          this.sacCodeSelectOptions = sacRes.data.map(code => ({
            title: `${code.code} - ${code.description}`,
            value: code.code
          }))
        }
        if (paymentTermsRes && paymentTermsRes.status === 'OK' && paymentTermsRes.data) {
          this.paymentTermsOptions = paymentTermsRes.data
          if (paymentTermsRes._source === 'derived-from-credit-periods') {
            // The backend's dedicated `paymentTerms` array in `/invoice/config`
            // is still genuinely empty — these options are derived client-side
            // from the same payload's (real, populated) `creditPeriods` field
            // instead. See getPaymentTerms()'s jsdoc in invoice-config-service.js.
            console.info('[InvoiceCreateManual] Payment Terms derived from creditPeriods — no dedicated backend paymentTerms config yet.')
          }
          const dueOnReceipt = this.paymentTermsOptions.find(t => t.days === 0 || t.name === 'Due on Receipt')
          if (dueOnReceipt && !this.formData.paymentTerms) {
            this.formData.paymentTerms = dueOnReceipt
            this.$nextTick(() => this.onPaymentTermsSelected(dueOnReceipt))
          }
        }
      } catch (error) {
        console.error('Error loading initial data:', error)
      }
    },
    async applyRouteQueryPrefill() {
      const q = this.$route.query
      if (!q || !Object.keys(q).length) return
      if (q.duplicateFrom) {
        await this.hydrateFromDuplicateSource(q.duplicateFrom)
        return
      }
      if (q.client) {
        this.formData.client = q.client
        await this.onClientSelected(q.client)
      }
      if (q.category) {
        this.formData.category = q.category
        this.onCategorySelected()
      }
      if (q.cid != null && q.jid != null) {
        this.isForCandidates = true
        const baseAmt = parseFloat((parseFloat(q.revenue) || 0).toFixed(2))
        const candidate = {
          cid: q.cid,
          jid: q.jid,
          candidate_name: q.candidate_name || 'N/A',
          position_name: q.position_name || 'N/A',
          revenue: baseAmt,
          base_amount: baseAmt,
        }
        this.selectedCandidates.push(candidate)
        this.selectedCandidateIds.push(`${q.cid}-${q.jid}`)
        this.convertCandidatesToLineItems()
      }
    },

    /** Truthy helpers for API 0/1/'true'/'false' flags. */
    parseInvoiceFlag(val) {
      return val === true || val === 1 || val === '1' || val === 'true' || val === 'TRUE'
    },

    cloneColumnConfig(cols) {
      try {
        return JSON.parse(JSON.stringify(cols || []))
      } catch (e) {
        return (cols || []).map((c) => ({ ...c }))
      }
    },

    rowMatchesEditTable(item) {
      if (!this.usesSeparateTableColumnsNow()) return true
      const group = Number(item && item.tableGroup) === 2 ? 2 : 1
      return group === Number(this.columnConfigEditTable)
    },

    table2ColumnsDiverged() {
      if (!this.usesSeparateTableColumnsNow()) return false
      const t2 = this.dynamicColumnConfigTable2
      if (!Array.isArray(t2) || !t2.length) return false
      try {
        return JSON.stringify(t2) !== JSON.stringify(this.dynamicColumnConfig || [])
      } catch (e) {
        return true
      }
    },

    async confirmResetDivergedTable2() {
      if (!this.table2ColumnsDiverged()) {
        this._preserveTable2OnPreset = false
        return
      }
      const resetBoth = await this.$bvModal.msgBoxConfirm(
        'Table 2 has its own columns. Reset Table 2 to the new preset as well, or keep Table 2’s columns?',
        {
          title: 'Table 2 columns',
          size: 'sm',
          buttonSize: 'sm',
          okTitle: 'Reset Table 2',
          cancelTitle: 'Keep Table 2',
          centered: true,
          footerClass: 'p-2',
        },
      )
      this._preserveTable2OnPreset = !resetBoth
    },

    nullColumnConfigsForPreset() {
      this.dynamicColumnConfig = null
      if (!this._preserveTable2OnPreset) this.dynamicColumnConfigTable2 = null
    },

    usesSeparateTableColumnsNow() {
      return !!(this.formData.useSecondLineItemsTable && this.formData.secondTableColumnsSame === false)
    },

    getActiveColumnConfigArray() {
      if (this.usesSeparateTableColumnsNow() && Number(this.columnConfigEditTable) === 2) {
        return this.dynamicColumnConfigTable2
      }
      return this.dynamicColumnConfig
    },

    setActiveColumnConfigArray(val) {
      if (this.usesSeparateTableColumnsNow() && Number(this.columnConfigEditTable) === 2) {
        this.dynamicColumnConfigTable2 = val
      } else {
        this.dynamicColumnConfig = val
      }
      this.columnConfigEpoch += 1
    },

    ensureActiveColumnConfig() {
      if (this.usesSeparateTableColumnsNow() && Number(this.columnConfigEditTable) === 2) {
        if (!this.dynamicColumnConfigTable2 || !this.dynamicColumnConfigTable2.length) {
          this.dynamicColumnConfigTable2 = this.cloneColumnConfig(
            (this.dynamicColumnConfig && this.dynamicColumnConfig.length)
              ? this.dynamicColumnConfig
              : this.getBaseColumnConfig(),
          )
        }
        return this.dynamicColumnConfigTable2
      }
      if (!this.dynamicColumnConfig || !this.dynamicColumnConfig.length) {
        this.resetColumnConfigFromTemplate()
      }
      return this.dynamicColumnConfig
    },

    resolveValidationColumnsFromSource(sourceConfig) {
      return resolveCreateValidationColumns(
        sourceConfig,
        (this.activeTemplate && this.activeTemplate.lineItemColumns) || [],
      )
    },

    getValidationColumnsForTable(group) {
      return Number(group) === 2 && this.usesSeparateTableColumnsNow()
        ? this.lineItemValidationColumnsTable2
        : this.lineItemValidationColumns
    },

    getValidationColumnsForRow(row) {
      return this.getValidationColumnsForTable(row && row.tableGroup)
    },

    async onSecondTableColumnsSameChange(same) {
      const next = same !== false
      const prev = this.formData.secondTableColumnsSame !== false
      if (next === prev) {
        this.formData.secondTableColumnsSame = next
        return
      }
      if (!next) {
        // Yes → No: clone Table 1 columns into Table 2 as a starting point.
        this.initColumnConfig()
        this.dynamicColumnConfigTable2 = this.cloneColumnConfig(
          (this.dynamicColumnConfig && this.dynamicColumnConfig.length)
            ? this.dynamicColumnConfig
            : this.getBaseColumnConfig(),
        )
        this.formData.secondTableColumnsSame = false
        this.columnConfigEditTable = 1
        this.columnConfigEpoch += 1
        this.$nextTick(() => this.recomputeAllLineItems())
        return
      }
      // No → Yes: warn if Table 2 config diverged, then share Table 1 again.
      const t2 = this.dynamicColumnConfigTable2
      const diverged = Array.isArray(t2) && t2.length > 0
        && JSON.stringify(t2) !== JSON.stringify(this.dynamicColumnConfig || [])
      if (diverged) {
        const ok = await this.$bvModal.msgBoxConfirm(
          'Table 2 has different columns. Switching to “same columns” will discard Table 2’s column layout and use Table 1’s columns for both tables. Continue?',
          {
            title: 'Use the same columns?',
            size: 'sm',
            buttonSize: 'sm',
            okTitle: 'Use Table 1 columns',
            cancelTitle: 'Cancel',
            centered: true,
            footerClass: 'p-2',
          },
        )
        if (!ok) return
      }
      this.formData.secondTableColumnsSame = true
      this.dynamicColumnConfigTable2 = null
      this.columnConfigEditTable = 1
      this.columnConfigEpoch += 1
      this.$nextTick(() => this.recomputeAllLineItems())
    },

    onColumnConfigEditTableChange() {
      this.ensureActiveColumnConfig()
      this.newColumnToAdd = null
      this.customColumnLabel = ''
      this.columnConfigEpoch += 1
    },

    advanceBillingMonthValue(billingMonth) {
      if (!billingMonth) return currentBillingMonthValue()
      const m = moment(String(billingMonth).trim(), ['MMM-YYYY', 'MMMM-YYYY', 'MMM YYYY', 'YYYY-MM'], true)
      let next = currentBillingMonthValue()
      if (m.isValid()) {
        next = m.clone().add(1, 'month').format('MMM-YYYY')
      } else {
        const loose = moment(String(billingMonth).trim(), 'MMM-YYYY')
        if (loose.isValid()) next = loose.clone().add(1, 'month').format('MMM-YYYY')
      }
      return clampBillingMonthToCurrentOrPast(next)
    },

    advanceInvoiceDateValue(dateStr) {
      const src = moment(dateStr)
      if (src.isValid()) return src.clone().add(1, 'month').format('YYYY-MM-DD')
      return moment().format('YYYY-MM-DD')
    },

    ensureBillingMonthOption(value) {
      if (!value) return
      const exists = (this.billingMonthOptions || []).some((o) => o.value === value)
      if (!exists) {
        this.billingMonthOptions = [...(this.billingMonthOptions || []), { text: value, value }]
      }
    },

    parseMaybeJsonValue(value) {
      if (value == null) return null
      if (typeof value === 'object') return value
      if (typeof value !== 'string') return null
      const s = value.trim()
      if (!s || (s[0] !== '{' && s[0] !== '[')) return null
      try {
        return JSON.parse(s)
      } catch (_) {
        return null
      }
    },

    parseColumnConfigFromRecord(record) {
      if (!record || typeof record !== 'object') return null
      const meta = this.parseMaybeJsonValue(record.metadata) || {}
      const raw = record.line_item_column_config
        || (record.config && record.config.line_item_column_config)
        || record.lineItemColumnConfig
        || meta.line_item_column_config
        || null
      if (!raw) return null
      if (Array.isArray(raw)) return raw
      try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
        return Array.isArray(parsed) ? parsed : null
      } catch (_) {
        return null
      }
    },

    parseColumnConfigTable2FromRecord(record) {
      if (!record || typeof record !== 'object') return null
      const raw = record.line_item_column_config_table2
        || (record.config && record.config.line_item_column_config_table2)
        || record.lineItemColumnConfigTable2
        || record.dynamicColumnConfigTable2
        || null
      if (!raw) return null
      if (Array.isArray(raw)) return raw
      try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
        return Array.isArray(parsed) ? parsed : null
      } catch (_) {
        return null
      }
    },

    parseLineItemsFromDuplicateRecord(data, candidates) {
      let raw = null
      const jsonRaw = data && (data.line_items_json || data.lineItemsJson)
      if (jsonRaw) {
        try {
          const parsed = typeof jsonRaw === 'string' ? JSON.parse(jsonRaw) : jsonRaw
          if (Array.isArray(parsed) && parsed.length) raw = parsed
        } catch (_) { /* fall through */ }
      }
      if (!raw) raw = (data && (data.line_items || data.lineItems)) || null
      if (!Array.isArray(raw) || !raw.length) {
        const meta = this.parseMaybeJsonValue(data && data.metadata) || {}
        const metaLines = meta.line_items || meta.lineItems || meta.items || null
        if (Array.isArray(metaLines) && metaLines.length) {
          raw = metaLines
        } else if (typeof metaLines === 'string') {
          const parsedMeta = this.parseMaybeJsonValue(metaLines)
          if (Array.isArray(parsedMeta) && parsedMeta.length) raw = parsedMeta
        }
      }
      if (Array.isArray(raw) && raw.length) {
        return raw.map((row) => {
          const src = (row && typeof row === 'object') ? { ...row } : {}
          const group = Number(src.tableGroup) === 2 ? 2 : 1
          // Flatten common API aliases into create-form keys.
          if (src.item_name && !src.itemName) src.itemName = src.item_name
          if (src.title && !src.itemName) src.itemName = src.title
          // Standard only: Item Details and title are the same field.
          // Placement / Headcount / Reliance keep title as an optional overlay.
          if (isOneLineItemsStructure(this.getValidationColumnsForTable(group)) && src.itemName && !src.title) {
            src.title = src.itemName
          }
          if (src.unit_price != null && src.unitPrice == null) src.unitPrice = src.unit_price
          if (src.base_amount != null && src.baseAmount == null) src.baseAmount = src.base_amount
          if (src.candidate_name && !src.candidateName) src.candidateName = src.candidate_name
          if (src.position_name && !src.positionName) src.positionName = src.position_name
          if (src.employee_name && !src.employeeName) src.employeeName = src.employee_name
          if (src.sac_code && !src.sacCode) src.sacCode = src.sac_code
          if (src.hsn_code && !src.sacCode) src.sacCode = src.hsn_code
          const base = this.buildEmptyLineItemRow(group)
          const merged = migrateLineItemSacHsn({ ...base, ...src, tableGroup: group })
          return recomputeRow(merged, this.getValidationColumnsForTable(group))
        })
      }
      if (Array.isArray(candidates) && candidates.length) {
        return candidates.map((c) => {
          const base = this.buildEmptyLineItemRow(1)
          const amount = parseFloat(c.base_amount != null ? c.base_amount : (c.revenue || 0)) || 0
          const merged = migrateLineItemSacHsn({
            ...base,
            itemName: c.candidate_name || c.position_name || 'Recruitment',
            candidateName: c.candidate_name || '',
            positionName: c.position_name || '',
            quantity: 1,
            unitPrice: amount,
            amount,
            baseAmount: amount,
            cid: c.cid,
            jid: c.jid,
            tableGroup: 1,
          })
          return recomputeRow(merged, this.getValidationColumnsForTable(1))
        })
      }
      const baseAmt = parseFloat((data && data.base_amount) || 0) || 0
      if (baseAmt > 0) {
        const base = this.buildEmptyLineItemRow(1)
        return [recomputeRow(migrateLineItemSacHsn({
          ...base,
          itemName: (data && (data.description || data.category)) || 'Services',
          quantity: 1,
          unitPrice: baseAmt,
          amount: baseAmt,
          baseAmount: baseAmt,
          tableGroup: 1,
        }), this.getValidationColumnsForTable(1))]
      }
      return []
    },

    async attachDuplicateSupportingDocuments(data) {
      this.invoiceFile = null
      this.invoiceFiles = []
      this.duplicateSupportCopied = false
      const docs = extractSupportingDocuments(data)
      if (!docs.length) return
      const files = []
      for (let i = 0; i < docs.length; i += 1) {
        try {
          const file = await loadSupportingDocAsFile(docs[i])
          if (file) files.push(file)
        } catch (_) { /* try remaining docs */ }
      }
      if (!files.length) return
      this.invoiceFiles = files
      this.invoiceFile = files[0]
      this.duplicateSupportCopied = true
    },

    bindPaymentTermsFromSource(creditPeriod, paymentTermsRaw) {
      let days = null
      if (creditPeriod != null && String(creditPeriod).trim() !== '') {
        const n = parseInt(creditPeriod, 10)
        if (!Number.isNaN(n)) days = n
      }
      if (days == null && paymentTermsRaw != null) {
        if (typeof paymentTermsRaw === 'object' && paymentTermsRaw.days != null) {
          const n = parseInt(paymentTermsRaw.days, 10)
          if (!Number.isNaN(n)) days = n
        } else {
          const match = String(paymentTermsRaw).match(/(\d+)/)
          if (match) days = parseInt(match[1], 10)
        }
      }
      if (days == null || Number.isNaN(days)) return
      this.applyCreditPeriodFromMaster(days)
    },

    /**
     * Prefill Create Manual from an existing invoice for the next billing month.
     * Does not copy invoice identity, signed docs, payment/dispute status, or created dates.
     */
    async hydrateFromDuplicateSource(sourceId) {
      const id = sourceId != null ? String(sourceId).trim() : ''
      if (!id) return
      this.duplicateLoading = true
      this._restoringDraft = true
      try {
        const res = await invoiceService.getById(id)
        if (!res || res.status !== 'OK' || !res.data) {
          const msg = (res && res.data && res.data.message)
            || (res && res.message)
            || 'Could not load invoice to duplicate.'
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Duplicate failed',
              text: msg,
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
          })
          return
        }

        const data = res.data
        const candidates = Array.isArray(res.candidates)
          ? res.candidates
          : (Array.isArray(data.candidates) ? data.candidates : [])

        // ── Client / category (async master load) ──
        const clientName = data.client || data.client_name || ''
        if (clientName) {
          this.formData.client = clientName
          await this.onClientSelected(clientName)
        }
        if (data.category) {
          this.formData.category = data.category
          if (typeof this.onCategorySelected === 'function') this.onCategorySelected()
        }
        if (data.vertical) this.formData.vertical = data.vertical

        // Overlay client bill-to from source (prefer invoice snapshot over master).
        const legalName = data.client_legal_name || data.legal_name
          || (this.formData.invoiceClientInfo && this.formData.invoiceClientInfo.legalName) || ''
        if (legalName) {
          this.formData.invoiceClientInfo.legalName = legalName
          if (!(this.clientLegalNameOptions || []).includes(legalName)) {
            this.clientLegalNameOptions = [...(this.clientLegalNameOptions || []), legalName]
          }
          await this.onLegalNameSelected(legalName)
        }
        const clientState = data.client_state || data.state
          || (this.formData.invoiceClientInfo && this.formData.invoiceClientInfo.clientState) || ''
        if (clientState) {
          if (!(this.clientStateOptions || []).includes(clientState)) {
            this.clientStateOptions = [...(this.clientStateOptions || []), clientState]
          }
          await this.onClientStateSelected(clientState)
        }
        // Re-apply source bill-to after master fills (source wins).
        this.formData.invoiceClientInfo = {
          ...this.formData.invoiceClientInfo,
          clientName: clientName || this.formData.invoiceClientInfo.clientName || '',
          legalName: legalName || this.formData.invoiceClientInfo.legalName || '',
          clientGST: (data.client_gst || data.clientGST || this.formData.invoiceClientInfo.clientGST || '').toUpperCase(),
          clientAddress: data.client_address || this.formData.invoiceClientInfo.clientAddress || '',
          clientState: clientState || this.formData.invoiceClientInfo.clientState || '',
          placeOfSupply: data.place_of_supply || this.formData.invoiceClientInfo.placeOfSupply || '',
          clientPhone: data.client_phone || this.formData.invoiceClientInfo.clientPhone || '',
        }

        // ── Sattva location ──
        const location = data.location || ''
        if (location) {
          this.formData.location = location
          await this.onLocationSelected(location)
        }
        // Overlay bill-from snapshot from source.
        const srcSac = data.sattva_sac_code || data.sac_code || data.sacCode
          || resolveSacHsnCode(data) || ''
        const srcLut = data.sattva_lut_no || data.lut_no || data.lutNo
          || resolveLutNo(data) || ''
        this.formData.invoiceSattvaInfo = {
          ...this.formData.invoiceSattvaInfo,
          companyName: data.sattva_company_name || this.formData.invoiceSattvaInfo.companyName || '',
          gst: (data.sattva_gst || this.formData.invoiceSattvaInfo.gst || '').toUpperCase(),
          pan: data.sattva_pan || this.formData.invoiceSattvaInfo.pan || '',
          address: data.sattva_address || this.formData.invoiceSattvaInfo.address || '',
          bankName: data.sattva_bank_name || this.formData.invoiceSattvaInfo.bankName || '',
          accountName: data.sattva_account_name || this.formData.invoiceSattvaInfo.accountName || '',
          accountNumber: data.sattva_account_number || this.formData.invoiceSattvaInfo.accountNumber || '',
          accountType: data.sattva_account_type || this.formData.invoiceSattvaInfo.accountType || '',
          ifscCode: data.sattva_ifsc || this.formData.invoiceSattvaInfo.ifscCode || '',
          lutNo: srcLut || this.formData.invoiceSattvaInfo.lutNo || '',
          sacCode: srcSac || this.formData.invoiceSattvaInfo.sacCode || null,
        }
        if (srcLut && !this.lutNoOptions.some((o) => o.title === srcLut || o.value === srcLut)) {
          this.lutNoOptions.push({ title: srcLut, value: srcLut })
        }

        // ── Core commercial fields ──
        if (data.document_type || data.documentType) {
          this.formData.documentType = data.document_type || data.documentType
        }
        this.formData.gstType = data.gst_type || data.gstType || this.formData.gstType || ''
        this.formData.description = data.description || ''
        this.formData.poNumber = data.po_number || data.poNumber || this.formData.poNumber || ''

        const packageCode = String(data.package_code || data.packageCode || '').trim()
        this.formData.packageCode = packageCode
        this.formData.hasPackageCode = !!packageCode

        this.formData.isCollectNPay = this.parseInvoiceFlag(data.is_collect_n_pay ?? data.isCollectNPay)
        if (data.pay_date || data.payDate) {
          const pd = moment(data.pay_date || data.payDate)
          this.formData.payDate = pd.isValid() ? pd.format('YYYY-MM-DD') : null
        }

        this.formData.tdsApplicable = this.parseInvoiceFlag(data.tds_applicable ?? data.tdsApplicable)
        if (data.tds_percentage != null && data.tds_percentage !== '') {
          this.formData.tdsPercentage = parseFloat(data.tds_percentage) || null
        }

        if (data.include_msme_terms != null || data.includeMsmeTerms != null) {
          this.formData.includeMsmeTerms = this.parseInvoiceFlag(
            data.include_msme_terms != null ? data.include_msme_terms : data.includeMsmeTerms,
          )
        } else {
          // Missing flag = opt-out; never force Notes on from a prior session toggle.
          this.formData.includeMsmeTerms = false
        }

        this.formData.useSecondLineItemsTable = this.parseInvoiceFlag(
          data.use_second_line_items_table ?? data.useSecondLineItemsTable,
        )
        this.formData.lineItemTableHeading1 = data.line_item_table_heading_1
          || data.lineItemTableHeading1 || ''
        this.formData.lineItemTableHeading2 = data.line_item_table_heading_2
          || data.lineItemTableHeading2 || ''
        const sameColsRaw = data.second_table_columns_same ?? data.secondTableColumnsSame
        if (sameColsRaw == null) {
          this.formData.secondTableColumnsSame = true
        } else {
          this.formData.secondTableColumnsSame = this.parseInvoiceFlag(sameColsRaw)
        }

        // ── Next-month dates ──
        const nextBilling = this.advanceBillingMonthValue(data.billing_month || data.billingMonth)
        this.ensureBillingMonthOption(nextBilling)
        this.formData.billingMonth = nextBilling
        this.formData.invoiceDate = this.advanceInvoiceDateValue(data.invoice_date || data.invoiceDate)

        this.bindPaymentTermsFromSource(
          data.credit_period != null ? data.credit_period : data.creditPeriod,
          data.payment_terms != null ? data.payment_terms : data.paymentTerms,
        )
        if (this.formData.paymentTerms) {
          this.onPaymentTermsSelected(this.formData.paymentTerms)
        } else if (this.formData.creditPeriod != null && this.formData.invoiceDate) {
          const days = parseFloat(this.formData.creditPeriod) || 0
          this.formData.dueDate = moment(this.formData.invoiceDate)
            .add(days, 'days')
            .format('YYYY-MM-DD')
        }

        // ── Template style + column config ──
        const layoutStyle = data.layout_style
          || (data.config && data.config.layout_style)
          || data.layoutStyle
          || null
        if (layoutStyle) this.previewStyleOverride = layoutStyle === 'minimal' ? 'bajaj' : layoutStyle

        const colConfig = this.parseColumnConfigFromRecord(data)
        const presetId = data.line_item_preset_id
          || data.manual_template_id
          || data.template_id
          || (data.config && (data.config.line_item_preset_id || data.config.manual_template_id))
          || null
        if (presetId != null && presetId !== '') {
          await this.onTemplateSelected(presetId, { quiet: true })
        }
        if (Array.isArray(colConfig) && colConfig.length) {
          this.dynamicColumnConfig = colConfig.map((c) => ({ ...c }))
        }
        const colConfig2 = this.parseColumnConfigTable2FromRecord(data)
        if (Array.isArray(colConfig2) && colConfig2.length) {
          this.dynamicColumnConfigTable2 = colConfig2.map((c) => ({ ...c }))
          if (this.formData.useSecondLineItemsTable) {
            this.formData.secondTableColumnsSame = false
          }
        } else if (this.formData.secondTableColumnsSame !== false) {
          this.dynamicColumnConfigTable2 = null
        }
        if (layoutStyle) {
          this.previewStyleOverride = layoutStyle === 'minimal' ? 'bajaj' : layoutStyle
        }

        // ── Line items + candidates (includes metadata.line_items from GET) ──
        this.lineItems = this.parseLineItemsFromDuplicateRecord(data, candidates)
        if (typeof this.syncSrNoColumns === 'function') this.syncSrNoColumns()
        if (candidates.length) {
          this.isForCandidates = true
          this.selectedCandidates = candidates.map((c) => ({
            cid: c.cid,
            jid: c.jid,
            candidate_name: c.candidate_name || 'N/A',
            position_name: c.position_name || 'N/A',
            revenue: parseFloat(c.base_amount || c.revenue || 0) || 0,
            base_amount: parseFloat(c.base_amount || c.revenue || 0) || 0,
          }))
          this.selectedCandidateIds = this.selectedCandidates
            .filter((c) => c.cid != null && c.jid != null)
            .map((c) => `${c.cid}-${c.jid}`)
        }

        // ── Fresh invoice identity ──
        this.formData.invoiceId = ''
        this.invoiceIdManuallyEdited = false
        this._invoiceNumberVerticalAtGen = null
        await this.refreshInvoiceNumber(true)
        this.syncAutoInvoiceName()

        this.duplicateSourceId = id
        await this.attachDuplicateSupportingDocuments(data)

        // Drop duplicateFrom from the URL so refresh doesn't re-hydrate.
        if (this.$route.query.duplicateFrom) {
          const nextQuery = { ...this.$route.query }
          delete nextQuery.duplicateFrom
          this.$router.replace({ query: nextQuery }).catch(() => {})
        }

        const supportNote = this.duplicateSupportCopied
          ? ' Supporting document copied from the source — replace on Payment if needed.'
          : ' You can Create without a file; attach PDF/PNG/JPG/TXT on Payment if you have one.'
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Duplicated for next month',
            text: `Prefilled from ${id}. Billing month set to ${nextBilling}. Review and submit to create a new invoice.${supportNote}`,
            icon: 'CopyIcon',
            variant: 'success',
          },
        })
      } catch (err) {
        console.error('[InvoiceCreateManual] hydrateFromDuplicateSource failed:', err)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Duplicate failed',
            text: 'Could not load the source invoice. Please try again.',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.duplicateLoading = false
        this.$nextTick(() => {
          this._restoringDraft = false
          this.syncAutoInvoiceName()
        })
      }
    },

    generateBillingMonthOptions() {
      // Current IST month + previous 6. No future months on new create.
      this.billingMonthOptions = buildBillingMonthOptions(this.formData.billingMonth)
      // Default to the current month (not the oldest option). Draft restore
      // / duplicate run after this and will overwrite it.
      if (!this.formData.billingMonth) {
        this.formData.billingMonth = currentBillingMonthValue()
      }
      this.syncAutoInvoiceName()
    },
    /**
     * Invoice display/save name:
     * `{Client} {Invoice|Proforma|Credit Note|Debit Note} {InvoiceId} ({Mon-YYYY})`
     * e.g. `Bajaj Invoice SH1697 (Jun-2026)`
     * Shared with all invoice PDF downloads via invoice-download-filename util.
     */
    buildAutoInvoiceName() {
      return buildAutoInvoiceNameUtil(this.formData)
    },
    syncAutoInvoiceName() {
      const next = this.buildAutoInvoiceName()
      if (this.formData.invoiceName !== next) {
        this.formData.invoiceName = next
      }
    },
    async onClientSelected(clientName) {
      if (!clientName) return
      this.clientEntities = []
      this.clientStateOptions = []
      this.clientLegalNameOptions = []
      this.legalNameIsFallback = false
      this.formData.clientState = null
      this.formData.legalEntityId = null
      this.clientMasterData = null
      this.formData.invoiceClientInfo = emptyInvoiceClientInfo()
      // PO/WO # and Credit Period are tied to client master data — reset so
      // a previous client's values don't linger, then re-derive below.
      // applyClientEntity() refines Credit Period / Payment Terms per
      // legal-entity row once State (or sole Legal Name) is resolved.
      this.formData.poNumber = ''
      this.formData.creditPeriod = null
      this.clientMasterLoading = true
      try {
        const res = await invoiceConfigService.getClientMasterData(clientName)
        if (res && res.status === 'OK') {
          this.clientEntities = normalizeClientMasterDataList(res.data)
          const legalNames = extractClientLegalNames(this.clientEntities)
          // PO number lookup keyed by client name only: take the first
          // entity (across all states/legal-entities returned for this
          // client) that actually has a non-blank `po_number`/`poNumber`
          // (see normalizeClientMasterData()'s alias list) rather than
          // waiting for a state to be selected. If none of the entities
          // carry one, this is a genuine backend-data gap for this client
          // (or the field isn't populated on the backend at all yet) —
          // leave it blank for manual entry, same as today.
          const poEntity = this.clientEntities.find(
            (e) => e.poNumber != null && String(e.poNumber).trim() !== '',
          )
          if (poEntity) this.formData.poNumber = String(poEntity.poNumber).trim()

          // Same early prefill for Credit Period (master `credit_period`) so
          // Payment Terms / Collect-N-Pay aren't left empty while the user
          // is still picking Legal Name / State. applyClientEntity() will
          // overwrite with the selected entity's value when it differs.
          const cpEntity = this.clientEntities.find(
            (e) => e.creditPeriod != null && String(e.creditPeriod).trim() !== '',
          )
          if (cpEntity) this.applyCreditPeriodFromMaster(cpEntity.creditPeriod)

          // Progressive disclosure: do NOT expose State until a Legal Name
          // is chosen. Populate Legal Name options (or fall back to the
          // client name), then auto-select when there's only one choice.
          if (legalNames.length === 0) {
            this.legalNameIsFallback = true
            this.clientLegalNameOptions = [clientName]
            this.formData.invoiceClientInfo.legalName = clientName
            this.formData.invoiceClientInfo.clientName = clientName
            await this.onLegalNameSelected(clientName)
          } else {
            this.clientLegalNameOptions = legalNames
            if (legalNames.length === 1) {
              this.formData.invoiceClientInfo.legalName = legalNames[0]
              await this.onLegalNameSelected(legalNames[0])
            }
            // Multiple legal names → wait for explicit Q2 selection;
            // State stays hidden until then.
          }
        } else {
          // No usable master data — still allow progressive flow with
          // client-name fallback so the user can continue via override.
          this.legalNameIsFallback = true
          this.clientLegalNameOptions = [clientName]
          this.formData.invoiceClientInfo.legalName = clientName
          this.formData.invoiceClientInfo.clientName = clientName
        }
      } catch (error) {
        console.error('Error loading client master data:', error)
        this.legalNameIsFallback = true
        this.clientLegalNameOptions = [clientName]
        this.formData.invoiceClientInfo.legalName = clientName
        this.formData.invoiceClientInfo.clientName = clientName
      } finally {
        this.clientMasterLoading = false
      }
      // Drop any template pinned for a previous client so backend
      // client+category assignment can load fresh.
      this.resetTemplateAutoState()
      await this.loadTemplateForClient()
      // Restore this client's last Edit Columns schema (API or local mirror).
      await this.restoreClientLineItemPreference(clientName)
    },
    async onClientStateSelected(stateName) {
      if (!stateName || !this.formData.client) return
      this.formData.clientState = stateName
      const legalName = this.formData.invoiceClientInfo.legalName
      let entity = findClientEntityByLegalNameAndState(
        this.clientEntities,
        legalName,
        stateName,
      )
      if (!entity) {
        try {
          const res = await invoiceConfigService.getClientMasterData(this.formData.client, stateName)
          if (res && res.status === 'OK') {
            const list = normalizeClientMasterDataList(res.data)
            entity = findClientEntityByLegalNameAndState(list, legalName, stateName)
              || findClientEntityByState(list, stateName)
              || list[0]
            if (list.length) {
              // Merge newly fetched rows into the in-memory entity list so
              // Legal Name / State options stay consistent if the user
              // re-picks without a full client reload.
              const byId = new Map(this.clientEntities.map((e) => [e.id, e]))
              list.forEach((e) => { byId.set(e.id, e) })
              this.clientEntities = Array.from(byId.values())
              if (!this.legalNameIsFallback) {
                this.clientLegalNameOptions = extractClientLegalNames(this.clientEntities)
              }
            }
          }
        } catch (err) {
          console.error('Error loading client master data for state:', err)
        }
      }
      if (entity) {
        this.applyClientEntity(entity)
        // Preserve the user's Legal Name choice when the matched row has a
        // blank legal_name (common in the client-name fallback path).
        if (legalName && !this.formData.invoiceClientInfo.legalName) {
          this.formData.invoiceClientInfo.legalName = legalName
        }
      } else {
        this.clientMasterData = null
        this.formData.invoiceClientInfo = {
          ...emptyInvoiceClientInfo(),
          clientName: this.formData.client,
          legalName: legalName || '',
          clientState: stateName,
        }
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'No master data for this state',
            text: `No GST/address record found for "${this.formData.client}" in ${stateName}. Enter details manually.`,
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        })
      }
    },
    // Fired when the user picks (or we auto-select) a Legal Name. Filters
    // State options to that entity, then auto-selects State when only one
    // applies. GST/address fill happens via onClientStateSelected /
    // applyClientEntity — not guessed here when multiple states exist.
    async onLegalNameSelected(legalName) {
      if (!legalName || !this.formData.client) return
      this.formData.invoiceClientInfo.legalName = legalName
      this.formData.clientState = null
      this.clientMasterData = null

      const matched = filterClientEntitiesByLegalName(this.clientEntities, legalName)
      // Fallback path: entities often have blank legal_name when we used the
      // client name as the option — use the full entity list for states.
      const pool = matched.length ? matched : this.clientEntities
      this.clientStateOptions = extractClientStates(pool)

      if (this.clientStateOptions.length === 1) {
        this.formData.clientState = this.clientStateOptions[0]
        await this.onClientStateSelected(this.clientStateOptions[0])
      } else if (this.clientStateOptions.length === 0) {
        // No state on file — still apply the best legal-name entity (or
        // leave name-only fields) so the preview isn't empty.
        const entity = findClientEntityByLegalName(this.clientEntities, legalName)
        if (entity) {
          this.applyClientEntity(entity)
        } else {
          this.formData.invoiceClientInfo = {
            ...emptyInvoiceClientInfo(),
            clientName: this.formData.client,
            legalName,
          }
        }
      }
      // Multiple states → wait for Q3; GST/address fills on state pick.
    },
    // Fills the invoice's client fields from one normalized client-master-data
    // entity (see normalizeClientMasterDataList()) — used both for the
    // auto-selected first entity and when the user changes the "State"
    // picker in WizardClientStep.vue.
    applyClientEntity(entity) {
      if (!entity) return
      this.formData.legalEntityId = entity.id
      this.clientMasterData = entity.raw
      this.formData.invoiceClientInfo = {
        clientName: entity.clientName || this.formData.client,
        legalName: entity.legalName || '',
        clientGST: entity.gstin || '',
        clientAddress: entity.address || '',
        clientState: entity.state || '',
        placeOfSupply: entity.placeOfSupply || '',
        clientPhone: entity.phone || ''
      }
      // Credit Period lives on client master (`credit_period`) — same as
      // CreateInvoice.vue's loadClientMasterData(). Bind it into the form so
      // Payment Terms (More Details) and Collect-N-Pay Credit Period stay
      // filled instead of re-prompting the user on every invoice.
      if (entity.creditPeriod != null && String(entity.creditPeriod).trim() !== '') {
        this.applyCreditPeriodFromMaster(entity.creditPeriod)
      }
      if (isClientMasterDataIncomplete(entity.raw)) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'GST/address not auto-filled',
            text: `Could not auto-fill GST/address for "${this.formData.client}" (${this.formData.clientState || 'selected state'}) — please enter GSTIN, address and state manually, or pick a different state above.`,
            icon: 'AlertCircleIcon',
            variant: 'warning'
          }
        })
      }
    },
    /**
     * Bind a master-data credit_period into formData.creditPeriod and align
     * Payment Terms (Net N days) + due date. Editable override remains OK —
     * this only runs when master data is applied / client is selected.
     */
    applyCreditPeriodFromMaster(creditPeriod) {
      if (creditPeriod == null || String(creditPeriod).trim() === '') return
      const days = parseInt(creditPeriod, 10)
      if (Number.isNaN(days) || days < 0) return
      this.ensureCreditPeriodOption(days)
      this.formData.creditPeriod = days
      let match = (this.paymentTermsOptions || []).find(
        (t) => parseInt(t.days, 10) === days,
      )
      // Master may carry a period not yet present in /invoice/config options
      // (or paymentTerms may still be empty) — synthesize Net N so More
      // Details shows the filled master value instead of "Due on Receipt".
      if (!match && days > 0) {
        match = { name: `Net ${days} days`, days }
        this.paymentTermsOptions = [...(this.paymentTermsOptions || []), match]
          .sort((a, b) => (parseInt(a.days, 10) || 0) - (parseInt(b.days, 10) || 0))
      }
      if (match) {
        this.formData.paymentTerms = match
        this.onPaymentTermsSelected(match)
      }
    },
    /** Keep the Credit Period <select> able to show a master value even if
     *  it isn't in the global `/invoice/config` creditPeriods list. */
    ensureCreditPeriodOption(days) {
      const n = parseInt(days, 10)
      if (Number.isNaN(n)) return
      const exists = (this.creditPeriodOptions || []).some(
        (o) => parseInt(o.value, 10) === n,
      )
      if (!exists) {
        this.creditPeriodOptions = [
          ...(this.creditPeriodOptions || []),
          { text: `${n} days`, value: n },
        ].sort((a, b) => (parseInt(a.value, 10) || 0) - (parseInt(b.value, 10) || 0))
      }
    },
    async onCreateClient(payload) {
      this.addClientSaving = true
      try {
        const res = await invoiceConfigService.createInvoiceClient({
          clientName: payload.clientName,
          legalName: payload.legalName,
          clientGST: payload.clientGST,
          clientAddress: payload.clientAddress,
          clientState: payload.clientState,
          placeOfSupply: payload.placeOfSupply,
          clientPhone: payload.clientPhone,
        })
        if (res && (res.status === 'OK' || res.status === 'SUCCESS')) {
          const name = payload.clientName
          if (!this.clients.some(c => c.title === name)) {
            this.clients = [...this.clients, { title: name }]
          }
          this.showAddClientModal = false
          this.formData.client = name
          const newEntity = {
            id: (res.data && res.data.id) || Date.now(),
            clientName: name,
            legalName: payload.legalName || '',
            gstin: payload.clientGST || '',
            address: payload.clientAddress || '',
            state: payload.clientState || '',
            placeOfSupply: payload.placeOfSupply || '',
            phone: payload.clientPhone || '',
            raw: res.data || payload,
          }
          this.clientEntities = [newEntity]
          this.legalNameIsFallback = !payload.legalName
          this.clientLegalNameOptions = extractClientLegalNames(this.clientEntities)
          if (!this.clientLegalNameOptions.length) {
            this.legalNameIsFallback = true
            this.clientLegalNameOptions = [name]
          }
          this.formData.invoiceClientInfo.legalName = payload.legalName || name
          this.clientStateOptions = extractClientStates(this.clientEntities)
          this.formData.clientState = payload.clientState
          this.applyClientEntity(newEntity)
          this.resetTemplateAutoState()
          await this.loadTemplateForClient()
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Client added',
              text: res._pendingBackend
                ? `${name} added to dropdown; full master-data sync pending backend POST /invoice/config/clients.`
                : `${name} was created and selected.`,
              icon: 'CheckIcon',
              variant: res._pendingBackend ? 'warning' : 'success',
            },
          })
        } else {
          throw new Error((res && res.message) || 'Create failed')
        }
      } catch (err) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Could not add client',
            text: (err && err.message) || 'Please try again or contact admin.',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.addClientSaving = false
      }
    },
    /**
     * Auto-generates the Invoice # for the currently selected Vertical
     * ("Service Industry" → SH prefix, "Infra Industry" → SHI prefix — see
     * INVOICE_PREFIX_BY_VERTICAL in invoice-config-service.js). Called from
     * the `formData.vertical` watcher whenever the user changes Vertical,
     * and from the wizard step's "Regenerate" button.
     *
     * Uses `getNextInvoiceNumber()` (a standalone named export from
     * invoice-config-service.js, NOT a method on the default-exported
     * `invoiceConfigService` singleton — calling it as
     * `invoiceConfigService.getNextInvoiceNumber(...)` throws "is not a
     * function" since that property doesn't exist on the class instance;
     * same pattern/bug as `getInvoicePrefixForVertical` above, which was
     * already correctly imported/called as a bare named function).
     * Prefers a backend atomic counter when that route is available
     * (`PROBE_NEXT_INVOICE_NUMBER_API` in invoice-config-service.js).
     * Live backend currently 500s that route, so the client skips the
     * probe and uses max(existing)+1 from `GET invoice`, then warns
     * once (toast + More Details hint) instead of a silent 500.
     * If the vertical isn't mapped to a known prefix, the field is
     * left as-is (still editable) rather than guessing.
     */
    async refreshInvoiceNumber(force = false) {
      const prefix = getInvoicePrefixForVertical(this.formData.vertical)
      if (!prefix) return
      if (force) {
        // Explicit "Regenerate" click always wins, even over a value the
        // user previously typed by hand.
        this.invoiceIdManuallyEdited = false
      } else if (this.invoiceIdManuallyEdited) {
        // The user has typed their own Invoice # — don't clobber it just
        // because the Vertical changed. They can clear the field (or hit
        // Regenerate) to hand control back to auto-fill.
        return
      }
      // Avoid refetching every time an unrelated watcher fires for the same
      // vertical — only (re)generate when the vertical actually changed
      // since the last generation, or when explicitly forced (Regenerate).
      if (!force && this.formData.invoiceId && this._invoiceNumberVerticalAtGen === this.formData.vertical) {
        return
      }
      this.invoiceNumberLoading = true
      try {
        const res = await getNextInvoiceNumber(prefix)
        if (res && res.status === 'OK' && res.data && res.data.nextNumber) {
          this.formData.invoiceId = res.data.nextNumber
          this._invoiceNumberVerticalAtGen = this.formData.vertical
          this.invoiceNumberSource = res._source || null
          if (this.error) this.error.invoiceId = ''
          if (this.invoiceNumberIsFallback && !this._invoiceNumberFallbackNotified) {
            this._invoiceNumberFallbackNotified = true
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Invoice # generated locally',
                text: 'Server invoice numbering is unavailable. This number was computed from existing invoices — please verify it before submitting. Concurrent creates can collide.',
                icon: 'AlertCircleIcon',
                variant: 'warning',
              },
            })
          }
        } else {
          this.invoiceNumberSource = null
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Could not auto-generate Invoice #',
              text: (res && res.message) || 'Please try Regenerate, or enter manually.',
              icon: 'AlertCircleIcon',
              variant: 'warning',
            },
          })
        }
      } catch (err) {
        this.invoiceNumberSource = null
        console.error('[InvoiceCreateManual] refreshInvoiceNumber failed:', err)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Could not auto-generate Invoice #',
            text: 'Please enter an Invoice # manually, or try Regenerate.',
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        })
      } finally {
        this.invoiceNumberLoading = false
      }
    },
    /** Normalize /locations (and sattva options) so vue-select always has title + id. */
    normalizeLocationOptions(list) {
      if (!Array.isArray(list)) return []
      return list.map((loc) => {
        if (loc == null) return null
        if (typeof loc === 'string') {
          const title = loc.trim()
          return title ? { title, id: null } : null
        }
        if (typeof loc !== 'object') return null
        const title = (
          loc.title || loc.name || loc.label || loc.location
          || loc.location_name || loc.locationName || loc.city || ''
        )
        const titleStr = title != null ? String(title).trim() : ''
        if (!titleStr) return null
        const rawId = loc.id ?? loc.location_id ?? loc.locationId ?? loc.ID ?? null
        let id = rawId
        if (id != null && id !== '') {
          const asNum = Number(id)
          // Prefer numeric DB ids; keep other non-empty ids as-is for matching.
          if (Number.isFinite(asNum) && String(asNum) === String(id).trim()) id = asNum
          else if (!Number.isFinite(asNum)) id = null // never keep "Pune" as id
        } else {
          id = null
        }
        return { ...loc, title: titleStr, id }
      }).filter(Boolean)
    },
    /**
     * When sattva list overrides the dropdown but rows lack locationId,
     * copy numeric ids from /locations master by case-insensitive name.
     */
    enrichLocationIdsFromMaster(options) {
      if (!Array.isArray(options) || !options.length) return options || []
      return options.map((opt) => {
        if (!opt) return opt
        if (this.coerceNumericLocationId(opt.id) != null) return opt
        const master = this.findLocationRecordInList(opt.title, this.masterLocations)
        const mid = master
          ? this.coerceNumericLocationId(
            master.id ?? master.location_id ?? master.locationId ?? null,
          )
          : null
        return mid != null ? { ...opt, id: mid } : opt
      })
    },
    /** Reload /locations if master list is still empty (late / race). */
    async ensureLocationsReady() {
      if ((this.masterLocations && this.masterLocations.length)
        || (this.locations && this.locations.length)) {
        return
      }
      try {
        const locationsRes = await locationService.getAll()
        if (locationsRes && locationsRes.data) {
          const raw = Array.isArray(locationsRes.data)
            ? locationsRes.data
            : (Array.isArray(locationsRes.data.data) ? locationsRes.data.data : [])
          this.masterLocations = this.normalizeLocationOptions(raw)
          if (!this.locations || !this.locations.length) {
            this.locations = this.masterLocations.slice()
          } else {
            this.locations = this.enrichLocationIdsFromMaster(this.locations)
          }
        }
      } catch (e) { /* soft — resolve may still work by name later */ }
    },
    locationNameCandidates(loc) {
      if (!loc) return []
      if (typeof loc === 'string') return [loc]
      return [
        loc.title, loc.name, loc.label, loc.location,
        loc.location_name, loc.locationName, loc.city,
      ].filter((v) => v != null && String(v).trim() !== '')
    },
    /** Aliases so "Pune" matches "Pune HQ" / "Pune Office" etc. */
    locationNameAliases(name) {
      const needle = String(name || '').trim().toLowerCase()
      if (!needle) return []
      const ALIASES = {
        pune: ['pune', 'pune hq', 'pune office', 'hadapsar', 'amanora'],
        mumbai: ['mumbai', 'bombay', 'mumbai hq'],
        bangalore: ['bangalore', 'bengaluru', 'blr'],
        bengaluru: ['bangalore', 'bengaluru', 'blr'],
        delhi: ['delhi', 'new delhi', 'ncr'],
        hyderabad: ['hyderabad', 'hyd'],
        chennai: ['chennai', 'madras'],
        gurgaon: ['gurgaon', 'gurugram'],
        gurugram: ['gurgaon', 'gurugram'],
      }
      const hit = ALIASES[needle]
      if (hit) return hit
      // Also treat first word as alias key (e.g. "Pune HQ" → pune).
      const first = needle.split(/[\s,/|-]+/)[0]
      return ALIASES[first] || [needle, first].filter(Boolean)
    },
    namesMatchLocation(candidate, needle) {
      const c = String(candidate || '').trim().toLowerCase()
      const n = String(needle || '').trim().toLowerCase()
      if (!c || !n) return false
      if (c === n) return true
      const aliases = this.locationNameAliases(n)
      if (aliases.includes(c)) return true
      if (aliases.some((a) => c === a || c.startsWith(`${a} `) || c.endsWith(` ${a}`))) return true
      // Loose contains for short city names (min 3 chars to avoid noise).
      if (n.length >= 3 && (c.includes(n) || n.includes(c))) return true
      return false
    },
    findLocationRecordInList(location, list) {
      const needle = String(location || '').trim().toLowerCase()
      if (!needle || !Array.isArray(list) || !list.length) return null
      // Exact / alias pass first.
      const exact = list.find((loc) => (
        this.locationNameCandidates(loc).some((c) => this.namesMatchLocation(c, needle))
      ))
      if (exact) return exact
      return null
    },
    findLocationRecordByName(location) {
      return this.findLocationRecordInList(location, this.locations)
        || this.findLocationRecordInList(location, this.masterLocations)
        || null
    },
    /** Only send numeric locationId — never a city name (avoids historical 400s). */
    coerceNumericLocationId(value) {
      if (value == null || value === '') return null
      if (typeof value === 'number' && Number.isFinite(value)) return value
      const trimmed = String(value).trim()
      if (!trimmed) return null
      const asNum = Number(trimmed)
      if (Number.isFinite(asNum) && String(asNum) === trimmed) return asNum
      return null
    },
    applySattvaCompanyDetailsRow(data) {
      if (!data || typeof data !== 'object') return
      this.sattvaMasterData = data
      // Prefer a numeric id from the master row when /locations id was missing.
      const rowId = this.coerceNumericLocationId(
        data.locationId ?? data.location_id ?? data.id ?? null,
      )
      if (rowId != null) this.resolvedSattvaLocationId = rowId
      else if (data.locationName || data.location_name || data.location) {
        const byName = this.resolveNumericLocationIdForName(
          data.locationName || data.location_name || data.location,
        )
        if (byName != null) this.resolvedSattvaLocationId = byName
      }
      this.formData.invoiceSattvaInfo = {
        companyName: data.companyName || '',
        gst: data.gstin || data.gst || '',
        pan: data.pan || '',
        address: data.registeredAddress || data.address || '',
        bankName: data.bankName || '',
        accountName: data.accountName || '',
        accountNumber: data.accountNumber || '',
        accountType: data.accountType || '',
        ifscCode: data.ifscCode || '',
        logoUrl: data.logoUrl || '',
        lutNo: data.lutNo || '',
        sacCode: null,
      }
      if (data.lutNo && !this.lutNoOptions.some((o) => o.title === data.lutNo)) {
        this.lutNoOptions.push({ title: data.lutNo, value: data.lutNo })
      }
      this.sattvaDetailsUnavailable = false
    },
    /**
     * Resolve numeric locationId for Pune/etc from /locations list.
     * Never returns a city name string (avoids locationId=Pune 400s).
     * Prefer masterLocations (/locations) — sattva dropdown rows may lack ids.
     */
    resolveNumericLocationIdForName(location) {
      const idFrom = (row) => {
        if (!row) return null
        return this.coerceNumericLocationId(
          row.id
            ?? row.location_id
            ?? row.locationId
            ?? row.ID
            ?? row.value
            ?? null,
        )
      }
      // /locations master first — most reliable numeric ids.
      const fromMaster = idFrom(this.findLocationRecordInList(location, this.masterLocations))
      if (fromMaster != null) return fromMaster
      // Then current dropdown list (may already be enriched).
      const fromLocations = idFrom(this.findLocationRecordInList(location, this.locations))
      if (fromLocations != null) return fromLocations
      // Last resort: any name match (may still be null id).
      return idFrom(this.findLocationRecordByName(location))
    },
    /** Numeric id for POST /invoice when /locations or sattva master provides one. */
    resolveSubmitLocationId() {
      const locationName = String(this.formData.location || '').trim()
      if (!locationName) return null
      return this.resolvedSattvaLocationId
        ?? this.resolveNumericLocationIdForName(locationName)
    },
    async resolveSattvaDetailsByLocationName(location) {
      const needle = String(location || '').trim().toLowerCase()
      if (!needle) return null
      try {
        // List-all mode (no locationId) — match by locationName when id is missing.
        const res = await invoiceConfigService.getSattvaCompanyDetails()
        if (!res || res.status !== 'OK' || res.data == null) return null
        const rows = Array.isArray(res.data) ? res.data : [res.data]
        const match = rows.find((row) => {
          if (!row) return false
          const names = [
            row.locationName, row.location_name, row.location, row.title, row.name, row.city,
          ]
          return names.some((n) => n != null && this.namesMatchLocation(n, needle))
        })
        return match || null
      } catch (e) {
        return null
      }
    },
    buildSattvaCompanyDetailsSavePayload() {
      const s = this.formData.invoiceSattvaInfo || {}
      const locationName = String(this.formData.location || '').trim()
      let locationId = this.resolvedSattvaLocationId
      if (locationId == null) {
        locationId = this.resolveNumericLocationIdForName(locationName)
      }
      // Keep resolved id on the instance so later submit/save reuse it.
      if (locationId != null && this.resolvedSattvaLocationId == null) {
        this.resolvedSattvaLocationId = locationId
      }
      const gstin = String(s.gst || '').trim().toUpperCase().replace(/\s+/g, '')
      const stateCode = (/^(\d{2})/.exec(gstin) || [])[1] || ''
      const STATE_NAMES = {
        '27': 'Maharashtra', '29': 'Karnataka', '07': 'Delhi', '09': 'Uttar Pradesh',
        '33': 'Tamil Nadu', '24': 'Gujarat', '06': 'Haryana', '03': 'Punjab',
        '08': 'Rajasthan', '36': 'Telangana', '37': 'Andhra Pradesh', '32': 'Kerala',
        '19': 'West Bengal',
      }
      const panFromGst = gstin.length >= 12 ? gstin.slice(2, 12) : ''
      return {
        locationId,
        // Backend validates `location` (required); keep locationName + aliases too.
        location: locationName,
        locationName,
        location_name: locationName,
        companyName: s.companyName || '',
        gstin,
        pan: String(s.pan || panFromGst || '').trim().toUpperCase(),
        stateCode,
        stateName: STATE_NAMES[stateCode] || '',
        registeredAddress: s.address || '',
        lutNo: s.lutNo || '',
        bankName: s.bankName || '',
        accountName: s.accountName || '',
        accountNumber: s.accountNumber || '',
        ifscCode: s.ifscCode || '',
        accountType: s.accountType || '',
        logoUrl: s.logoUrl || '',
        isActive: true,
      }
    },
    /**
     * Persist Bill From edits to invoice_sattva_company_details (master).
     * Soft-fails on 404/400 — never blocks invoice create; invoice-only data still applies.
     */
    async onSaveSattvaCompanyDetails({ proceedNext = false } = {}) {
      if (this.sattvaSaveLoading) {
        if (proceedNext) this.goToLineItems()
        return
      }
      await this.ensureLocationsReady()
      // Re-resolve id after locations may have loaded late.
      if (this.resolvedSattvaLocationId == null && this.formData.location) {
        this.resolvedSattvaLocationId = this.resolveNumericLocationIdForName(this.formData.location)
      }
      const payload = this.buildSattvaCompanyDetailsSavePayload()
      if (!payload.locationName || (!payload.companyName && !payload.gstin)) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Cannot save company details',
            text: 'Enter at least company name or GST for this location.',
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        })
        if (proceedNext) this.goToLineItems()
        return
      }

      const payloadKey = JSON.stringify({
        location: payload.location,
        locationId: payload.locationId,
        locationName: payload.locationName,
        companyName: payload.companyName,
        gstin: payload.gstin,
        pan: payload.pan,
        registeredAddress: payload.registeredAddress,
      })
      // Skip a second identical PUT (Save then Next / double click) — still proceed.
      if (this._sattvaSaveLastPayloadKey === payloadKey) {
        if (proceedNext) this.goToLineItems()
        return
      }

      this.sattvaSaveLoading = true
      this._sattvaSaveLastFailed = false
      try {
        const res = await invoiceConfigService.saveSattvaCompanyDetails(payload)
        if (res && res.status === 'OK') {
          this._sattvaSaveLastPayloadKey = payloadKey
          this._sattvaSaveLastFailed = false
          if (res.data) this.applySattvaCompanyDetailsRow(res.data)
          else this.sattvaDetailsUnavailable = false
          // Reload so next open shows Auto-filled from master.
          try {
            const locationId = payload.locationId != null
              ? payload.locationId
              : this.resolvedSattvaLocationId
            let reload = null
            if (locationId != null) {
              reload = await invoiceConfigService.getSattvaCompanyDetails(locationId)
            }
            if ((!reload || reload.status !== 'OK' || !reload.data) && payload.locationName) {
              const byName = await this.resolveSattvaDetailsByLocationName(payload.locationName)
              if (byName) this.applySattvaCompanyDetailsRow(byName)
            } else if (reload && reload.status === 'OK' && reload.data) {
              const row = Array.isArray(reload.data) ? reload.data[0] : reload.data
              this.applySattvaCompanyDetailsRow(row)
            }
          } catch (e) { /* keep local form values */ }
          if (!res._deduped) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Company details saved',
                text: `Saved for ${payload.locationName} — will auto-fill on future invoices.`,
                icon: 'CheckIcon',
                variant: 'success',
              },
            })
          }
        } else if (res && res.status === 'SERVICE_UNAVAILABLE') {
          this._sattvaSaveLastPayloadKey = payloadKey
          this._sattvaSaveLastFailed = true
          if (!res._deduped) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Master save not available yet',
                text: res.message
                  || 'Backend must implement upsert for invoice_sattva_company_details. Continuing with this invoice only.',
                icon: 'AlertCircleIcon',
                variant: 'warning',
              },
            })
          }
        } else {
          this._sattvaSaveLastPayloadKey = payloadKey
          this._sattvaSaveLastFailed = true
          if (!res || !res._deduped) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Could not save company details',
                text: (res && res.message) || 'Save failed — invoice will still use the details you entered.',
                icon: 'AlertCircleIcon',
                variant: 'warning',
              },
            })
          }
        }
      } catch (err) {
        this._sattvaSaveLastPayloadKey = payloadKey
        this._sattvaSaveLastFailed = true
        const msg = (err && err.response && err.response.data && err.response.data.message)
          || (err && err.message)
          || 'Save failed — invoice will still use the details you entered.'
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Could not save company details',
            text: msg,
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        })
      } finally {
        this.sattvaSaveLoading = false
        if (proceedNext) this.goToLineItems()
      }
    },
    async onLocationSelected(location) {
      // Clear any previous location's data first — otherwise switching
      // locations while the new lookup is unavailable would leave the OLD
      // location's Sattva info incorrectly displayed.
      this.formData.invoiceSattvaInfo = {
        companyName: '', gst: '', pan: '', address: '',
        bankName: '', accountName: '', accountNumber: '', accountType: '',
        ifscCode: '', logoUrl: '',
        lutNo: '', sacCode: null,
      }
      this.sattvaMasterData = null
      this.sattvaDetailsUnavailable = false
      this.resolvedSattvaLocationId = null
      this._sattvaSaveLastPayloadKey = null
      this._sattvaSaveLastFailed = false
      if (!location) return

      // `location` is the dropdown display title (`:reduce="o => o.title"`).
      // Lookups need a numeric locationId — never send the city name as id
      // (historical 400: locationId=Pune). If id is missing, try list-all
      // sattva-company-details matched by name before giving up.
      await this.ensureLocationsReady()
      const resolvedLocationId = this.resolveNumericLocationIdForName(location)
      this.resolvedSattvaLocationId = resolvedLocationId

      this.sattvaDetailsLoading = true
      try {
        let res = null
        if (resolvedLocationId != null) {
          res = await invoiceConfigService.getSattvaCompanyDetails(resolvedLocationId)
        } else {
          const byName = await this.resolveSattvaDetailsByLocationName(location)
          if (byName) {
            this.applySattvaCompanyDetailsRow(byName)
            // Row may carry locationId even when /locations match failed.
            if (this.resolvedSattvaLocationId == null) {
              this.resolvedSattvaLocationId = this.coerceNumericLocationId(
                byName.locationId ?? byName.location_id ?? byName.id ?? null,
              )
            }
            return
          }
          this.sattvaDetailsUnavailable = true
          return
        }

        if (res && res.status === 'OK' && res.data) {
          const row = Array.isArray(res.data) ? res.data[0] : res.data
          this.applySattvaCompanyDetailsRow(row)
        } else {
          // Id lookup failed/empty — last chance: match by display name.
          const byName = await this.resolveSattvaDetailsByLocationName(location)
          if (byName) {
            this.applySattvaCompanyDetailsRow(byName)
          } else {
            this.sattvaDetailsUnavailable = true
          }
        }
      } catch (error) {
        // Expected when dedicated + fallback configs are empty — no console spam.
        this.sattvaDetailsUnavailable = true
      } finally {
        this.sattvaDetailsLoading = false
      }
    },
    // See refreshInvoiceNumber() — a non-empty typed value marks the field
    // as manually-controlled (auto-fill backs off); clearing it back to
    // empty hands control back to auto-fill on the next Vertical change.
    onInvoiceIdEdited(val) {
      this.invoiceIdManuallyEdited = !!(val && String(val).trim())
    },
    // Shared by onCategorySelected() and the formData.vertical watcher below
    // (2026-07-22) — isCandidateCategory requires Category === 'Recruitment'
    // AND Vertical === 'Service Industry' together, so either one changing
    // away from that exact combination needs to clear out any
    // candidate-derived state, not just a Category change.
    resetCandidateSelectionIfNotEligible() {
      if (this.isCandidateCategory) return
      this.isForCandidates = null
      this.selectedCandidates = []
      this.selectedCandidateIds = []
      this.convertCandidatesToLineItems()
    },
    onCategorySelected() {
      this.resetCandidateSelectionIfNotEligible()
      // Re-load template when category changes (may be category-specific template).
      // Only when Line-items picker is on Auto — explicit preset pins skip reload.
      if (this.manualTemplateId == null) {
        this.loadTemplateForClient()
      }
      // BOUNCE-BACK (2026-07-21 reorder, mostly superseded 2026-07-22): this
      // handler now fires from TWO places — the Category quick-pick that
      // was added directly to the Line Items step (see the "Category
      // quick-pick" comment in WizardLineItemsStep.vue) so a user answers
      // this right where its consequences (RPO-vs-generic columns, whether
      // "is this for candidates?" even applies) actually show up, AND the
      // original "Category *" field on More Details, since Category is
      // still also editable there for consistency with Vertical/Billing
      // Month, and this is the exact same shared formData.category field
      // either way. The bounce-back below is now only a safety net for that
      // second path — e.g. a user changes Category back on More Details
      // (currentStep === 4) in a way that leaves Line Items' candidate
      // question unanswered or its lines invalid for the new category —
      // rather than leaving Submit silently disabled with no visible reason
      // on this later step.
      if (this.currentStep === 4) {
        this.$nextTick(() => {
          if (!this.canProceedPastLineItems) {
            this.currentStep = 3
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Line Items need a look',
                text: 'This category changes the line-item format and/or needs a "candidates?" answer — please review the Line Items step before continuing.',
                icon: 'AlertCircleIcon',
                variant: 'warning',
              },
            })
          }
        })
      }
    },
    /** Normalize toolbar/modal select values to null | id. */
    normalizeLineItemPresetId(val) {
      if (val === null || val === undefined || val === '' || val === 'null') return null
      return val
    },
    onToolbarLineItemPresetChange(val) {
      // Toolbar dropdown only — apply columns + toast. Never open the
      // Line Items Configuration modal (gear / Columns button owns that).
      this.onTemplateSelected(this.normalizeLineItemPresetId(val))
    },
    /**
     * User-driven line-items preset from the Live Preview toolbar (was on
     * WizardLineItemsStep until 2026-07-23). Picking "Auto (recommended)"
     * (`templateId === null`) clears the override and re-runs client/category
     * best-match; picking a specific preset pins it until cleared.
     *
     * Line items never change Style / visual template — only columns.
     * (Style → Line items still runs via syncLineItemsFromStyle.)
     * Does NOT open LineItemsConfigModal — only openLineItemsConfig()
     * (gear / Columns) should.
     */
    async onTemplateSelected(templateId, { quiet = false } = {}) {
      const id = this.normalizeLineItemPresetId(templateId)
      // Same preset already active / in-flight — skip work + toast.
      // Covers: BV FormSelect re-emitting change when :value updates from
      // Style→Line-items sync, and sibling toolbar selects (desktop/mobile/
      // preview modal) seeing the same prop update.
      if (id != null && id === this.manualTemplateId) {
        if (this.activeTemplate && this.activeTemplate.id === id) {
          if (!quiet && this.showColumnConfigModal) this.columnConfigModalTab = 0
          return
        }
        if (this.templateLoading) return
      }

      await this.confirmResetDivergedTable2()
      this.stashOutgoingPresetColumns(id)

      // Pin the Style currently shown so swapping a column preset (whose
      // config may carry layout.style, e.g. Reliance) cannot change the
      // Live Preview Style dropdown or skin. Style→Line-items already set
      // previewStyleOverride; this covers the null-override case too.
      const styleToKeep = this.previewStyleOverride
        || (this.activeTemplate && this.activeTemplate.layout && this.activeTemplate.layout.style)
        || 'bajaj'

      this.manualTemplateId = id
      if (id == null) {
        // "Auto (recommended)" → API/heuristic match only. Explicit toolbar
        // clear also drops the backend preference for this client.
        if (!quiet && this.formData.client) {
          invoiceConfigService.clearClientTemplatePreference(this.formData.client).catch(() => {})
        }
        await this.loadTemplateForClient()
        if (!this.previewStyleOverride) this.previewStyleOverride = styleToKeep
        this.afterLineItemPresetApplied({ quiet })
        return
      }
      // Built-in Line Items column presets — applied from constants
      // (not saved records), so skip getTemplateById().
      const builtInTemplates = this.builtInLineItemTemplates()
      if (builtInTemplates[id]) {
        const builtIn = builtInTemplates[id]
        this.activeTemplate = { ...builtIn }
        this.formData.template = builtIn.id
        this.loadedTemplateRecord = null
        this.nullColumnConfigsForPreset()
        Object.assign(this.formData, applyTemplateDefaults(this.formData, builtIn))
        this.previewStyleOverride = styleToKeep
        this.afterLineItemPresetApplied({
          quiet,
          toastName: builtIn.name,
        })
        return
      }
      this.templateLoading = true
      try {
        const res = await invoiceConfigService.getTemplateById(id)
        if (res && res.status === 'OK' && res.data && res.data.config) {
          this.activeTemplate = res.data.config
          this.formData.template = res.data.id
          this.loadedTemplateRecord = res.data
          this.nullColumnConfigsForPreset()
          Object.assign(this.formData, applyTemplateDefaults(this.formData, res.data.config))
          this.previewStyleOverride = styleToKeep
          this.afterLineItemPresetApplied({
            quiet,
            toastName: res.data.name,
          })
        }
      } catch (e) {
        console.error('Error loading selected template:', e)
      } finally {
        this.templateLoading = false
      }
    },
    /**
     * Shared post-apply for preset switches: one toast max, re-init Edit
     * Columns when the config modal is open, remap/dedupe line-item rows.
     * Never opens the config modal — gear / Columns click only.
     */
    /**
     * Snapshot the outgoing preset's Manage Columns so switching Standard →
     * Reliance → Standard restores custom types/formulas instead of wiping.
     */
    stashOutgoingPresetColumns(incomingId) {
      if (this._restoringDraft) {
        this._pendingPresetColumnRestore = null
        return
      }
      const fromId = this.manualTemplateId
        ?? (this.activeTemplate && this.activeTemplate.id)
        ?? null
      const { store, restored } = stashAndTakePresetColumns(
        this.columnConfigByPreset,
        fromId,
        incomingId,
        this.dynamicColumnConfig,
      )
      this.columnConfigByPreset = store
      this._pendingPresetColumnRestore = restored
    },

    takePendingPresetColumnRestore() {
      const restored = this._pendingPresetColumnRestore
      this._pendingPresetColumnRestore = null
      return (Array.isArray(restored) && restored.length) ? restored : null
    },

    afterLineItemPresetApplied({
      quiet = false,
      toastName = '',
      preserveColumnConfig = false,
    } = {}) {
      observeLineItemColumnTypes(
        (this.activeTemplate && this.activeTemplate.lineItemColumns) || [],
      )
      // Fresh column editor state from the new template (deduped keys).
      // Required when the modal stays open across a preset change — @show
      // does not re-fire, so Edit Columns used to stay empty/stale.
      // preserveColumnConfig: keep a just-restored client/template column
      // schema (applyClientTemplateFromBackend / preference load) instead of
      // wiping it — that wipe was the "config lost for same client" bug.
      const stashed = this.takePendingPresetColumnRestore()
      if (preserveColumnConfig && this.dynamicColumnConfig && this.dynamicColumnConfig.length) {
        if (this.showColumnConfigModal) this.columnConfigModalTab = 0
      } else if (stashed) {
        this.dynamicColumnConfig = stashed
        this.columnConfigEpoch += 1
        if (this.showColumnConfigModal) this.columnConfigModalTab = 0
      } else if (this.showColumnConfigModal) {
        this.resetColumnConfigFromTemplate()
        this.columnConfigModalTab = 0
      } else {
        this.nullColumnConfigsForPreset()
      }
      this.reconcileLineItemsAfterPresetChange()
      if (!quiet && toastName) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Line items changed',
            text: `Now using "${toastName}" columns for this invoice.`,
            icon: 'LayoutIcon',
            variant: 'info',
          },
        })
      }
      // Soft default for next invoice — save whenever we have a client and
      // either a pinned preset or a customized column schema.
      if (!quiet) {
        this.rememberClientTemplatePreference()
      }
    },
    /**
     * Persist Style + Line-items preset + Edit Columns schema for this client
     * (API + localStorage fallback) so the next Create Invoice restores them.
     */
    rememberClientTemplatePreference() {
      const client = this.formData && this.formData.client
      if (!client) return
      const lineItemColumnConfig = this.dynamicColumnConfig
        ? persistableColumnConfig(this.dynamicColumnConfig)
        : null
      const lineItemPresetId = this.manualTemplateId
        ?? (this.loadedTemplateRecord && this.loadedTemplateRecord.id)
        ?? (this.activeTemplate && this.activeTemplate.id)
        ?? null
      if (lineItemPresetId != null && lineItemPresetId !== '' && lineItemColumnConfig && lineItemColumnConfig.length) {
        this.columnConfigByPreset = persistablePresetColumnStore({
          ...this.columnConfigByPreset,
          [lineItemPresetId]: lineItemColumnConfig,
        })
      }
      // Still persist when Auto (no pinned preset) if the user customized columns.
      if ((lineItemPresetId == null || lineItemPresetId === '') && !lineItemColumnConfig) return
      const layoutStyle = this.previewStyleOverride
        || (this.activeTemplate && this.activeTemplate.layout && this.activeTemplate.layout.style)
        || null
      invoiceConfigService.persistClientInvoicePreferences(client, {
        templateId: lineItemPresetId,
        lineItemPresetId,
        lineItemColumnConfig,
        lineItemColumnConfigByPreset: persistablePresetColumnStore(this.columnConfigByPreset),
        dbTemplateId: (this.loadedTemplateRecord && this.loadedTemplateRecord.id) || null,
        layoutStyle,
        category: this.formData.category || null,
      }).catch(() => {})
    },
    /**
     * Restore saved line-item column config (and optional preset) for a client
     * after template auto-load. Preference wins over a bare template default.
     */
    async restoreClientLineItemPreference(clientId) {
      if (!clientId) return false
      try {
        const res = await invoiceConfigService.getClientTemplatePreference(clientId)
        const pref = res && res.status === 'OK' ? res.data : null
        if (!pref) return false
        const rawCol = pref.line_item_column_config ?? pref.lineItemColumnConfig ?? null
        const colConfig = Array.isArray(rawCol) && rawCol.length
          ? persistableColumnConfig(rawCol)
          : null
        const rawByPreset = pref.line_item_column_config_by_preset
          ?? pref.lineItemColumnConfigByPreset
          ?? null
        if (rawByPreset && typeof rawByPreset === 'object') {
          this.columnConfigByPreset = persistablePresetColumnStore(rawByPreset)
        }
        const rawPreset = pref.line_item_preset_id ?? pref.lineItemPresetId ?? pref.templateId ?? null
        const presetId = (rawPreset != null && rawPreset !== '')
          ? this.normalizeLineItemPresetId(rawPreset)
          : null
        const layoutStyle = pref.layout_style ?? pref.layoutStyle ?? null
        if (layoutStyle) this.previewStyleOverride = layoutStyle

        if (presetId != null && this.manualTemplateId == null) {
          await this.applyLineItemPresetOverride(presetId, {
            quiet: true,
            preserveLoadedRecord: !!this.loadedTemplateRecord,
          })
        }
        if (colConfig) {
          this.dynamicColumnConfig = colConfig
          this.reconcileLineItemsAfterPresetChange()
          return true
        }
        return false
      } catch (e) {
        console.warn('[InvoiceCreateManual] restoreClientLineItemPreference failed:', e)
        return false
      }
    },
    /**
     * When the user picks a Style that shares an id with a built-in line-item
     * preset (e.g. Style "Reliance" → columns "Reliance"), auto-apply that
     * preset quietly (no toast). Manual Line-items override remains available
     * in the toolbar. Styles without a matching preset id (modern/corporate/…)
     * leave columns alone. One-way only — Line items never updates Style.
     * Persists preference once Style + Line items are both set.
     */
    async syncLineItemsFromStyle(style) {
      if (!style) return
      const match = LINE_ITEM_PRESETS.find((p) => p.id === style)
      if (match && this.manualTemplateId !== match.id) {
        await this.onTemplateSelected(match.id, { quiet: true })
      }
      // Style change with a pinned Line-items preset → refresh saved default
      // (quiet preset apply does not save inside afterLineItemPresetApplied).
      if (this.manualTemplateId != null) {
        this.rememberClientTemplatePreference()
      }
    },
    /**
     * Clear auto-detected template state when the client changes so the next
     * loadTemplateForClient() call is not blocked by a prior client's pin.
     */
    resetTemplateAutoState() {
      this.manualTemplateId = null
      this.previewStyleOverride = null
      this.loadedTemplateRecord = null
      this.dynamicColumnConfig = null
      this.dynamicColumnConfigTable2 = null
      this.columnConfigByPreset = {}
      this._pendingPresetColumnRestore = null
      this.lineItems = []
    },
    /**
     * Read line-item preset / column override / default rows from a backend
     * template record (top-level or nested under config).
     */
    extractTemplateLineItemMeta(record) {
      if (!record) {
        return { presetId: null, columnConfig: null, defaultLineItems: null }
      }
      const cfg = record.config || {}
      const rawCol = record.line_item_column_config
        ?? record.lineItemColumnConfig
        ?? cfg.line_item_column_config
        ?? cfg.lineItemColumnConfig
        ?? null
      const columnConfig = Array.isArray(rawCol) && rawCol.length ? rawCol : null
      const rawPreset = record.line_item_preset_id
        ?? record.lineItemPresetId
        ?? cfg.line_item_preset_id
        ?? cfg.lineItemPresetId
        ?? null
      const presetId = (rawPreset != null && rawPreset !== '')
        ? this.normalizeLineItemPresetId(rawPreset)
        : null
      const defaultLineItems = record.default_line_items
        ?? record.defaultLineItems
        ?? cfg.default_line_items
        ?? cfg.defaultLineItems
        ?? cfg.lineItems
        ?? null
      return { presetId, columnConfig, defaultLineItems }
    },
    builtInLineItemTemplates() {
      return {
        [DEFAULT_TEMPLATE.id]: DEFAULT_TEMPLATE,
        [RPO_TEMPLATE.id]: RPO_TEMPLATE,
        [HEADCOUNT_TEMPLATE.id]: HEADCOUNT_TEMPLATE,
        [CONTRACT_STAFFING_TEMPLATE.id]: CONTRACT_STAFFING_TEMPLATE,
        [BAJAJ_TEMPLATE.id]: BAJAJ_TEMPLATE,
        [RELIANCE_TEMPLATE.id]: RELIANCE_TEMPLATE,
      }
    },
    /**
     * Apply a line-item preset's columns without discarding the loaded DB
     * template record (layout/tax/calc rules stay from client assignment).
     */
    async applyLineItemPresetOverride(presetId, { quiet = false, preserveLoadedRecord = false } = {}) {
      const id = this.normalizeLineItemPresetId(presetId)
      if (id == null) return false
      await this.confirmResetDivergedTable2()
      this.stashOutgoingPresetColumns(id)
      const loadedId = this.loadedTemplateRecord && this.loadedTemplateRecord.id
      if (loadedId != null && String(loadedId) === String(id)) return false
      if (!preserveLoadedRecord && this.manualTemplateId === id && this.activeTemplate && this.activeTemplate.id === id) {
        return false
      }

      const styleToKeep = this.previewStyleOverride
        || (this.activeTemplate && this.activeTemplate.layout && this.activeTemplate.layout.style)
        || 'bajaj'
      const savedRecord = preserveLoadedRecord ? this.loadedTemplateRecord : null
      const savedTemplateId = preserveLoadedRecord ? this.formData.template : null
      const baseConfig = preserveLoadedRecord && this.activeTemplate
        ? { ...this.activeTemplate }
        : null

      const builtInTemplates = this.builtInLineItemTemplates()
      if (builtInTemplates[id]) {
        const builtIn = builtInTemplates[id]
        if (preserveLoadedRecord && baseConfig) {
          this.activeTemplate = {
            ...baseConfig,
            lineItemColumns: builtIn.lineItemColumns,
          }
          this.manualTemplateId = id
        } else {
          this.activeTemplate = { ...builtIn }
          this.formData.template = builtIn.id
          this.loadedTemplateRecord = null
          this.manualTemplateId = id
          Object.assign(this.formData, applyTemplateDefaults(this.formData, builtIn))
        }
        this.nullColumnConfigsForPreset()
        this.previewStyleOverride = styleToKeep
        if (preserveLoadedRecord && savedRecord) {
          this.loadedTemplateRecord = savedRecord
          if (savedTemplateId != null) this.formData.template = savedTemplateId
        }
        this.afterLineItemPresetApplied({ quiet, toastName: quiet ? '' : builtIn.name })
        return true
      }

      this.templateLoading = true
      try {
        const res = await invoiceConfigService.getTemplateById(id)
        if (!(res && res.status === 'OK' && res.data && res.data.config)) return false
        if (preserveLoadedRecord && baseConfig) {
          this.activeTemplate = {
            ...baseConfig,
            lineItemColumns: res.data.config.lineItemColumns || baseConfig.lineItemColumns,
          }
          this.manualTemplateId = id
        } else {
          this.activeTemplate = res.data.config
          this.formData.template = res.data.id
          this.loadedTemplateRecord = res.data
          this.manualTemplateId = id
          Object.assign(this.formData, applyTemplateDefaults(this.formData, res.data.config))
        }
        this.nullColumnConfigsForPreset()
        this.previewStyleOverride = styleToKeep
        if (preserveLoadedRecord && savedRecord) {
          this.loadedTemplateRecord = savedRecord
          if (savedTemplateId != null) this.formData.template = savedTemplateId
        }
        this.afterLineItemPresetApplied({ quiet, toastName: quiet ? '' : (res.data.name || '') })
        return true
      } catch (e) {
        console.error('Error loading line-item preset override:', e)
        return false
      } finally {
        this.templateLoading = false
      }
    },
    /**
     * Seed template-defined default rows when the table is still empty.
     */
    applyDefaultLineItemsFromTemplate(templateConfig, defaultRows) {
      const rows = defaultRows
        || (templateConfig && (templateConfig.defaultLineItems || templateConfig.lineItems))
        || null
      if (!Array.isArray(rows) || !rows.length) return
      if ((this.lineItems || []).some((r) => this.lineItemRowHasMeaningfulData(r))) return
      const cols = this.lineItemValidationColumns
      this.lineItems = rows.map((row) => recomputeRow(
        migrateLineItemSacHsn({ ...row }),
        cols,
      ))
      this.syncSrNoColumns()
    },
    /**
     * Load the best-matching template for the current client + category.
     * Order: (1) API getTemplateForClient (backend client assignment),
     * (2) RPO/DEFAULT built-in heuristic when no active backend row.
     * Skipped while a manual template override is active (see
     * onTemplateSelected() above) — the user's explicit pick wins over
     * auto-detection until they switch back to "Auto (recommended)".
     */
    async loadTemplateForClient() {
      const clientId = this.formData.client
      const category = this.formData.category
      if (!clientId) return
      if (this.manualTemplateId != null) return

      // Backend client+category template association (source of truth).
      this.templateLoading = true
      this.loadedTemplateRecord = null
      try {
        const res = await invoiceConfigService.getTemplateForClient(clientId, category)
        if (res && res.status === 'OK' && res.data) {
          const applied = await this.applyClientTemplateFromBackend(res.data, { clientId, category })
          if (applied) return
        }
      } catch (e) {
        // fall through to built-in defaults
      } finally {
        this.templateLoading = false
      }

      // Built-in RPO vs generic heuristic when API has no active row.
      const lineItemType = invoiceConfigService.getClientInvoiceLineItemTemplate(clientId, category)
      const fallback = lineItemType === 'rpo' ? RPO_TEMPLATE : DEFAULT_TEMPLATE
      this.activeTemplate = { ...fallback }
      this.formData.template = fallback.id || 'standard'
      Object.assign(this.formData, applyTemplateDefaults(this.formData, fallback))
      this.dynamicColumnConfig = null
      this.dynamicColumnConfigTable2 = null
      this.afterLineItemPresetApplied({ quiet: true })
      // Preference restore runs from onClientSelected after this returns.
    },
    /**
     * Apply a template record returned by GET /invoice/config/templates
     * (best-match for client + category). Keeps manualTemplateId null when
     * no explicit preset override so category changes can re-fetch; UI shows
     * "Auto · {name}" unless a line_item_preset_id is on the record.
     *
     * @returns {Promise<boolean>} true when applied
     */
    async applyClientTemplateFromBackend(record, { clientId, category, quiet = false } = {}) {
      if (!record || !record.config) return false
      const meta = this.extractTemplateLineItemMeta(record)

      this.activeTemplate = record.config
      this.formData.template = record.id || 'custom'
      this.loadedTemplateRecord = record
      this.manualTemplateId = null

      if (meta.columnConfig) {
        this.dynamicColumnConfig = meta.columnConfig.map((c) => ({ ...c }))
      } else {
        this.dynamicColumnConfig = null
        this.dynamicColumnConfigTable2 = null
      }

      Object.assign(this.formData, applyTemplateDefaults(this.formData, record.config))
      const layoutStyle = record.config.layout && record.config.layout.style
      if (layoutStyle) {
        this.previewStyleOverride = layoutStyle
      }

      if (meta.presetId != null && !meta.columnConfig) {
        await this.applyLineItemPresetOverride(meta.presetId, {
          quiet: true,
          preserveLoadedRecord: true,
        })
      } else if (meta.presetId != null) {
        this.manualTemplateId = meta.presetId
      }

      // Keep template/preference column schema — do not null it out.
      this.afterLineItemPresetApplied({
        quiet: true,
        preserveColumnConfig: !!(meta.columnConfig && this.dynamicColumnConfig),
      })
      this.applyDefaultLineItemsFromTemplate(record.config, meta.defaultLineItems)

      if (!quiet) {
        const name = record.name || 'Template'
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Format loaded',
            text: `Using "${name}" for ${clientId || this.formData.client || 'client'}${category ? ' · ' + category : ''}`,
            icon: 'LayoutIcon',
            variant: 'success',
          },
        })
      }
      return true
    },
    _colSpecToField(col) {
      const isNumeric = ['number', 'currency', 'percentage', 'computed'].includes(col.type)
      return {
        key: col.key,
        label: (col.label || col.key).toUpperCase(),
        thStyle: col.width ? { width: col.width } : undefined,
        thClass: 'small text-uppercase text-muted' + (isNumeric ? ' text-right' : ''),
        tdClass: (isNumeric ? 'text-right ' : '') + 'align-middle',
        sortable: false,
      }
    },
    // Recognizes "Sr No" / "S.No" / "Serial No" style columns by key or
    // label, regardless of what the backend template or a renamed
    // Manage-Columns entry happens to call it.
    isSrNoColumnKey(key, label) {
      const k = String(key || '').toLowerCase().replace(/[^a-z0-9]/g, '')
      if (k === 'srno' || k === 'sno' || k === 'slno') return true
      const l = String(label || '').toLowerCase()
      return /\bsr\.?\s*no\.?\b|\bsl\.?\s*no\.?\b|serial\s*no/.test(l)
    },
    // Stamps every recognized "Sr No" column to the row's 1-based position —
    // makes it "correct by construction" (always sequential) instead of a
    // free-typed field a user could get wrong or leave blank. Called after
    // every operation that reorders/adds/removes rows, and once more right
    // before building the submit payload as a final safety net.
    syncSrNoColumns() {
      this.lineItems.forEach((item, idx) => {
        const cols = this.getValidationColumnsForRow(item).filter((c) => this.isSrNoColumnKey(c.key, c.label))
        cols.forEach((c) => { this.$set(item, c.key, idx + 1) })
      })
    },
    /**
     * Validates a single line-item cell against its column spec.
     * - Required text/select columns: must be non-blank.
     * - Numeric-like columns (number/currency/percentage): if a value is
     *   present it must be a valid, non-negative number; if the column is
     *   required, it must additionally be > 0 — a bare "0" or blank on a
     *   required numeric field (e.g. Rate Card, Total Head Count) is
     *   treated as incomplete, not silently accepted.
     * - computed/move/actions columns are never user-editable, so always valid.
     * @returns {string} error message, or '' if valid
     */
    validateLineItemCell(col, value) {
      return validateInvoiceLineItemCell(col, value)
    },
    /** Serialize Payment Terms for API — objects use `.name`, free-text stays as string. */
    formatPaymentTermsForSubmit(term) {
      if (term == null || term === '') return ''
      if (typeof term === 'string') return term.trim()
      if (typeof term === 'object') {
        if (term.name != null && String(term.name).trim() !== '') return String(term.name).trim()
        if (term.days != null && term.days !== '') {
          if (term.days === 'EOM' || term.days === 'EONM') return String(term.days)
          const days = parseInt(term.days, 10)
          if (!Number.isNaN(days)) return days === 0 ? 'Due on Receipt' : `Net ${days} days`
        }
      }
      return ''
    },
    onPaymentTermsSelected(term) {
      if (!term || !this.formData.invoiceDate) return
      const invDate = moment(this.formData.invoiceDate)
      if (!invDate.isValid()) return
      // Normalize free-text (string) from taggable select into { name, days }.
      let normalized = term
      if (typeof term === 'string') {
        const name = term.trim()
        if (!name) return
        const match = name.match(/(\d+)/)
        const days = match ? parseInt(match[1], 10) : null
        normalized = { name, days: Number.isNaN(days) ? null : days }
        this.formData.paymentTerms = normalized
      }
      let due
      if (normalized.days === 'EOM') {
        due = invDate.clone().endOf('month')
      } else if (normalized.days === 'EONM') {
        due = invDate.clone().add(1, 'month').endOf('month')
      } else if (normalized.days == null || normalized.days === '') {
        // Custom text without a day count — leave Due Date for manual edit.
        return
      } else {
        const days = parseInt(normalized.days, 10)
        if (Number.isNaN(days)) return
        due = invDate.clone().add(days, 'days')
      }
      this.formData.dueDate = due.format('YYYY-MM-DD')
    },
    // BUG FIX (2026-07-17): this used to only read activeTemplate's own
    // columns, so once a user customized columns via Manage Columns
    // (dynamicColumnConfig), new rows silently omitted any dynamic-only
    // column key — it only existed on rows that were already on the page
    // when the column was added. lineItemValidationColumns is the same
    // dynamicColumnConfig-aware source used for rendering/validation, so
    // every freshly-built row now always matches what's actually displayed
    // and includes any custom fields a user has added, regardless of where
    // the row itself came from (a plain "Add Item" click, or — 2026-07-22 —
    // a candidate added via the "Add Candidates" flow; see
    // convertCandidatesToLineItems() below, which now shares this same
    // builder instead of hand-rolling its own narrower row shape).
    buildEmptyLineItemRow(tableGroup = 1) {
      const defaultSac = this.defaultSacCode
      const group = Number(tableGroup) === 2 ? 2 : 1
      const cols = this.getValidationColumnsForTable(group)
      const row = {}
      cols.forEach((col) => {
        if (col.type === 'number' || col.type === 'currency' || col.type === 'computed' || col.type === 'percentage') {
          // Most line items are qty 1 — default to that instead of 0 so users
          // don't have to type it on every row (templates rarely set an
          // explicit `default` for quantity).
          const fallback = col.key === 'quantity' ? 1 : 0
          row[col.key] = col.default !== undefined ? col.default : fallback
        } else if (col.type === 'sac-select') {
          row[col.key] = col.default !== undefined ? col.default : defaultSac
        } else {
          row[col.key] = col.default !== undefined ? col.default : ''
        }
      })
      row.tableGroup = group
      row.title = row.title || ''
      return row
    },
    addNewRow(preferredTableGroup) {
      // BUG FIX (2026-07-22): this used to recompute against
      // `activeTemplate.lineItemColumns` directly, on the theory that
      // "formulas only live on the template's own column specs, not
      // dynamicColumnConfig" — no longer true now that Manage Columns can
      // author its own computed/formula columns (see setColumnComputed
      // below); lineItemValidationColumns is the correct merged source that
      // includes those too, and is exactly what recomputeLineItem() also
      // uses for every SUBSEQUENT recompute, so a freshly-added row and one
      // recomputed later behave identically.
      const group = Number(preferredTableGroup) === 2 ? 2 : 1
      const empty = this.buildEmptyLineItemRow(group)
      const recomputed = recomputeRow(empty, this.getValidationColumnsForTable(group))
      this.lineItems.push(recomputed)
      this.syncSrNoColumns()
    },
    moveLineItemUp(index) {
      if (index <= 0) return
      const item = this.lineItems[index]
      this.lineItems.splice(index, 1)
      this.lineItems.splice(index - 1, 0, item)
      this.syncSrNoColumns()
    },
    moveLineItemDown(index) {
      if (index >= this.lineItems.length - 1) return
      const item = this.lineItems[index]
      this.lineItems.splice(index, 1)
      this.lineItems.splice(index + 1, 0, item)
      this.syncSrNoColumns()
    },
    duplicateLineItem(index) {
      const item = this.lineItems[index]
      const copy = { ...item }
      this.lineItems.splice(index + 1, 0, copy)
      this.syncSrNoColumns()
    },
    // Tag uses the same "Add Candidates from ATS" modal (Joined candidates
    // for client+category). Candidate Lookup was removed — it opened from here.
    openTagCandidateManual(index) {
      this.tagCandidateLineIndex = index
      this.openCandidateModal()
    },

    /** Text/name columns eligible for "mention candidate in Line Items". */
    defaultCandidateMentionColumnKey() {
      const cols = this.candidateNameColumnOptions
      if (!cols.length) return null
      const preferred = ['candidateName', 'employeeName', 'itemName', 'description', 'particulars']
      for (const key of preferred) {
        if (cols.some((c) => c.key === key)) return key
      }
      return cols[0].key
    },

    /**
     * Ask Yes/No then (if Yes) which column. Resolves
     * { mention: boolean, columnKey: string|null }. Cancel on column modal → null.
     */
    async askCandidateMentionPreference() {
      const mention = await this.$bvModal.msgBoxConfirm(
        'Do you want to mention the candidate in Line Items?',
        {
          title: 'Candidate in Line Items',
          size: 'sm',
          buttonSize: 'sm',
          okTitle: 'Yes',
          cancelTitle: 'No',
          centered: true,
          footerClass: 'p-2',
        },
      )
      if (!mention) {
        return { mention: false, columnKey: null }
      }
      const columnKey = await this.pickCandidateMentionColumn()
      if (!columnKey) return null
      return { mention: true, columnKey }
    },

    pickCandidateMentionColumn() {
      const options = this.candidateNameColumnOptions
      if (!options.length) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'No text columns',
            text: 'Add a text/name column (e.g. Description, Candidate Name) via Manage Columns first.',
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        })
        return Promise.resolve(null)
      }
      this.pendingCandidateMentionColumnKey = this.defaultCandidateMentionColumnKey()
      this.showCandidateMentionColumnModal = true
      return new Promise((resolve) => {
        this._candidateMentionColumnResolve = resolve
      })
    },

    confirmCandidateMentionColumn() {
      const key = this.pendingCandidateMentionColumnKey
      const resolve = this._candidateMentionColumnResolve
      this._candidateMentionColumnResolve = null
      this.showCandidateMentionColumnModal = false
      if (resolve) resolve(key || null)
    },

    cancelCandidateMentionColumn() {
      const resolve = this._candidateMentionColumnResolve
      this._candidateMentionColumnResolve = null
      this.showCandidateMentionColumnModal = false
      if (resolve) resolve(null)
    },

    onCandidateMentionColumnModalHidden() {
      if (this._candidateMentionColumnResolve) {
        const resolve = this._candidateMentionColumnResolve
        this._candidateMentionColumnResolve = null
        resolve(null)
      }
    },

    applyCandidateNameToRow(row, candidateName, mentionPref) {
      if (!row || !mentionPref || !mentionPref.mention || !mentionPref.columnKey) return row
      row[mentionPref.columnKey] = candidateName || ''
      return row
    },

    /**
     * ATS candidate DOJ — API uses `joining_date`; some payloads also send `doj`
     * / `date_of_joining` / `joiningDate`. Format for the line-item date column
     * (WizardLineItemsStep flat-pickr `d-M-y`, e.g. 24-Jul-26).
     */
    extractCandidateDojRaw(candidate) {
      if (!candidate) return null
      return candidate.joining_date
        || candidate.doj
        || candidate.date_of_joining
        || candidate.joiningDate
        || null
    },
    formatCandidateDojForLineItem(candidate) {
      const raw = this.extractCandidateDojRaw(candidate)
      if (raw == null || raw === '') return null
      const m = moment(raw)
      if (m.isValid()) return m.format('DD-MMM-YY')
      // Already a display string (e.g. restored draft) — keep as-is.
      return typeof raw === 'string' ? raw : null
    },
    lineItemHasDojColumn(row) {
      if (row && Object.prototype.hasOwnProperty.call(row, 'doj')) return true
      return (this.lineItemValidationColumns || []).some(c => c && c.key === 'doj')
    },
    applyCandidateDojToRow(row, candidate) {
      if (!row || !this.lineItemHasDojColumn(row)) return row
      const doj = this.formatCandidateDojForLineItem(candidate)
      if (doj != null) row.doj = doj
      return row
    },

    applyCandidateTagToLine(idx, candidate, mentionPref) {
      const line = this.lineItems[idx]
      if (!line || !candidate) return
      const name = candidate.candidate_name || ''
      const updated = {
        ...line,
        candidateId: candidate.candidate_id || candidate.cid || line.candidateId,
        cid: candidate.cid || line.cid,
        jid: candidate.jid || line.jid,
        tagged_status: 'MANUAL',
      }
      if (mentionPref && mentionPref.mention && mentionPref.columnKey) {
        this.applyCandidateNameToRow(updated, name, mentionPref)
      }
      this.applyCandidateDojToRow(updated, candidate)
      this.$set(this.lineItems, idx, recomputeRow(updated, this.getValidationColumnsForRow(updated)))
    },

    removeLineItem(index) {
      if (index < 0 || index >= this.lineItems.length) {
        console.error('Invalid line item index:', index)
        return
      }
      const item = this.lineItems[index]
      // If this line item came from a candidate, also remove the candidate
      if (item.cid && item.jid) {
        const candidateIndex = this.selectedCandidates.findIndex(c => 
          c.cid === item.cid && c.jid === item.jid
        )
        if (candidateIndex > -1) {
          this.selectedCandidates.splice(candidateIndex, 1)
          const candidateId = `${item.cid}-${item.jid}`
          const idIndex = this.selectedCandidateIds.indexOf(candidateId)
          if (idIndex > -1) {
            this.selectedCandidateIds.splice(idIndex, 1)
          }
        }
      }
      this.lineItems.splice(index, 1)
      this.syncSrNoColumns()
    },
    recomputeLineItem(index) {
      if (index < 0 || index >= this.lineItems.length) return
      const cols = this.getValidationColumnsForRow(this.lineItems[index])
      const updated = recomputeRow(this.lineItems[index], cols)
      this.$set(this.lineItems, index, updated)
    },
    onCandidateOptionChange(value) {
      // WizardLineItemsStep keeps a local radio copy for UI; the parent must
      // own the real flag. Without this, isForCandidates stayed null after
      // "Yes", and convertCandidatesToLineItems() early-returned — so Add /
      // Tag from ATS updated selectedCandidates but never created/synced
      // line-item rows (candidates appeared "added" in the hint but missing
      // from the Items list).
      this.isForCandidates = value
      if (value === false) {
        this.selectedCandidates = []
        this.selectedCandidateIds = []
        this.convertCandidatesToLineItems()
        // Same seeding as goToLineItems(): avoid forcing an extra "Add New
        // Row" click for the common single manual line item case.
        if (this.lineItems.length === 0) {
          this.addNewRow()
        }
      } else if (value === true && this.selectedCandidates.length > 0) {
        // Heal cases where candidates were selected while parent flag was
        // still null (rows never synced into lineItems).
        this.convertCandidatesToLineItems()
      }
      // 2026-07-22: no longer eagerly loads candidates on "Yes" — the
      // candidate list only ever gets fetched on demand now, when the "Add
      // Candidates" modal itself opens (see openCandidateModal()), not in
      // the background the moment this toggle flips.
    },
    openCandidateModal() {
      if (!this.formData.client || !this.formData.category) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Please select Client and Category first',
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
        this.tagCandidateLineIndex = null
        return
      }
      this.showCandidateModal = true
      this.searchCandidates()
    },
    onCandidateAtsModalHidden() {
      this.candidateSearchName = ''
      // Drop Tag target when ATS is dismissed without Add Selected.
      // During Apply, addSelectedCandidates closes the modal first and still needs tagIdx.
      if (!this._atsApplyingSelection) {
        this.tagCandidateLineIndex = null
      }
    },
    async searchCandidates() {
      this.loading = true
      try {
        // Use existing API: getCandidateJoinedListReadyForInvoice
        const res = await candidateService.getCandidateJoinedListReadyForInvoice(
          this.formData.client,
          this.formData.category
        )
        
        if (res && res.status === 'OK') {
          let candidates = (res.data || []).filter(c => c && c.cid && c.jid) // Filter out invalid candidates
          
          // Filter by search name if provided
          if (this.candidateSearchName && this.candidateSearchName.trim()) {
            candidates = candidates.filter(c => 
              c.candidate_name && 
              c.candidate_name.toLowerCase().includes(this.candidateSearchName.toLowerCase().trim())
            )
          }
          
          // Filter out already selected candidates
          const selectedCandidateIds = new Set(
            this.selectedCandidates.map(item => `${item.cid}-${item.jid}`)
          )
          
          // Mark already selected candidates and filter them out
          this.candidateList = candidates
            .filter(candidate => {
              const candidateKey = `${candidate.cid}-${candidate.jid}`
              return !selectedCandidateIds.has(candidateKey)
            })
            .map(candidate => ({
              ...candidate,
              selected: false // New candidates are not selected by default
            }))
        } else if (res && res.status === 500) {
          this.candidateList = []
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'No Candidates Found',
              text: 'No candidates with status "Joined" found for this client and category.',
              icon: 'InfoIcon',
              variant: 'info'
            }
          })
        } else if (res && (res.status === 401 || res.status === 403)) {
          window.sessionStorage.setItem('lastPage', window.location.href.split('/#')[1])
          localStorage.clear()
          this.$router.push({ name: 'auth-login' })
        } else {
          this.candidateList = []
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              text: res.message || 'Error loading candidates. Please try again.',
              icon: 'AlertCircleIcon',
              variant: 'danger'
            }
          })
        }
      } catch (error) {
        console.error('Error searching candidates:', error)
        this.candidateList = []
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Error searching candidates. Please try again.',
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
      } finally {
        this.loading = false
      }
    },
    toggleCandidateSelection(candidate) {
      // Toggle the selected state and ensure Vue reactivity
      if (candidate && candidate.cid && candidate.jid) {
        // Find the index in candidateList
        const index = this.candidateList.findIndex(c => 
          c && c.cid === candidate.cid && c.jid === candidate.jid
        )
        if (index !== -1) {
          // Toggle the selected state using Vue.set to ensure reactivity
          this.$set(this.candidateList[index], 'selected', !this.candidateList[index].selected)
        }
      }
    },
    async addSelectedCandidates() {
      if (!this.candidateList || this.candidateList.length === 0) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'No Candidates',
            text: 'No candidates available to add',
            icon: 'InfoIcon',
            variant: 'info'
          }
        })
        return
      }

      const newlySelected = this.candidateList.filter(c =>
        c && c.selected && c.cid && c.jid && !this.selectedCandidateIds.includes(`${c.cid}-${c.jid}`)
      )

      if (newlySelected.length === 0) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'No Selection',
            text: 'Please select at least one candidate',
            icon: 'AlertCircleIcon',
            variant: 'warning'
          }
        })
        return
      }

      // Close ATS picker first so the Yes/No + column modals are not stacked under it
      this._atsApplyingSelection = true
      this.showCandidateModal = false
      this.candidateSearchName = ''

      try {
        const mentionPref = await this.askCandidateMentionPreference()
        if (mentionPref === null) {
          this.tagCandidateLineIndex = null
          return
        }

        const tableGroup = await this.askCandidateTargetTableGroup()
        if (tableGroup == null) {
          this.tagCandidateLineIndex = null
          return
        }

        // Adding/tagging from ATS implies this invoice is for candidates.
        // Keep parent flag in sync even if the Yes/No radio local state was
        // the only place that flipped (see onCandidateOptionChange).
        if (this.isCandidateCategory && this.isForCandidates !== true) {
          this.isForCandidates = true
        }

        const tagIdx = this.tagCandidateLineIndex
        let remaining = newlySelected
        const toSelectedCandidate = (c) => {
          const dojRaw = this.extractCandidateDojRaw(c)
          return {
            cid: c.cid,
            jid: c.jid,
            candidate_name: c.candidate_name || 'N/A',
            position_name: c.position_name || 'N/A',
            client: c.client || this.formData.client || 'N/A',
            revenue: c.revenue || c.base_amount || 0,
            // Keep ATS DOJ so convertCandidatesToLineItems / drafts can fill `doj`.
            joining_date: dojRaw,
            doj: dojRaw,
          }
        }

        // Tag mode: first selected candidate updates the active line item row
        if (tagIdx !== null && tagIdx !== undefined && this.lineItems[tagIdx]) {
          const [first, ...rest] = newlySelected
          this.applyCandidateTagToLine(tagIdx, first, mentionPref)
          remaining = rest
          // Also track the tagged candidate so later Add/Remove stays consistent
          const firstId = `${first.cid}-${first.jid}`
          if (!this.selectedCandidateIds.includes(firstId)) {
            this.selectedCandidates.push(toSelectedCandidate(first))
            this.selectedCandidateIds.push(firstId)
          }
        }

        remaining.forEach((candidate) => {
          const candidateId = `${candidate.cid}-${candidate.jid}`
          if (!this.selectedCandidateIds.includes(candidateId)) {
            this.selectedCandidates.push(toSelectedCandidate(candidate))
            this.selectedCandidateIds.push(candidateId)
          }
        })

        this.convertCandidatesToLineItems(mentionPref, tableGroup)
        this.tagCandidateLineIndex = null

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            text: `${newlySelected.length} candidate(s) added`,
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        })
      } finally {
        this._atsApplyingSelection = false
      }
    },
    // BUG FIX (2026-07-22): this used to unconditionally rebuild
    // `this.lineItems` from scratch — `this.lineItems = selectedCandidates
    // .map(c => ({ itemName: ..., ... }))` — every single time a candidate
    // was added, removed, or the Yes/No toggle changed. That meant using
    // "Add Candidates" and Manage Columns together didn't actually work:
    // (a) any custom column a user added via Manage Columns had no key in
    //     that hand-built object, so it silently went blank on every
    //     candidate-sourced row the moment a SECOND candidate was added
    //     (the whole array — including rows already there — got replaced);
    // (b) any value a user had manually typed into a custom column on an
    //     existing candidate row was wiped out the same way; and
    // (c) computed columns (including a user-customized "Amount" formula
    //     via Manage Columns) were never actually evaluated for these rows
    //     — `amount` was just hardcoded to the raw candidate revenue,
    //     correct only by coincidence for the untouched default formula.
    // Now this only ADDS a row for a selected candidate not already
    // represented in lineItems, and only REMOVES a candidate-linked row
    // whose candidate is no longer selected — every other row (manual, or
    // an existing candidate row with custom-column edits already on it) is
    // left completely alone, and new rows are built from the same
    // dynamicColumnConfig-aware buildEmptyLineItemRow()/recomputeRow()
    // pipeline addNewRow() uses, so custom fields and real formulas both
    // work here exactly like they do for a manually-added row.
    async askCandidateTargetTableGroup() {
      if (!this.formData.useSecondLineItemsTable) return 1
      const h1 = String(this.formData.lineItemTableHeading1 || '').trim() || 'Table 1'
      const h2 = String(this.formData.lineItemTableHeading2 || '').trim() || 'Table 2'
      const pickTable1 = await this.$bvModal.msgBoxConfirm(
        'Which table should these candidates appear in?',
        {
          title: 'Add to which table?',
          size: 'sm',
          buttonSize: 'sm',
          okTitle: h1,
          cancelTitle: h2,
          centered: true,
          footerClass: 'p-2',
        },
      )
      if (pickTable1 === null) return null
      return pickTable1 ? 1 : 2
    },
    /**
     * Sync selectedCandidates → lineItems.
     * @param {{ mention: boolean, columnKey: string|null }|null} mentionPref
     *   When provided (Add/Tag from ATS), controls whether the candidate name
     *   is written into a chosen text column. When omitted (e.g. toggle cleanup),
     *   preserves prior auto-fill behavior for any still-missing rows.
     * @param {1|2} tableGroup Table 1 or Table 2 when two tables are enabled.
     */
    convertCandidatesToLineItems(mentionPref = null, tableGroup = 1) {
      if (this.isForCandidates === false) {
        this.lineItems = this.lineItems.filter(item => !item.cid || !item.jid)
        return
      }
      if (this.isForCandidates !== true) return

      const validCandidates = this.selectedCandidates.filter(c => c && c.cid && c.jid)
      const selectedKeys = new Set(validCandidates.map(c => `${c.cid}-${c.jid}`))
      // Drop any candidate-linked row whose candidate was deselected/removed
      // elsewhere (e.g. tracked externally in selectedCandidateIds) — every
      // manual row (no cid/jid) is untouched.
      this.lineItems = this.lineItems.filter(item => !(item.cid && item.jid) || selectedKeys.has(`${item.cid}-${item.jid}`))

      const existingKeys = new Set(
        this.lineItems.filter(item => item.cid && item.jid).map(item => `${item.cid}-${item.jid}`),
      )
      const useMentionPref = mentionPref && typeof mentionPref.mention === 'boolean'
      validCandidates
        .filter(c => !existingKeys.has(`${c.cid}-${c.jid}`))
        .forEach((c) => {
          const baseAmt = parseFloat(c.revenue || c.base_amount || 0)
          const row = this.buildEmptyLineItemRow(Number(tableGroup) === 2 ? 2 : 1)
          const name = c.candidate_name || 'N/A'
          if (this.isRpoTemplate) {
            if ('employeeId' in row) row.employeeId = String(c.cid || c.candidate_id || '')
            if ('bu' in row) row.bu = c.position_name || c.bu || ''
            if ('geo' in row) row.geo = c.geo || ''
            if ('baseAmount' in row) row.baseAmount = baseAmt
            // Only auto-fill employeeName when not using the mention prompt
            // (legacy path), or when that column was explicitly chosen.
            if (!useMentionPref && 'employeeName' in row) {
              row.employeeName = name
            }
          } else {
            if ('quantity' in row) row.quantity = 1
            if ('unitPrice' in row) row.unitPrice = baseAmt
            if ('baseAmount' in row) row.baseAmount = baseAmt
            // Legacy path: put "Name - Position" in itemName. Mention-prompt
            // path: only fill the chosen column (below), never force itemName.
            if (!useMentionPref && 'itemName' in row) {
              row.itemName = name + (c.position_name ? ' - ' + c.position_name : '')
            }
          }
          // DOJ: RPO + Placement (CTC)/Bajaj (and any custom template with `doj`)
          this.applyCandidateDojToRow(row, c)
          if (useMentionPref && mentionPref.mention && mentionPref.columnKey) {
            this.applyCandidateNameToRow(row, name, mentionPref)
          }
          row.cid = c.cid
          row.jid = c.jid
          this.lineItems.push(recomputeRow(row, this.getValidationColumnsForRow(row)))
        })
      this.syncSrNoColumns()
    },
    // Step 0 (Doc Type) → Step 1 (Client).
    goToClient() {
      if (!this.formData.documentType) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Document type required',
            text: 'Select Tax Invoice, Proforma, Credit Note, or Debit Note to continue.',
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        })
        return
      }
      this.currentStep = 1
    },
    // Step 1 (Client) → Step 2 (Sattva GST / Location).
    goToSattvaLocation() {
      if (!this.isClientStepValid) {
        this.validateClientStep()
        return
      }
      this.currentStep = 2
    },
    // Step 2 (Sattva GST / Location) → Step 3 (Line Items).
    goToLineItems() {
      if (!this.isLocationStepValid) {
        this.validateLocationStep()
        return
      }
      this.currentStep = 3
      // For non-candidate categories, seed one empty row automatically so
      // the common single-line-item invoice doesn't need an extra
      // "Add New Row" click before the user can start typing. Category and
      // Vertical (and therefore isCandidateCategory, which needs both) can
      // now also be picked right here via the quick-pick above, but a user
      // arriving on this step fresh still won't have picked either yet, so
      // this is almost always true on first arrival — the candidate-specific
      // flow surfaces reactively the moment isCandidateCategory does.
      if (!this.isCandidateCategory && this.lineItems.length === 0) {
        this.addNewRow()
      }
    },
    // Step 3 (Line Items) → Step 4 (More Details).
    goToMoreDetails() {
      if (!this.canProceedPastLineItems) {
        let errorMessage = 'Please add at least one line item'
        if (!this.formData.category || !this.formData.vertical) {
          errorMessage = 'Please select category and vertical'
        } else if (this.isCandidateCategory && this.isForCandidates === null) {
          errorMessage = 'Please select whether this invoice is for candidates'
        } else if (this.isCandidateCategory && this.isForCandidates === true && this.selectedCandidates.length === 0) {
          errorMessage = 'Please select at least one candidate'
        }
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            text: errorMessage,
            icon: 'AlertCircleIcon',
            variant: 'warning'
          }
        })
        return
      }
      this.currentStep = 4
    },
    // Step 4 (More Details) → Step 5 (Payment).
    goToPayment() {
      if (!this.isMoreDetailsValid) {
        this.validateMoreDetailsStep()
        return
      }
      this.currentStep = 5
    },
    validateClientStep() {
      this.error.client = !this.formData.client ? 'Client is required' : ''
      this.error.legalName = (
        this.clientLegalNameOptions.length > 0
        && !this.formData.invoiceClientInfo.legalName
      ) ? 'Legal name is required' : ''
      this.error.clientState = (this.clientStateOptions.length > 0 && !this.formData.clientState)
        ? 'State is required'
        : ''
    },
    validateLocationStep() {
      this.error.location = !this.formData.location ? 'Location is required' : ''
    },
    validateMoreDetailsStep() {
      this.error.category = !this.formData.category ? 'Category is required' : ''
      this.error.vertical = !this.formData.vertical ? 'Vertical is required' : ''
      if (!this.formData.invoiceId) {
        this.error.invoiceId = getInvoicePrefixForVertical(this.formData.vertical)
          ? 'Invoice # could not be auto-generated — click Regenerate or select a Vertical'
          : 'Invoice ID is required'
      } else {
        this.error.invoiceId = ''
      }
      this.error.invoiceDate = !this.formData.invoiceDate ? 'Invoice Date is required' : ''
      this.error.gstType = !this.formData.gstType ? 'GST Type is required' : ''
    },
    // ─── Auto-save draft (Sattva Backend — see invoice-draft-service.js) ─────
    _buildDebouncedSaveDraft() {
      let timer = null
      return () => {
        if (this._restoringDraft || typeof this._saveDraft !== 'function') return
        clearTimeout(timer)
        timer = setTimeout(() => this._saveDraft(), 3000)
      }
    },
    _buildDraftPayload() {
      return {
        formData: JSON.parse(JSON.stringify(this.formData)),
        lineItems: JSON.parse(JSON.stringify(this.lineItems)),
        template_id: (this.loadedTemplateRecord && this.loadedTemplateRecord.id)
          || this.formData.template
          || (this.activeTemplate && this.activeTemplate.id)
          || null,
        manual_template_id: this.manualTemplateId,
        layout_style: this.previewStyleOverride
          || (this.activeTemplate && this.activeTemplate.layout && this.activeTemplate.layout.style)
          || 'bajaj',
        line_item_preset_id: this.manualTemplateId
          || (this.activeTemplate && this.activeTemplate.id)
          || null,
        line_item_column_config: this.dynamicColumnConfig
          ? persistableColumnConfig(this.dynamicColumnConfig)
          : null,
        line_item_column_config_table2: (this.usesSeparateTableColumnsNow() && this.dynamicColumnConfigTable2)
          ? persistableColumnConfig(this.dynamicColumnConfigTable2)
          : null,
        line_item_column_config_by_preset: persistablePresetColumnStore(this.columnConfigByPreset),
        selected_candidates: JSON.parse(JSON.stringify(this.selectedCandidates || [])),
        is_for_candidates: this.isForCandidates,
        savedAt: new Date().toISOString(),
      }
    },
    async _saveDraft() {
      if (this._restoringDraft || this._draftSaveInFlight) return
      if (!this.formData.client && !(this.lineItems && this.lineItems.length)) return
      this._draftSaveInFlight = true
      try {
        const res = await invoiceDraftService.saveCurrentDraft(this._buildDraftPayload())
        if (res && res.status === 'OK') {
          this.lastSavedAt = new Date()
          this.draftPersistSource = res._source || 'api'
          if (res._source === 'session' && !this._draftSessionWarned) {
            this._draftSessionWarned = true
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Draft not persisted to server',
                text: res.message || 'Draft API unavailable — kept in memory for this session only.',
                icon: 'AlertCircleIcon',
                variant: 'warning',
              },
            })
          }
        } else if (res && res.status === 'ERROR') {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Draft save failed',
              text: res.message || 'Could not save invoice draft to the server.',
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
          })
        }
      } catch (e) {
        console.warn('[InvoiceCreateManual] draft save error:', e)
      } finally {
        this._draftSaveInFlight = false
      }
    },
    async _restoreDraftIfAvailable() {
      try {
        const res = await invoiceDraftService.getCurrentDraft()
        let draft = res && res.status === 'OK' ? res.data : null
        // One-time migration from legacy browser key (then purged).
        if (!draft) {
          draft = invoiceDraftService.consumeLegacyLocalDraft()
          if (draft) {
            // Best-effort push to API so resume survives refresh next time.
            invoiceDraftService.saveCurrentDraft(draft).catch(() => {})
          }
        } else {
          invoiceDraftService.clearLegacyLocalDraft()
        }
        if (!draft || !draft.formData || !draft.formData.client) return
        this.showDraftBanner = true
        this._pendingDraft = draft
        this.draftPersistSource = (res && res._source) || (draft ? 'legacy' : null)
      } catch (e) {
        console.warn('[InvoiceCreateManual] draft load error:', e)
      }
    },
    async restoreDraft() {
      if (!this._pendingDraft) return
      this._restoringDraft = true
      const draft = this._pendingDraft
      const formData = draft.formData || {}
      const lineItems = draft.lineItems || []
      Object.assign(this.formData, formData)
      this.ensureBillingMonthOption(this.formData.billingMonth)
      if (this.formData.includeMsmeTerms == null) {
        this.$set(this.formData, 'includeMsmeTerms', false)
      }
      // Normalize header SAC/HSN and line-item aliases — keep LUT separate.
      const info = this.formData.invoiceSattvaInfo
      if (info) {
        const sac = resolveSacHsnCode(info)
        this.$set(info, 'sacCode', sac || null)
      }
      this.lineItems = (lineItems || []).map((row) => migrateLineItemSacHsn({ ...row }))
      // Style + column layout (server-primary fields; also accept camelCase legacy)
      this.previewStyleOverride = draft.layout_style || draft.previewStyleOverride || null
      const colConfig = draft.line_item_column_config || draft.dynamicColumnConfig || null
      this.dynamicColumnConfig = Array.isArray(colConfig) ? persistableColumnConfig(colConfig) : null
      const colConfig2 = draft.line_item_column_config_table2
        || draft.dynamicColumnConfigTable2
        || null
      this.dynamicColumnConfigTable2 = Array.isArray(colConfig2) ? persistableColumnConfig(colConfig2) : null
      const byPreset = draft.line_item_column_config_by_preset
        || draft.columnConfigByPreset
        || null
      if (byPreset && typeof byPreset === 'object') {
        this.columnConfigByPreset = persistablePresetColumnStore(byPreset)
      }
      if (this.formData.secondTableColumnsSame == null) {
        this.$set(this.formData, 'secondTableColumnsSame', true)
      }
      if (!this.formData.useSecondLineItemsTable) {
        this.formData.secondTableColumnsSame = true
        this.dynamicColumnConfigTable2 = null
      }
      const presetId = draft.manual_template_id ?? draft.manualTemplateId
        ?? draft.line_item_preset_id ?? draft.activeTemplateId ?? null
      this.selectedCandidates = Array.isArray(draft.selected_candidates)
        ? draft.selected_candidates
        : (Array.isArray(draft.selectedCandidates) ? draft.selectedCandidates : [])
      if (draft.is_for_candidates != null) this.isForCandidates = draft.is_for_candidates
      else if (draft.isForCandidates != null) this.isForCandidates = draft.isForCandidates
      // Re-bind Credit Period / Payment Terms to option shapes so selects
      // show the stored master (or user-overridden) values instead of blank.
      if (this.formData.creditPeriod != null && String(this.formData.creditPeriod).trim() !== '') {
        const days = parseInt(this.formData.creditPeriod, 10)
        if (!Number.isNaN(days)) {
          this.ensureCreditPeriodOption(days)
          this.formData.creditPeriod = days
        }
      }
      if (this.formData.paymentTerms) {
        const stored = this.formData.paymentTerms
        const storedName = typeof stored === 'string'
          ? stored
          : (stored && stored.name)
        const storedDays = typeof stored === 'object' && stored != null
          ? stored.days
          : null
        const match = (this.paymentTermsOptions || []).find(
          (t) => (storedDays != null && t.days === storedDays)
            || (storedName && t.name === storedName),
        )
        if (match) {
          this.formData.paymentTerms = match
        } else if (typeof stored === 'string' && stored.trim()) {
          // Custom free-text from draft — keep as option-shaped object for v-select.
          const daysMatch = stored.match(/(\d+)/)
          const days = daysMatch ? parseInt(daysMatch[1], 10) : null
          this.formData.paymentTerms = {
            name: stored.trim(),
            days: Number.isNaN(days) ? null : days,
          }
        }
      }
      this.showDraftBanner = false
      this._pendingDraft = null
      // Draft wins over last-template preference — never soft-restore here.
      if (presetId != null && presetId !== '') {
        await this.onTemplateSelected(presetId, { quiet: true })
      } else {
        await this.loadTemplateForClient()
      }
      // Re-apply style/columns after template load (preset apply clears dynamic cols)
      if (draft.layout_style || draft.previewStyleOverride) {
        this.previewStyleOverride = draft.layout_style || draft.previewStyleOverride
      }
      if (Array.isArray(colConfig) && colConfig.length) {
        this.dynamicColumnConfig = colConfig
      }
      this.$nextTick(() => {
        this._restoringDraft = false
        this.syncAutoInvoiceName()
      })
      if (this.formData.invoiceId) {
        this._invoiceNumberVerticalAtGen = this.formData.vertical
        // Treat a restored draft's Invoice # like a manual edit too — don't
        // let a later Vertical change silently regenerate over it.
        this.invoiceIdManuallyEdited = true
      }
      this.$toast({
        component: ToastificationContent,
        props: { title: 'Draft Restored', text: 'Your previous draft has been loaded.', icon: 'ClockIcon', variant: 'info' },
      })
    },
    async discardDraft() {
      this.showDraftBanner = false
      this._pendingDraft = null
      const res = await invoiceDraftService.deleteCurrentDraft()
      if (res && res.status === 'ERROR') {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Could not discard draft on server',
            text: res.message || 'Draft cleared locally; server delete failed.',
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        })
      }
    },
    async _clearDraft() {
      await invoiceDraftService.deleteCurrentDraft()
      this.lastSavedAt = null
    },
    /**
     * Snapshot live preview props before clearing the wizard so the
     * post-submit modal / PDF persist still match the created invoice.
     */
    capturePostSubmitPreviewFreeze() {
      const clone = (v) => {
        try { return JSON.parse(JSON.stringify(v)) } catch (e) { return v }
      }
      this.postSubmitPreviewFreeze = {
        template: clone(this.previewTemplateForDisplay),
        documentType: this.previewDocumentType,
        billFrom: clone(this.previewBillFrom),
        billTo: clone(this.previewBillTo),
        invoiceMeta: clone(this.previewInvoiceMeta),
        status: this.previewInvoiceStatus,
        description: this.formData.description || '',
        lineItems: clone(this.previewLineItemsForGeneric),
        totals: clone(this.previewTotalsForGeneric),
        bankDetails: clone(this.previewBankDetailsForGeneric),
        logoUrl: this.companyLogoPreview,
        columns: clone(this.lineItemValidationColumns),
        columnsTable2: this.usesSeparateTableColumns
          ? clone(this.lineItemValidationColumnsTable2)
          : null,
        showDecimals: this.showDecimals,
        showCurrencySymbol: this.showCurrencySymbol,
        useThousandSeparators: this.useThousandSeparators,
        useSecondLineItemsTable: !!this.formData.useSecondLineItemsTable,
        lineItemTableHeading1: this.formData.lineItemTableHeading1 || '',
        lineItemTableHeading2: this.formData.lineItemTableHeading2 || '',
      }
    },
    buildEmptyFormData() {
      return {
        client: null,
        template: null,
        documentType: 'Tax Invoice',
        invoiceName: '',
        location: null,
        category: null,
        vertical: null,
        billingMonth: null,
        legalEntityId: null,
        clientState: null,
        invoiceClientInfo: {
          clientName: '',
          legalName: '',
          clientGST: '',
          clientAddress: '',
          clientState: '',
          placeOfSupply: '',
          clientPhone: '',
        },
        invoiceSattvaInfo: {
          companyName: '',
          gst: '',
          pan: '',
          address: '',
          bankName: '',
          accountName: '',
          accountNumber: '',
          accountType: '',
          ifscCode: '',
          logoUrl: '',
          lutNo: '',
          sacCode: null,
        },
        invoiceId: '',
        invoiceDate: moment().format('YYYY-MM-DD'),
        paymentTerms: null,
        dueDate: null,
        poNumber: '',
        gstType: '',
        description: '',
        isCollectNPay: false,
        payDate: null,
        creditPeriod: null,
        tdsApplicable: false,
        tdsPercentage: null,
        hasPackageCode: false,
        packageCode: '',
        useSecondLineItemsTable: false,
        lineItemTableHeading1: '',
        lineItemTableHeading2: '',
        secondTableColumnsSame: true,
        includeMsmeTerms: false,
      }
    },
    /**
     * Clear create wizard / sticky draft values after a successful create so
     * the form does not keep old data (draft watcher is suppressed briefly).
     */
    async resetCreateFormAfterSuccess() {
      this._suppressDraftSave = true
      try {
        this.currentStep = 0
        this.lineItems = []
        this.formData = this.buildEmptyFormData()
        this.invoiceFile = null
        this.invoiceFiles = []
        this.duplicateSourceId = ''
        this.duplicateSupportCopied = false
        this.isForCandidates = null
        this.selectedCandidates = []
        this.tagCandidateLineIndex = null
        this.showPreviewModal = false
        this.showMobilePreview = false
        this.showDraftBanner = false
        this._pendingDraft = null
        this.dynamicColumnConfig = null
        this.dynamicColumnConfigTable2 = null
        this.loadedTemplateRecord = null
        this.manualTemplateId = null
        this.previewStyleOverride = null
        await this._clearDraft()
      } finally {
        this.$nextTick(() => {
          this._suppressDraftSave = false
        })
      }
    },
    confirmSubmitFromPreview() {
      this.showPreviewModal = false
      this.onSubmit()
    },
    // "Download Invoice" — captures whichever <generic-invoice-preview> the
    // click came from (sticky panel vs. full-screen modal both have their
    // own ref, see the template) via html2canvas, then hands that off to
    // the shared exportElementToPdf() util (src/utils/pdf-export.js) for
    // A4, multi-page-aware PDF generation. No jsPDF/html2canvas usage
    // existed anywhere else in this codebase to match, so this + the util
    // it calls is a fresh, from-scratch implementation.
    onSupportingFileChanged(event) {
      const file = event && event.target && event.target.files && event.target.files[0]
      if (!file) {
        this.invoiceFile = null
        this.invoiceFiles = []
        return
      }
      const sizeError = getInvoiceFileSizeError(file)
      if (sizeError) {
        this.invoiceFile = null
        this.invoiceFiles = []
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'File too large',
            text: sizeError,
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        })
        if (event.target) event.target.value = ''
        return
      }
      const typeError = getInvoiceFileTypeError(file)
      if (typeError) {
        this.invoiceFile = null
        this.invoiceFiles = []
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'File type not allowed',
            text: typeError,
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        })
        if (event.target) event.target.value = ''
        return
      }
      this.invoiceFile = file
      this.invoiceFiles = [file]
    },
    onSupportingFilesChanged(files) {
      const list = Array.isArray(files) ? files.filter(Boolean) : []
      const allowed = []
      let rejected = false
      for (const file of list) {
        if (getInvoiceFileTypeError(file)) {
          rejected = true
          continue
        }
        allowed.push(file)
      }
      if (rejected) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'File type not allowed',
            text: INVOICE_FILE_TYPE_NOT_ALLOWED_MESSAGE,
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        })
      }
      this.invoiceFiles = allowed
      this.invoiceFile = allowed[0] || null
    },
    // Pick a preview DOM node that is actually laid out. `previewPanelRoot`
    // lives under `d-none d-lg-block`, so on <lg it has offsetWidth/Height 0
    // even though the ref exists — prefer any measurable sibling (modal /
    // post-submit / mobile) before falling back to the requested ref.
    resolvePdfPreviewElement(preferredRef) {
      const order = [
        preferredRef,
        'postSubmitPreviewRoot',
        'previewModalRoot',
        'previewPanelRoot',
        'previewMobileRoot',
      ]
      const seen = new Set()
      for (let i = 0; i < order.length; i += 1) {
        const name = order[i]
        if (!name || seen.has(name)) continue
        seen.add(name)
        const comp = this.$refs[name]
        const el = comp && comp.$el
        if (el && el.offsetWidth > 0 && el.offsetHeight > 0) return el
      }
      const preferred = this.$refs[preferredRef]
      return (preferred && preferred.$el) || null
    },
    async downloadInvoicePdf(refName, options = {}) {
      // Let Vue flush + one paint before measuring refs (modals / collapses).
      await this.$nextTick()
      await new Promise(resolve => {
        requestAnimationFrame(() => requestAnimationFrame(resolve))
      })
      const el = this.resolvePdfPreviewElement(refName)
      if (!el) {
        if (!options.silent) {
          this.$toast({
            component: ToastificationContent,
            props: { title: 'Preview not ready', text: 'Please wait for the preview to finish rendering and try again.', icon: 'AlertCircleIcon', variant: 'warning' },
          })
        }
        return null
      }
      this.downloadingPdf = true
      try {
        this.syncAutoInvoiceName()
        // Same builder as Overview / signed workflow downloads:
        // `{Client} {Invoice|Proforma|Credit Note|Debit Note} {Id} ({Mon-YYYY}).pdf`
        const filename = buildInvoiceDownloadFilename({
          ...this.formData,
          invoiceName: this.formData.invoiceName
            || `Invoice-${this.formData.invoiceId || 'SH000'}`,
        })
        const shouldReturnBlob = !!options.returnBlob || !!options.persistGenerated
        const result = await exportElementToPdf(el, filename, {
          download: options.download !== false,
          returnBlob: shouldReturnBlob,
        })
        // Persist generated PDF when explicitly requested (post-submit auto-save).
        // SignedInvoiceWorkflow also persists when using clientPdfProvider.
        if (options.persistGenerated && this.createdInvoiceKey && result && result.blob) {
          try {
            const sizeError = getInvoiceFileSizeError(result.blob)
            if (sizeError) {
              if (!options.silent) {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: 'File too large',
                    text: sizeError,
                    icon: 'AlertCircleIcon',
                    variant: 'warning',
                  },
                })
              }
            } else {
              const file = typeof File !== 'undefined'
                ? new File([result.blob], filename, { type: 'application/pdf', lastModified: Date.now() })
                : result.blob
              await enhancedInvoiceService.uploadGeneratedPdf(this.createdInvoiceKey, file, {
                source: 'client_preview_download',
              })
            }
          } catch (persistErr) {
            console.warn('[InvoiceCreateManual] generated PDF persist failed:', persistErr)
          }
        }
        return result
      } catch (e) {
        const msg = (e && e.message) || 'Could not generate the PDF. Please try again.'
        if (options.silent) {
          console.warn('[InvoiceCreateManual] PDF export skipped:', msg)
          return null
        }
        const isSize = /2 MB|2MB|must be 2/i.test(msg)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: isSize ? 'File too large' : 'Download failed',
            text: msg,
            icon: 'AlertCircleIcon',
            variant: isSize ? 'warning' : 'danger',
          },
        })
        return null
      } finally {
        this.downloadingPdf = false
      }
    },
    async providePostSubmitPdf() {
      const result = await this.downloadInvoicePdf('postSubmitPreviewRoot', {
        download: true,
        returnBlob: true,
      })
      if (result && result.blob) {
        return { blob: result.blob, filename: result.filename }
      }
      return null
    },
    onPostSubmitSignedUploaded() {
      if (this.createdInvoiceRecord) {
        this.$set(this.createdInvoiceRecord, 'has_signed_invoice', true)
      }
    },
    goToDashboardAfterSubmit() {
      this._postSubmitNavigating = true
      this.showPostSubmitModal = false
      this.postSubmitPreviewFreeze = null
      this.$router.push({ name: 'apps-invoice-dashboard' })
    },
    onPostSubmitModalHidden() {
      this.postSubmitPreviewFreeze = null
      if (!this._postSubmitNavigating) {
        this.$router.push({ name: 'apps-invoice-dashboard' })
      }
      this._postSubmitNavigating = false
    },
    getBaseColumnConfig() {
      const seen = new Set()
      return (this.activeTemplate.lineItemColumns || [])
        .filter((c) => {
          if (!c || isSystemColumnType(c.type) || !c.key || seen.has(c.key)) return false
          seen.add(c.key)
          return true
        })
        .map((c) => persistableColumn(c) || {
          key: c.key,
          label: c.label || c.key,
          type: resolveColumnType(c.type, c.data_type || c.dataType),
          required: !!c.required,
          formula: displayFormula(c.formula),
          visible: c.visible !== false,
          computed: resolveColumnType(c.type) === 'computed',
        })
    },
    /**
     * Live column list for GenericInvoicePreview's template.lineItemColumns.
     * Keeps move/actions chrome from the active preset and injects the
     * current Edit Columns / dynamicColumnConfig-aware data columns so the
     * invoice preview never reads a stale preset thead — order, labels,
     * types, and visibility all come from lineItemValidationColumns.
     */
    buildPreviewLineItemColumns() {
      void this.columnConfigEpoch
      const tmplCols = (this.activeTemplate && this.activeTemplate.lineItemColumns) || []
      const moveCol = tmplCols.find((c) => c && c.type === 'move')
      const actionsCol = tmplCols.find((c) => c && c.type === 'actions')
      const dataCols = (this.lineItemValidationColumns || [])
        .filter((c) => c && c.key && c.visible !== false)
        .map((c) => ({
          key: c.key,
          label: String(c.label || '').trim() || c.key,
          type: resolveColumnType(c.type),
          required: !!c.required,
          formula: c.formula,
          visible: c.visible !== false,
        }))
      const out = []
      if (moveCol) out.push({ ...moveCol })
      out.push(...dataCols)
      if (actionsCol) out.push({ ...actionsCol })
      return out
    },
    /**
     * Edit Columns rename — write label into dynamicColumnConfig and bump
     * columnConfigEpoch so Live Preview / PDF thead update as the user types
     * (Vue 2 can miss nested string edits for some computed consumers).
     */
    onColumnHeaderLabelInput(col, value) {
      if (!col) return
      const next = value == null ? '' : String(value)
      this.$set(col, 'label', next)
      this.columnConfigEpoch += 1
    },
    /** Force a new array reference so Vue recomputes preview columns reliably. */
    touchDynamicColumnConfig() {
      const cfg = this.getActiveColumnConfigArray()
      if (!cfg || !cfg.length) return
      this.setActiveColumnConfigArray(cfg.map((c) => ({ ...c })))
    },
    /** Force Edit Columns list from the active template (deduped). */
    resetColumnConfigFromTemplate() {
      const base = this.getBaseColumnConfig().map((c) => ({ ...c }))
      if (this.usesSeparateTableColumnsNow() && Number(this.columnConfigEditTable) === 2) {
        this.dynamicColumnConfigTable2 = base
      } else {
        this.dynamicColumnConfig = base
      }
      this.columnConfigEpoch += 1
    },
    /**
     * After a preset switch: keep user-entered values for shared keys, drop
     * orphan keys, and collapse accidental extra empty/sample rows (at most
     * one blank row when nothing meaningful is entered).
     */
    reconcileLineItemsAfterPresetChange() {
      const metaKeys = ['cid', 'jid', 'candidateId', 'tagged_status', 'candidateName', 'employeeName', 'tableGroup', 'title']
      if (!this.lineItems.length) return

      const remapped = this.lineItems.map((item) => {
        const group = Number(item.tableGroup) === 2 ? 2 : 1
        const cols = this.getValidationColumnsForTable(group)
        const row = this.buildEmptyLineItemRow(group)
        cols.forEach((col) => {
          if (item[col.key] !== undefined && item[col.key] !== null) {
            row[col.key] = item[col.key]
          }
        })
        metaKeys.forEach((k) => {
          if (item[k] !== undefined) row[k] = item[k]
        })
        row.tableGroup = group
        return recomputeRow(row, cols)
      })

      const meaningful = remapped.filter((r) => this.lineItemRowHasMeaningfulData(r))
      if (meaningful.length > 0) {
        // Keep real data; drop leftover blank rows from prior presets/seeds.
        this.lineItems = meaningful
      } else {
        // All blank — keep a single empty row so Step 3 stays ready to type.
        this.lineItems = remapped.length ? [remapped[0]] : []
      }
      this.syncSrNoColumns()
    },
    initColumnConfig() {
      const dedupe = (arr) => {
        if (!arr || !arr.length) return null
        const seen = new Set()
        return arr.filter((c) => {
          if (!c || !c.key || seen.has(c.key)) return false
          seen.add(c.key)
          return true
        })
      }
      if (!this.dynamicColumnConfig || this.dynamicColumnConfig.length === 0) {
        this.dynamicColumnConfig = this.getBaseColumnConfig().map((c) => ({ ...c }))
      } else {
        this.dynamicColumnConfig = dedupe(this.dynamicColumnConfig)
      }
      if (this.usesSeparateTableColumnsNow()) {
        if (!this.dynamicColumnConfigTable2 || !this.dynamicColumnConfigTable2.length) {
          this.dynamicColumnConfigTable2 = this.cloneColumnConfig(this.dynamicColumnConfig)
        } else {
          this.dynamicColumnConfigTable2 = dedupe(this.dynamicColumnConfigTable2)
        }
      }
    },
    /**
     * Open Line Items Configuration.
     * @param {'preview'|'edit'|0|1} tab  preview/0 = sample layout; edit/1 = Edit Columns
     */
    openLineItemsConfig(tab = 'preview') {
      this.columnConfigModalTab = tab === 'edit' || tab === 1 ? 1 : 0
      if (!this.usesSeparateTableColumnsNow()) this.columnConfigEditTable = 1
      this.initColumnConfig()
      this.ensureActiveColumnConfig()
      this.showColumnConfigModal = true
    },
    onLineItemsConfigShow() {
      this.initColumnConfig()
      // Capture after init's possible array replace so Done only toasts on
      // real Edit Columns edits, not on open alone.
      this.$nextTick(() => {
        this.columnEpochAtModalOpen = this.columnConfigEpoch
      })
    },
    async onConfigModalPresetChange(val) {
      // quiet: modal already shows the selected preview; avoid toast spam
      await this.onTemplateSelected(this.normalizeLineItemPresetId(val), { quiet: true })
      // Configure a specific preset → remember for next invoice for this client.
      // Auto leaves preference cleared (onTemplateSelected) / untouched.
      if (this.manualTemplateId != null) {
        this.rememberClientTemplatePreference()
      }
    },
    lineItemRowHasMeaningfulData(row) {
      if (!row) return false
      const defaultSac = this.defaultSacCode
      return (this.lineItemValidationColumns || []).some((col) => {
        if (!col || isComputedColumn(col)) return false
        if (this.isSrNoColumnKey(col.key, col.label)) return false
        const v = row[col.key]
        if (v == null || v === '') return false
        // Seeded blanks default quantity to 1 / SAC to the company default —
        // those alone must not count as "user entered" (or we'd keep every
        // auto-seeded empty row when switching presets).
        if (col.key === 'quantity' && (v === 1 || v === '1')) return false
        if (usesSacSelectWidget(col) && defaultSac && String(v) === String(defaultSac)) {
          return false
        }
        if (typeof v === 'number') return v !== 0
        return String(v).trim().length > 0
      })
    },
    recomputeAllLineItems() {
      this.lineItems.forEach((item, idx) => {
        const cols = this.getValidationColumnsForRow(item)
        this.$set(this.lineItems, idx, recomputeRow({ ...item }, cols))
      })
      this.syncSrNoColumns()
    },
    buildLineItemSampleRows(columns) {
      const cols = columns || []
      const samples = [
        this.buildSampleLineItemValues(cols, 0),
        this.buildSampleLineItemValues(cols, 1),
      ]
      return samples.map((row) => recomputeRow(row, cols))
    },
    buildSampleLineItemValues(columns, variant = 0) {
      const row = {}
      const a = variant === 0
      columns.forEach((col) => {
        const key = col.key
        const k = String(key || '').toLowerCase()
        if (this.isSrNoColumnKey(key, col.label)) {
          row[key] = variant + 1
        } else if (usesSacSelectWidget(col) || k.includes('sac') || k.includes('hsn')) {
          row[key] = this.defaultSacCode || '998311'
        } else if (col.type === 'date' || k === 'doj') {
          row[key] = a ? '01-Apr-26' : '15-May-26'
        } else if (k.includes('employeeid') || k === 'empid') {
          row[key] = a ? 'EMP-1042' : 'EMP-2108'
        } else if (k.includes('employeename') || k.includes('candidatename')) {
          row[key] = a ? 'Asha Verma' : 'Rohan Mehta'
        } else if (k.includes('itemname') || k.includes('description') || k.includes('goods')) {
          row[key] = a ? 'ITI Electricians' : 'Site Supervisors'
        } else if (k.includes('totalheadcount') || k === 'quantity') {
          row[key] = a ? 10 : 4
        } else if (k.includes('ratecard') || k === 'unitprice' || k === 'rate' || k === 'ctc') {
          row[key] = a ? 39790 : 55000
        } else if (k.includes('monthtoday') || k.includes('dailyrate')) {
          row[key] = a ? 1326.33 : 1833.33
        } else if (k.includes('presentday') || k.includes('billableday')) {
          row[key] = a ? 199 : 72
        } else if (k.includes('daytomonth') || k.includes('ratio')) {
          row[key] = a ? 1 : 0.85
        } else if (k.includes('payoutrate') || col.type === 'percentage' || k.includes('igstrate')) {
          row[key] = a ? 8.33 : 18
        } else if (k === 'bu') {
          row[key] = a ? 'Ops' : 'Tech'
        } else if (k === 'geo') {
          row[key] = a ? 'West' : 'South'
        } else if (k.includes('jrnumber') || k === 'jr') {
          row[key] = a ? 'JR-8821' : 'JR-9104'
        } else if (k.includes('band')) {
          row[key] = a ? 'B2' : 'B3'
        } else if (k.includes('expensemonth') || k.includes('billing')) {
          row[key] = a ? 'Jun 2026' : 'Jul 2026'
        } else if (col.type === 'currency' || col.type === 'number' || col.type === 'computed') {
          row[key] = col.type === 'computed' ? 0 : (a ? 1 : 2)
        } else {
          row[key] = a ? 'Sample A' : 'Sample B'
        }
      })
      return row
    },
    // BUG FIX (2026-07-21): this used to call bvModalEvt.preventDefault()
    // here whenever hasColumnConfigErrors was true, which blocked EVERY hide
    // path — the header X, Esc, AND backdrop click, not just the (separately
    // disabled) "Done" button. A blank/duplicate column name is trivially
    // easy to hit by accident (e.g. selecting a column-name field's text to
    // retype it leaves it briefly empty), and once hit there was no way out
    // of the modal at all — the backdrop covers the whole page underneath,
    // including the wizard's own Next/Back buttons, so from the user's
    // perspective the entire app appeared to "lose" its navigation. Now only
    // informs (toast) instead of trapping; the in-dialog red alert/per-row
    // error text still nudges toward fixing the name, and the "Done" button
    // itself no longer needs to be the only way out (see the template).
    onManageColumnsHide() {
      // Apply formula/type edits to existing rows (they only recompute on
      // cell edits otherwise — felt like "Save didn't work"). Also touch the
      // column array so the live invoice preview picks up label/order changes
      // immediately (no page reload).
      if (!this.hasColumnConfigErrors) {
        const changed = this.columnConfigEpoch !== this.columnEpochAtModalOpen
        this.touchDynamicColumnConfig()
        this.recomputeAllLineItems()
        this.$nextTick(() => this.syncAllA4PreviewFits())
        if (changed) {
          this.rememberClientTemplatePreference()
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Columns updated',
              text: 'Line-item columns now match Edit Columns on the invoice preview.',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        }
      } else {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Column issue left unresolved',
            text: 'One or more columns have a blank/duplicate name or an invalid formula — fix it any time via Manage Columns.',
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        })
      }
    },
    addColumn() {
      if (!this.newColumnToAdd) return
      const cfg = this.ensureActiveColumnConfig()
      // "+ Custom Field…" (2026-07-22) — a genuinely new column, not one of
      // the 4 fixed presets below, so its key is slugified from whatever
      // label the user typed rather than looked up from a preset list.
      if (this.newColumnToAdd === '__custom__') {
        const created = buildCustomColumn({
          label: this.customColumnLabel,
          type: this.customColumnType,
          existingKeys: cfg.map((c) => c && c.key),
          slugify: (name) => this.slugifyColumnKey(name),
        })
        if (!created) return
        if (cfg.some(c => c.key === created.key)) return
        if (created.type === 'computed' && !created.formula) {
          created.formula = this.defaultFormulaForColumn(created)
        }
        cfg.push(created)
        this.lineItems.forEach((item) => {
          if (this.rowMatchesEditTable(item)) this.$set(item, created.key, '')
        })
        this.newColumnToAdd = null
        this.customColumnLabel = ''
        this.customColumnType = getDefaultColumnType()
        this.columnConfigEpoch += 1
        this.$nextTick(() => this.recomputeAllLineItems())
        return
      }
      const preset = this.addableColumnPresets.find(p => p.key === this.newColumnToAdd)
        || (this.activeTemplate.lineItemColumns || []).find(c => c && c.key === this.newColumnToAdd)
      if (!preset) return
      if (cfg.some(c => c.key === preset.key)) return
      cfg.push(persistableColumn({
        key: preset.key,
        label: preset.label || preset.key,
        type: resolveColumnType(preset.type),
        required: !!preset.required,
        formula: displayFormula(preset.formula || ''),
        visible: true,
      }))
      this.lineItems.forEach((item) => {
        if (this.rowMatchesEditTable(item)) {
          this.$set(item, preset.key, item[preset.key] != null ? item[preset.key] : '')
        }
      })
      this.newColumnToAdd = null
      this.columnConfigEpoch += 1
      this.$nextTick(() => this.recomputeAllLineItems())
    },
    // Turns a free-typed label like "Overtime Hours" into a unique camelCase
    // key ("overtimeHours") — same shape as every other column key in this
    // file (rateCard, monthToDayConversion, …) so it reads/writes/formulas
    // exactly like a built-in one. Suffixes with 2/3/… if the slug already
    // collides with an existing column (e.g. two custom fields both named
    // "Amount") instead of silently overwriting/merging with it.
    slugifyColumnKey(label) {
      const words = String(label || '').trim().split(/[^a-zA-Z0-9]+/).filter(Boolean)
      const base = words
        .map((w, i) => (i === 0 ? w.charAt(0).toLowerCase() + w.slice(1) : w.charAt(0).toUpperCase() + w.slice(1)))
        .join('') || 'field'
      const existing = new Set((this.getActiveColumnConfigArray() || []).map(c => c.key))
      if (!existing.has(base)) return base
      let n = 2
      while (existing.has(`${base}${n}`)) n += 1
      return `${base}${n}`
    },
    // Headcount engine columns (no user formula) — still derived by
    // computeHeadcountBaseAmount when Computed is on. Not used to lock the
    // UI anymore; formulas and the Computed toggle stay fully editable.
    isHeadcountEngineColumn(col) {
      if (!col || col.formula) return false
      const active = this.getActiveColumnConfigArray()
      const headcountCols = findHeadcountCalcColumns(
        active && active.length
          ? active
          : (this.activeTemplate.lineItemColumns || []),
      )
      if (!headcountCols) return false
      return col.key === headcountCols.amountKey
        || (col.key === headcountCols.monthToDayKey && !!headcountCols.rateCardKey)
    },
    /** @deprecated Prefer isHeadcountEngineColumn — formulas are never locked. */
    isForcedComputedColumn(col) {
      return false
    },
    isColumnComputed(col) {
      return isComputedColumn(col)
    },
    // Data type is always editable (including SAC Select / Computed / Base Amount).
    onColumnTypeSelect(col, newType) {
      if (!col) return
      const next = applyColumnTypeChange(col, newType, {
        defaultFormula: this.defaultFormulaForColumn(col),
        defaultType: getDefaultColumnType(),
      })
      Object.keys(next).forEach((k) => this.$set(col, k, next[k]))
      this.columnConfigEpoch += 1
      this.$nextTick(() => this.recomputeAllLineItems())
    },
    // Reorder via drag-and-drop (vuedraggable). Order matters for formulas:
    // recomputeRow() evaluates computed columns in array order.
    onColumnConfigDragEnd() {
      this.columnConfigEpoch += 1
      this.$nextTick(() => this.recomputeAllLineItems())
    },
    onColumnFormulaInput(col) {
      if (col && col.formula != null) {
        const cleaned = displayFormula(col.formula)
        if (cleaned !== col.formula) this.$set(col, 'formula', cleaned)
      }
      this.columnConfigEpoch += 1
      this.$nextTick(() => this.recomputeAllLineItems())
    },
    setColumnVisible(col, checked) {
      if (!col) return
      this.$set(col, 'visible', !!checked)
      this.columnConfigEpoch += 1
    },
    defaultFormulaForColumn(col) {
      if (!col) return ''
      if (col.formula) return col.formula
      const keys = new Set((this.getActiveColumnConfigArray() || []).map((c) => c.key))
      const keyNorm = String(col.key || '').toLowerCase().replace(/[^a-z0-9]/g, '')
      if (col.key === 'amount') return 'quantity * unitPrice'
      // Qty (Man Months) — Round(presentDays / 26, 2); also accept unitPrice÷26
      // when that is what the author typed in the formula bar.
      if (
        keys.has('presentDays')
        && (col.key === 'quantity' || /^(qty|manmonth|qtymanmonth)/.test(keyNorm))
      ) {
        return 'Round(presentDays / 26, 2)'
      }
      if (col.key === 'monthToDayConversion' || keyNorm.includes('monthtoday')) {
        if (keys.has('unitPrice')) return 'Round(unitPrice / 26, 2)'
        if (keys.has('rateCard')) return 'Round(rateCard / 26, 2)'
        if (keys.has('monthlyRate')) return 'Round(monthlyRate / 26, 2)'
      }
      if (col.key === 'baseAmount') {
        if (keys.has('ctc') && keys.has('payoutRate')) return 'ctc * payoutRate / 100'
        if (keys.has('rate') && keys.has('dayToMonthRatio')) return 'rate * (dayToMonthRatio || 1)'
        if (keys.has('unitPrice') && keys.has('quantity')) return 'unitPrice * quantity'
        if (keys.has('monthToDayConversion') && keys.has('presentDays')) {
          return 'monthToDayConversion * presentDays'
        }
        if (keys.has('rateCard') && keys.has('presentDays')) {
          return 'Round(rateCard / 26, 2) * presentDays'
        }
      }
      return ''
    },
    // Toggle handler for the "Computed" switch in Manage Columns.
    // Off → manual entry (currency for amount/baseAmount). On → editable
    // formula via safeEval (quantity * unitPrice, quantity / 30, etc.).
    setColumnComputed(col, checked) {
      const next = toggleColumnComputed(col, checked, {
        defaultFormula: this.defaultFormulaForColumn(col),
        defaultType: getDefaultColumnType(),
      })
      Object.keys(next).forEach((k) => this.$set(col, k, next[k]))
      this.columnConfigEpoch += 1
      this.$nextTick(() => this.recomputeAllLineItems())
    },
    removeColumn(idx) {
      const cfg = this.getActiveColumnConfigArray()
      if (!cfg || idx < 0 || idx >= cfg.length) return
      const col = cfg[idx]
      // Any column (including Amount) is removable unless another formula
      // still references it. Deleted template columns can be re-added via Add.
      const dependents = cfg.filter(
        (c, i) => i !== idx && isComputedColumn(c) && formulaReferencesKey(c.formula, col.key),
      )
      if (dependents.length > 0) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Column still in use',
            text: `"${col.label || col.key}" is used in ${dependents.map((d) => `"${d.label || d.key}"`).join(', ')}'s formula — update or remove that formula first.`,
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        })
        return
      }
      cfg.splice(idx, 1)
      this.lineItems.forEach((item) => {
        if (this.rowMatchesEditTable(item)) this.$delete(item, col.key)
      })
      this.columnConfigEpoch += 1
      this.$nextTick(() => this.recomputeAllLineItems())
    },
    resetColumnConfig() {
      this.resetColumnConfigFromTemplate()
      const keys = new Set((this.getActiveColumnConfigArray() || []).map(c => c.key))
      this.lineItems.forEach((item) => {
        if (!this.rowMatchesEditTable(item)) return
        Object.keys(item).forEach((k) => {
          if (!keys.has(k) && !['cid', 'jid', 'sacCode', 'tableGroup', 'title'].includes(k)) this.$delete(item, k)
        })
      })
      this.reconcileLineItemsAfterPresetChange()
    },
    /**
     * Pre-tax amount for a line item. If the active column set includes
     * `baseAmount` (RPO / headcount / Bajaj / etc.), use that; otherwise use
     * computed `amount` / normalizeLineItemForApi (Standard qty×rate).
     */
    resolveLineItemBaseAmount(item) {
      if (!item) return 0
      const cols = this.lineItemValidationColumns || []
      const hasBaseCol = cols.some(c => c && c.key === 'baseAmount')
      if (hasBaseCol) {
        const fromBase = parseFloat(item.baseAmount)
        if (!Number.isNaN(fromBase)) return fromBase
      }
      const fromAmount = parseFloat(item.amount)
      if (!Number.isNaN(fromAmount)) return fromAmount
      const normalized = normalizeLineItemForApi(item, { isRpo: this.isRpoTemplate })
      return parseFloat(normalized && normalized.amount) || 0
    },
    /**
     * Build cid+jid → revenue from tagged line items only.
     * Aggregation: SUM baseAmount when the same candidate appears on multiple
     * lines (same approach as Candidate Revenue dashboard merge).
     * Skips non-candidate invoices and rows without real cid/jid.
     */
    buildTaggedCandidateRevenueMap() {
      const map = new Map()
      if (!(this.isCandidateCategory && this.isForCandidates === true)) return map
      ;(this.lineItems || []).forEach((item) => {
        if (!item || item.cid == null || item.jid == null) return
        const cid = String(item.cid).trim()
        const jid = String(item.jid).trim()
        if (!cid || !jid || cid === '0' || jid === '0') return
        const key = `${cid}-${jid}`
        const amt = this.resolveLineItemBaseAmount(item)
        const prev = map.has(key) ? map.get(key).revenue : 0
        map.set(key, {
          cid,
          jid,
          revenue: parseFloat(((prev || 0) + (Number.isNaN(amt) ? 0 : amt)).toFixed(2)),
        })
      })
      return map
    },
    /**
     * After successful invoice create: set ATS candidate revenue = line
     * baseAmount (aggregated). Uses PUT candidates/status/:cid/:jid — same
     * endpoint as InvoiceDashboard permanent revenue update.
     * Non-fatal: invoice already exists if this fails.
     */
    async syncTaggedCandidatesRevenueFromLineItems() {
      const map = this.buildTaggedCandidateRevenueMap()
      if (map.size === 0) return { updated: 0, failed: 0 }
      const userData = JSON.parse(localStorage.getItem('userData') || '{}')
      const updatedBy = `[{"title" : "${userData.first_name || ''} ${userData.last_name || ''}", "emp_id": "${userData.emp_id || ''}"}]`
      const results = await Promise.all(
        Array.from(map.values()).map(async ({ cid, jid, revenue }) => {
          try {
            // Payload: { updatedBy, revenue } — same shape as
            // InvoiceDashboard.savePermanentRevenue (revenue_confirmed omitted
            // so we do not overwrite an existing confirmation flag).
            const res = await candidateService.updateCandidateStatus(cid, jid, {
              updatedBy,
              revenue,
            })
            return !!(res && res.status === 'OK')
          } catch (err) {
            console.error('[InvoiceCreateManual] ATS revenue sync failed', cid, jid, err)
            return false
          }
        }),
      )
      const updated = results.filter(Boolean).length
      return { updated, failed: results.length - updated }
    },
    async onSubmit() {
      if (!this.isFormValid) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            text: this.formValidationMessage,
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        })
        return
      }

      const supportFilesPre = (this.invoiceFiles && this.invoiceFiles.length)
        ? this.invoiceFiles
        : (this.invoiceFile ? [this.invoiceFile] : [])
      const allowedSupportFiles = []
      let typeRejected = false
      for (const f of supportFilesPre) {
        const supportSizeError = getInvoiceFileSizeError(f)
        if (supportSizeError) {
          this.invoiceFile = null
          this.invoiceFiles = []
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'File too large',
              text: supportSizeError,
              icon: 'AlertCircleIcon',
              variant: 'warning',
            },
          })
          return
        }
        if (getInvoiceFileTypeError(f)) {
          typeRejected = true
          continue
        }
        allowedSupportFiles.push(f)
      }
      if (typeRejected) {
        this.invoiceFiles = allowedSupportFiles
        this.invoiceFile = allowedSupportFiles[0] || null
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'File type not allowed',
            text: INVOICE_FILE_TYPE_NOT_ALLOWED_MESSAGE,
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        })
      }
      // Supporting docs are optional on the current prod API (item 3 not deployed).
      // Excel/CSV is kept in the form but omitted from POST — old multer 500s.

      // Stamp any Sr No-style columns to their final row position one last
      // time — covers cases where a row was reordered/removed without going
      // through the wizard's own move/remove handlers (e.g. a restored draft).
      this.syncSrNoColumns()

      this.loading = true

      // Get user data for added_by field
      const userData = JSON.parse(localStorage.getItem('userData') || '{}')
      const addedBy = userData.emp_id || ''

      const formData = new FormData()
      formData.append('invoice_id', this.formData.invoiceId)
      formData.append('invoice_date', moment(this.formData.invoiceDate).format('YYYY-MM-DD'))
      formData.append('status', 'Raised') // Default status
      formData.append('category', this.formData.category)
      const locationName = String(this.formData.location || '').trim()
      formData.append('location', locationName)
      const submitLocationId = this.resolveSubmitLocationId()
      formData.append('vertical', this.formData.vertical)
      formData.append('sattva_gst', this.formData.invoiceSattvaInfo.gst || this.sattvaMasterData?.gstin || this.sattvaMasterData?.gst || '')
      // template_id / layout_style / column-config / MSME notes are item-3+
      // persist fields — omitted so old POST /invoice does not 500. Preview
      // calculations stay client-side.
      formData.append('billing_month', this.formData.billingMonth || '')
      // Always stamp the auto name right before submit so payload matches
      // the latest client / doc type / invoice # / billing month.
      this.syncAutoInvoiceName()
      formData.append('invoice_name', this.formData.invoiceName || '')
      formData.append('client', this.formData.client)
      formData.append('client_gst', this.formData.invoiceClientInfo.clientGST || this.clientMasterData?.gstin_no || '')
      formData.append('client_legal_name', this.formData.invoiceClientInfo.legalName || '')
      formData.append('client_address', this.formData.invoiceClientInfo.clientAddress || '')
      formData.append('client_state', this.formData.invoiceClientInfo.clientState || '')
      formData.append('place_of_supply', this.formData.invoiceClientInfo.placeOfSupply || '')
      formData.append('sattva_company_name', this.formData.invoiceSattvaInfo.companyName || '')
      formData.append('sattva_address', this.formData.invoiceSattvaInfo.address || '')
      // PAN + bank — present on Bill From / company-details but previously omitted
      // from multipart create (likely NOT NULL / PDF letterhead crash on server).
      const sattvaPan = String(this.formData.invoiceSattvaInfo.pan || '').trim()
      if (sattvaPan) formData.append('sattva_pan', sattvaPan)
      const bankName = String(this.formData.invoiceSattvaInfo.bankName || '').trim()
      const accountName = String(this.formData.invoiceSattvaInfo.accountName || '').trim()
      const accountNumber = String(this.formData.invoiceSattvaInfo.accountNumber || '').trim()
      const accountType = String(this.formData.invoiceSattvaInfo.accountType || '').trim()
      const ifscCode = String(this.formData.invoiceSattvaInfo.ifscCode || '').trim()
      if (bankName) formData.append('sattva_bank_name', bankName)
      if (accountName) formData.append('sattva_account_name', accountName)
      if (accountNumber) formData.append('sattva_account_number', accountNumber)
      if (accountType) formData.append('sattva_account_type', accountType)
      if (ifscCode) formData.append('sattva_ifsc', ifscCode)
      // SAC/HSN and LUT are separate header fields on POST /invoice.
      const sacCode = resolveSacHsnCode(this.formData.invoiceSattvaInfo) || this.defaultSacCode || ''
      const lutNo = resolveLutNo(this.formData.invoiceSattvaInfo) || ''
      if (sacCode) formData.append(SAC_HSN_API_FIELD, sacCode)
      if (lutNo) formData.append(LUT_API_FIELD, lutNo)
      formData.append('gst_type', this.formData.gstType)
      formData.append('po_number', String(this.formData.poNumber || '').trim())
      const packageCode = String(this.formData.packageCode || '').trim()
      if ((this.formData.hasPackageCode || packageCode) && packageCode) {
        formData.append('package_code', packageCode)
      }
      formData.append('description', this.formData.description || '')
      formData.append('added_by', addedBy)
      // Numeric totals as finite numbers (never empty string — crashes Number()/DECIMAL parsers).
      const numOrZero = (v) => {
        const n = typeof v === 'number' ? v : parseFloat(v)
        return Number.isFinite(n) ? n : 0
      }
      const baseAmount = numOrZero(this.subtotal)
      const gstAmount = numOrZero(this.gstAmount)
      const amountWithGst = numOrZero(baseAmount + gstAmount)
      const totalAmount = numOrZero(this.totalAmount)
      formData.append('base_amount', baseAmount)
      formData.append('gst', gstAmount)
      // Legacy CreateInvoice.vue uses gst_amount — send both for back-compat.
      formData.append('gst_amount', gstAmount)
      formData.append('amount_with_gst', amountWithGst)
      formData.append('total_amount', totalAmount)
      if (this.formData.tdsApplicable) {
        formData.append('tds_amount', numOrZero(this.calculatedTdsAmount))
      }

      // Credit / due / payment terms — always send when known (not only Collect N Pay).
      const creditPeriod = this.formData.creditPeriod != null && this.formData.creditPeriod !== ''
        ? String(this.formData.creditPeriod)
        : ''
      const dueDate = this.formData.dueDate
        || this.calculateDueDate
        || (this.formData.invoiceDate && creditPeriod
          ? moment(this.formData.invoiceDate).add(parseFloat(creditPeriod) || 0, 'days').format('YYYY-MM-DD')
          : '')
      const paymentTerms = this.formatPaymentTermsForSubmit(this.formData.paymentTerms)
        || creditPeriod
      if (creditPeriod) formData.append('credit_period', creditPeriod)
      if (dueDate) formData.append('due_date', dueDate)
      if (paymentTerms) formData.append('payment_terms', paymentTerms)

      // Collect N Pay
      if (this.formData.isCollectNPay) {
        formData.append('is_collect_n_pay', true)
        formData.append('pay_date', this.formData.payDate ? moment(this.formData.payDate).format('YYYY-MM-DD') : '')
      }

      // TDS
      if (this.formData.tdsApplicable) {
        formData.append('tds_applicable', true)
        const tdsPct = this.formData.tdsPercentage
        if (tdsPct != null && tdsPct !== '') formData.append('tds_percentage', numOrZero(tdsPct))
      }

      // Line Items
      if (!this.lineItems || !Array.isArray(this.lineItems) || this.lineItems.length === 0) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            text: 'Please add at least one line item',
            icon: 'AlertCircleIcon',
            variant: 'warning'
          }
        })
        this.loading = false
        return
      }

      const defaultSac = this.defaultSacCode
      const normalizedItems = []
      this.lineItems.forEach((item, index) => {
        if (!item) return
        const row = normalizeLineItemForApi(item, { isRpo: this.isRpoTemplate })
        if (!row) return
        if (!row.sacCode && defaultSac) row.sacCode = defaultSac
        // Flattened numerics must be finite (not '' / NaN).
        row.quantity = numOrZero(row.quantity)
        row.unitPrice = numOrZero(row.unitPrice)
        row.amount = numOrZero(row.amount)
        normalizedItems.push(row)
        if (row.itemName) formData.append(`lineItems[${index}][itemName]`, row.itemName)
        const rowTitle = (row.extra && row.extra.title) || ''
        if (rowTitle) formData.append(`lineItems[${index}][title]`, rowTitle)
        if (row.sacCode) formData.append(`lineItems[${index}][sacCode]`, row.sacCode)
        formData.append(`lineItems[${index}][quantity]`, row.quantity)
        formData.append(`lineItems[${index}][unitPrice]`, row.unitPrice)
        formData.append(`lineItems[${index}][amount]`, row.amount)
      })
      // line_items_json is a new persist field — omit on current prod create.

      // Candidates (if selected for Recruitment + Service Industry — see isCandidateCategory).
      // base_amount is taken from each tagged line's baseAmount (summed per
      // cid+jid), not the stale ATS revenue snapshot stored at tag time.
      if (this.isCandidateCategory && this.isForCandidates === true) {
        const revenueMap = this.buildTaggedCandidateRevenueMap()
        const candidatesForAPI = Array.from(revenueMap.values()).map(({ cid, jid, revenue }) => ({
          cid,
          jid,
          base_amount: revenue,
        }))
        if (candidatesForAPI.length > 0) {
          formData.append('candidates', JSON.stringify(candidatesForAPI))
        }
      }

      // Logo / layout extras omitted — old create 500s on companyLogo / unknown columns.
      const logoUrl = String(this.formData.invoiceSattvaInfo.logoUrl || '').trim()
      const attachedLogo = false

      // Prod multer still only allows pdf/png/jpg/txt. Excel/CSV stays in the
      // wizard but is not POSTed until item 3 fileFilter is deployed.
      const supportFiles = allowedSupportFiles.filter(isInvoiceProdMulterAllowed)
      const skippedExcel = allowedSupportFiles.length > 0 && !supportFiles.length
      if (skippedExcel) {
        console.warn(
          '[InvoiceCreateManual] invoiceFile omitted — Excel/CSV is not accepted by current prod multer',
        )
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Excel/CSV not uploaded',
            text: 'Current API does not accept Excel/CSV on create. The invoice will still be saved without that file.',
            icon: 'InfoIcon',
            variant: 'info',
          },
        })
      }
      if (supportFiles.length) {
        formData.append('document_type', this.formData.documentType || 'Tax Invoice')
        const primary = supportFiles[0]
        const fileName = (primary && primary.name) || 'invoice.pdf'
        formData.append('invoiceFile', primary, fileName)
        if (supportFiles.length > 1) {
          console.warn(
            `[InvoiceCreateManual] ${supportFiles.length} supporting files selected; `
            + 'backend accepts a single invoiceFile — sending the first only.',
          )
        }
      }

      // Diagnostic: which commonly-expected optional fields we intentionally omitted.
      const omittedOptional = []
      if (!sattvaPan) omittedOptional.push('sattva_pan')
      if (!bankName) omittedOptional.push('sattva_bank_name')
      if (!accountNumber) omittedOptional.push('sattva_account_number')
      if (!ifscCode) omittedOptional.push('sattva_ifsc')
      if (!creditPeriod) omittedOptional.push('credit_period')
      if (!dueDate) omittedOptional.push('due_date')
      if (!paymentTerms) omittedOptional.push('payment_terms')
      if (!attachedLogo && !logoUrl) {
        omittedOptional.push(
          INVOICE_CREATE_INCLUDE_LOGO
            ? 'companyLogo/company_logo_url'
            : 'companyLogo (skipped; set VUE_APP_INVOICE_CREATE_INCLUDE_LOGO=true)',
        )
      }
      if (!supportFiles.length) {
        omittedOptional.push('invoiceFile/document_type')
      }
      if (submitLocationId == null && locationName) {
        omittedOptional.push(`location_id (no numeric id for "${locationName}")`)
      }
      if (!(this.formData.hasPackageCode || packageCode) || !packageCode) omittedOptional.push('package_code')
      if (!this.formData.tdsApplicable) omittedOptional.push('tds_applicable/tds_percentage/tds_amount')
      if (!this.formData.isCollectNPay) omittedOptional.push('is_collect_n_pay/pay_date')
      console.info('[InvoiceCreateManual] create FormData omitted optional fields:', omittedOptional)

      try {
        // Use existing invoiceService.create() API
        // TODO: Switch to new API endpoint when available (e.g., enhanced-invoice-service.create() or new endpoint)
        const res = await invoiceService.create(formData)
        if (res.status === 'OK') {
          this.rememberClientTemplatePreference()
          this.capturePostSubmitPreviewFreeze()
          // Product: tagged candidate line baseAmount → ATS candidate revenue.
          // Trigger = successful create/submit (no separate "invoice selected" step).
          try {
            const sync = await this.syncTaggedCandidatesRevenueFromLineItems()
            if (sync.failed > 0) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Invoice created — revenue sync incomplete',
                  text: `Invoice saved, but ${sync.failed} candidate revenue update(s) failed. Update revenue from Candidate Revenue if needed.`,
                  icon: 'AlertCircleIcon',
                  variant: 'warning',
                },
              })
            }
          } catch (syncErr) {
            console.warn('[InvoiceCreateManual] ATS revenue sync error:', syncErr)
          }

          const created = (res && res.data) || {}
          // Prefer numeric/internal id for document APIs; fall back to invoice_id string.
          this.createdInvoiceKey = created.id
            || created.invoice_pk
            || created.invoiceId
            || created.invoice_id
            || this.formData.invoiceId
          this.createdInvoiceStatus = created.status || 'Raised'
          this.createdInvoiceRecord = {
            ...created,
            id: this.createdInvoiceKey,
            invoice_id: created.invoice_id || this.formData.invoiceId,
            status: this.createdInvoiceStatus,
          }

          // Best-effort: store a generated PDF immediately after create so the
          // invoice has a durable PDF reference even before the user clicks Download.
          // Must use postSubmitPreviewRoot — previewPanelRoot is under
          // `d-none d-lg-block` and has zero size on viewports < lg (and the
          // export used to toast "Download failed" for this background job).
          this.showPostSubmitModal = true
          this.$nextTick(async () => {
            try {
              await this.downloadInvoicePdf('postSubmitPreviewRoot', {
                download: false,
                returnBlob: true,
                persistGenerated: true,
                silent: true,
              })
            } catch (e) {
              console.warn('[InvoiceCreateManual] auto-persist generated PDF skipped:', e)
            }
            // Clear wizard/draft after PDF capture so sticky values cannot
            // re-save into a draft (toast remains).
            await this.resetCreateFormAfterSuccess()
          })

          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'New invoice created',
              text: 'Invoice created — download and upload the signed PDF next.',
              icon: 'CheckCircleIcon',
              variant: 'success'
            }
          })
        } else {
          const httpStatus = res.httpStatus || (typeof res.status === 'number' ? res.status : null)
          const errText = formatInvoiceApiError(
            res,
            'Server error creating invoice — check required fields / logo format',
          )
          const isFileType = /file type not allowed/i.test(errText)
          const isMulterField = /Unexpected field|invoiceFile only/i.test(errText)
          const errTitle = res.networkError || (!httpStatus && /cannot reach invoice api/i.test(errText))
            ? 'Cannot reach invoice API'
            : (isFileType
              ? 'File type not allowed'
              : (isMulterField
                ? 'File upload field rejected'
                : (httpStatus === 500
                  ? 'Server error creating invoice (500)'
                  : (httpStatus ? `Invoice create failed (${httpStatus})` : 'Error creating invoice'))))
          console.warn('[InvoiceCreateManual] create failed:', { httpStatus, message: errText, networkError: !!res.networkError, res })
          this.$toast({
            component: ToastificationContent,
            props: {
              title: errTitle,
              text: errText,
              icon: 'AlertCircleIcon',
              variant: 'danger',
              timeout: 12000,
            },
          })
        }
      } catch (error) {
        console.error('Error creating invoice:', error)
        const httpStatus = error?.response?.status
        const errText = formatInvoiceApiError(
          error,
          'Server error creating invoice — check required fields / logo format',
        )
        const isFileType = /file type not allowed/i.test(errText)
        const isMulterField = /Unexpected field|invoiceFile only/i.test(errText)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: isFileType
              ? 'File type not allowed'
              : (isMulterField
                ? 'File upload field rejected'
                : (httpStatus === 500
                  ? 'Server error creating invoice (500)'
                  : (!httpStatus && /cannot reach invoice api/i.test(errText)
                    ? 'Cannot reach invoice API'
                    : 'Error creating invoice'))),
            text: errText,
            icon: 'AlertCircleIcon',
            variant: 'danger',
            timeout: 12000,
          },
        })
      } finally {
        this.loading = false
      }
    },
    /**
     * Convert bundled SVG logo → PNG blob for multipart upload.
     * Backend sharp/PDF stacks often crash on image/svg+xml; PNG is safe.
     * Returns null if conversion fails (create proceeds without companyLogo).
     */
    async buildDefaultLogoPngBlob() {
      const src = sattvaDefaultLogo
      if (!src) return null
      // Already a raster URL from webpack — fetch as-is if not SVG.
      if (typeof src === 'string' && /\.(png|jpe?g|webp)(\?|$)/i.test(src)) {
        const blob = await fetch(src).then((r) => r.blob())
        return blob && blob.type && !/svg/i.test(blob.type) ? blob : null
      }
      const img = new Image()
      img.crossOrigin = 'anonymous'
      await new Promise((resolve, reject) => {
        img.onload = resolve
        img.onerror = () => reject(new Error('logo image load failed'))
        img.src = src
      })
      const w = img.naturalWidth || 480
      const h = img.naturalHeight || 160
      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d')
      if (!ctx) return null
      ctx.drawImage(img, 0, 0, w, h)
      const pngBlob = await new Promise((resolve) => {
        canvas.toBlob((b) => resolve(b), 'image/png')
      })
      return pngBlob || null
    },
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>

<style scoped>
/*
 * 2026-07-23 density pass — kill the large empty bands between navbar title,
 * draft banner, stepper, and form. Scoped to this page only.
 */
.invoice-create-manual {
  /* Pull closer to the sticky navbar page title (theme content padding stays). */
  margin-top: -0.25rem;
}
/* Slim bar — override b-alert's roomy default padding */
.invoice-draft-banner {
  padding: 0.3rem 0.65rem !important;
  margin-bottom: 0.35rem;
  line-height: 1.25;
}
.invoice-draft-banner__msg {
  font-size: 0.8125rem;
}
.invoice-draft-banner >>> .btn {
  padding: 0.15rem 0.45rem;
  font-size: 0.75rem;
  line-height: 1.25;
}
.invoice-draft-banner >>> .close {
  padding: 0.2rem 0.45rem;
  font-size: 1rem;
  line-height: 1;
}
.invoice-wizard-card {
  overflow: visible;
}
.invoice-wizard-card >>> .card-body {
  padding-top: 0.65rem;
  padding-bottom: 0.75rem;
  overflow: visible;
}

/*
 * Manage Columns modal — same ~28px compact input sizing convention used in
 * WizardClientStep.vue/WizardLineItemsStep.vue (the theme's own
 * $input-height/-sm run noticeably taller than plain Bootstrap, so
 * !important is needed here too to reliably win against it). Matched to the
 * exact same height/font-size as those two so switching between the wizard
 * and this modal doesn't feel like a different, less-dense app.
 */
.manage-columns-modal >>> .form-control,
.manage-columns-modal >>> .custom-select,
.line-items-config-modal .manage-columns-modal .form-control,
.line-items-config-modal .manage-columns-modal .custom-select {
  height: 26px !important;
  min-height: 26px !important;
  padding-top: 0.1rem !important;
  padding-bottom: 0.1rem !important;
  font-size: 0.78rem !important;
}
/*
 * Edit Columns density pass (2026-07-27): tighter single-row cards —
 * key inline beside name (no second line), smaller controls/gaps/padding.
 * Modal is xl via LineItemsConfigModal; flex-wrap still handles narrow widths.
 * Formula expansion (Computed on) unchanged — still its own dashed row.
 */
.edit-cols-intro {
  margin-bottom: 0.35rem;
  line-height: 1.3;
  font-size: 0.76rem;
}
.edit-cols-add-group >>> .col-form-label,
.edit-cols-add-group >>> label {
  margin-bottom: 0.1rem;
  font-size: 0.7rem;
  padding-top: 0;
  line-height: 1.15;
}
/* Single scroll region — modal-body handles overflow; no nested trap. */
.column-config-list {
  padding-right: 0.05rem;
}
.column-config-row {
  background-color: #fbfbfd;
  border: 1px solid #ebe9f1;
  border-radius: 0.28rem;
  padding: 0.15rem 0.3rem;
  margin-bottom: 0.18rem;
}
.column-config-row:last-child {
  margin-bottom: 0;
}
.column-config-header,
.column-row-main {
  display: grid;
  grid-template-columns: min-content minmax(140px, 1fr) 118px minmax(258px, auto) min-content;
  align-items: center;
  gap: 0.15rem 0.4rem;
}
.column-config-header {
  padding: 0.15rem 0.3rem 0.35rem;
  margin-bottom: 0.1rem;
  border-bottom: 1px solid #ebe9f1;
  color: #6e6b7b;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}
.column-config-h-toggles {
  display: grid;
  grid-template-columns: repeat(3, minmax(72px, 1fr));
  gap: 0.35rem;
  text-align: center;
}
.column-config-h-type {
  text-align: left;
}
.column-row-main {
  align-items: start;
}
.column-reorder-group {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding-top: 0.15rem;
}
.column-drag-handle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 24px;
  color: #b9b7c3;
  cursor: grab;
  border-radius: 0.2rem;
  user-select: none;
}
.column-drag-handle:hover {
  color: #6e6b7b;
  background: #f0f0f3;
}
.column-drag-handle:active {
  cursor: grabbing;
}
.column-config-row-ghost {
  opacity: 0.45;
  background: #eef3ff;
}
.column-config-row-drag {
  box-shadow: 0 4px 12px rgba(34, 41, 47, 0.12);
}
.column-name-group {
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.column-name-line {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
}
.column-name-input {
  flex: 1 1 auto;
  min-width: 0;
  width: auto;
  font-weight: 500;
}
.column-key-hint {
  flex-shrink: 0;
  font-size: 0.58rem;
  line-height: 1;
  max-width: 72px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.column-delete-btn {
  flex-shrink: 0;
  padding: 0.08rem 0.28rem;
  height: 24px;
  min-height: 24px;
  line-height: 1;
  margin-top: 0.1rem;
}
.column-setting-group {
  display: flex;
  align-items: flex-start;
  gap: 0.15rem;
  flex-shrink: 0;
  padding-top: 0.15rem;
}
.column-type-select {
  flex: 0 0 118px;
  width: 118px;
}
.custom-col-add-row {
  gap: 0.35rem;
}
.custom-col-name-input {
  min-width: 180px;
}
.custom-col-type-select {
  flex: 0 0 140px;
  width: 140px;
}
.column-toggle-group {
  display: grid;
  grid-template-columns: repeat(3, minmax(72px, 1fr));
  gap: 0.35rem;
  flex-shrink: 0;
  align-items: start;
  padding-top: 0.05rem;
}
.column-toggle-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  min-width: 0;
}
.column-toggle-label {
  font-size: 0.68rem;
  line-height: 1.1;
  font-weight: 600;
  color: #5e5873;
  white-space: nowrap;
  text-align: center;
}
.column-visible-toggle,
.column-required-toggle,
.column-computed-toggle {
  flex-shrink: 0;
  margin-bottom: 0 !important;
  margin-top: 0 !important;
  min-height: 0;
  padding-top: 0;
  padding-bottom: 0;
}
.column-visible-toggle >>> .custom-control-label,
.column-required-toggle >>> .custom-control-label,
.column-computed-toggle >>> .custom-control-label {
  font-size: 0;
  line-height: 0;
  padding-top: 0;
  padding-left: 0;
  width: 1.75rem;
  min-height: 1.1rem;
}
.column-visible-toggle >>> .custom-switch.custom-control,
.column-required-toggle >>> .custom-switch.custom-control,
.column-computed-toggle >>> .custom-switch.custom-control,
.column-visible-toggle.custom-control,
.column-required-toggle.custom-control,
.column-computed-toggle.custom-control {
  min-height: 1.1rem;
  padding-left: 1.75rem;
  padding-top: 0;
  padding-bottom: 0;
  margin-right: 0;
}
.column-error-text {
  margin-top: 0.05rem;
  font-size: 0.64rem;
  line-height: 1.15;
}
.column-formula-row {
  margin-top: 0.15rem;
  padding-top: 0.15rem;
  border-top: 1px dashed #ebe9f1;
}
.column-formula-hint {
  margin-top: 0.08rem;
  font-size: 0.64rem;
  line-height: 1.2;
}
.formula-hint-details {
  border: 1px solid #ebe9f1;
  border-radius: 0.28rem;
  background: #f8f8fb;
}
.formula-hint-summary {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.7rem;
  font-weight: 600;
  color: #00AC99;
  padding: 0.3rem 0.45rem;
  cursor: pointer;
  list-style: none;
  user-select: none;
}
.formula-hint-summary::-webkit-details-marker {
  display: none;
}
.formula-hint-summary::before {
  content: '▸';
  font-size: 0.65rem;
  color: #b9b7c3;
  transition: transform 0.15s ease;
}
.formula-hint-details[open] .formula-hint-summary::before {
  transform: rotate(90deg);
}
.formula-hint-box {
  font-size: 0.66rem;
  line-height: 1.35;
  color: #6e6b7b;
  border-top: 1px solid #ebe9f1;
  border-left: 3px solid #00AC99;
  padding: 0.35rem 0.45rem;
  margin: 0;
}
.formula-hint-icon {
  flex-shrink: 0;
  color: #00AC99;
}
.edit-cols-reset-row {
  padding-top: 0.15rem;
}
@media (max-width: 991px) {
  .column-config-header {
    display: none;
  }
  .column-row-main {
    grid-template-columns: min-content 1fr min-content;
    grid-template-areas:
      "reorder name delete"
      "type type type"
      "toggles toggles toggles";
  }
  .column-reorder-group { grid-area: reorder; }
  .column-name-group { grid-area: name; }
  .column-delete-btn { grid-area: delete; justify-self: end; }
  .column-type-group { grid-area: type; }
  .column-toggle-group {
    grid-area: toggles;
    justify-self: stretch;
    width: 100%;
  }
}
.formula-hint-box code,
.column-key-hint code {
  font-size: 0.9em;
  background: #f4f4f6;
  padding: 0 0.2rem;
  border-radius: 0.15rem;
}

/*
 * Persistent live-preview panel (split-view layout, >=lg viewports) — sticks
 * in place as the (usually taller) form column scrolls, with its own
 * independent scroll region so a long line-item table in the preview never
 * pushes the wizard's Next/Back buttons off-screen or grows the page itself.
 */
.live-preview-sticky {
  position: sticky;
  top: 0.5rem;
}
/* Shared compact band for Live Preview header + wizard step/fullscreen row
   (2026-07-23) — was mb-1 (~1rem) above/below controls; keep a tight gap
   so the toolbar doesn't dominate the first viewport. */
.preview-toolbar-row {
  margin-bottom: 0.25rem;
  min-height: 28px;
}
.preview-live-badge {
  padding: 0.15rem 0.4rem;
  font-size: 0.7rem;
  line-height: 1.1;
  font-weight: 500;
}
/* Inline Style / Line items selects in the Live Preview toolbar
   (2026-07-22 / 2026-07-23) — same ~28px compact control convention used
   throughout this file. Line items needs a bit more width for names like
   "Placement (CTC)" / "Auto (recommended)". */
.preview-toolbar-controls {
  gap: 0.15rem 0;
}
.preview-style-select,
.preview-line-items-select {
  height: 28px !important;
  min-height: 28px !important;
  padding-top: 0.2rem !important;
  padding-bottom: 0.2rem !important;
  font-size: 0.8rem !important;
  width: auto;
}
.preview-style-select {
  min-width: 110px;
}
.preview-line-items-select {
  min-width: 140px;
  max-width: 190px;
}
.live-preview-scroll {
  max-height: calc(100vh - 7rem);
  overflow-y: auto;
  padding-right: 0.25rem;
}
/*
 * A4 preview stage (2026-07-23): desk-like backdrop + centered true A4
 * sheet. Fit-to-width is done with transform:scale on .a4-preview-scaler
 * (see syncA4PreviewFit) — never by shrinking font-size on the sheet,
 * which used to crush GST/line-item/bank text and desync PDF export.
 */
.a4-preview-stage {
  width: 100%;
  padding: 0.65rem 0.35rem 1rem;
  background:
    linear-gradient(180deg, #ececef 0%, #e4e4e8 100%);
  border-radius: 0.35rem;
  overflow: hidden;
}
.a4-preview-stage--modal {
  max-height: min(70vh, calc(100vh - 12rem));
  overflow-x: hidden;
  overflow-y: auto;
}
.a4-preview-scaler {
  will-change: transform;
}
.live-preview-modal-content {
  /* Modal body already scrolls via the stage; avoid double bars. */
  overflow: visible;
  max-width: 100%;
}
.invoice-post-submit-preview {
  max-height: min(42vh, 360px);
  overflow-x: hidden;
  overflow-y: auto;
}
.invoice-modal-helper {
  color: #3d3d3d;
  font-size: 0.875rem;
}
</style>

<style lang="scss">
/* Preview modal: keep sheet inside the viewport with internal scroll only. */
.invoice-preview-modal-shell .modal-dialog {
  max-width: min(1140px, calc(100vw - 1.5rem));
  margin: 0.75rem auto;
}
.invoice-preview-modal-shell .modal-content {
  max-height: calc(100vh - 1.5rem);
}
.invoice-preview-modal-shell .invoice-preview-modal-body {
  overflow-y: auto;
  max-height: calc(100vh - 10.5rem);
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
}

/* Invoice submitted dialog only — flatten elevation, keep borders.
   Unscoped because b-modal portals to <body>. */
.invoice-submitted-modal {
  .modal-content {
    box-shadow: none !important;
    border: 1px solid #d8d6de;
  }

  .modal-header .close,
  .modal-header .close:hover,
  .modal-header .close:focus,
  .modal-header .close:active {
    box-shadow: none !important;
  }

  .alert,
  .badge,
  .btn,
  .btn:hover,
  .btn:focus,
  .btn:active,
  .btn:not(:disabled):not(.disabled):active,
  .btn:not(:disabled):not(.disabled).active {
    box-shadow: none !important;
  }

  .a4-preview-stage,
  .invoice-post-submit-preview {
    box-shadow: none !important;
    border: 1px solid #d8d6de;
  }

  .generic-invoice-preview {
    box-shadow: none !important;
  }

  .signed-invoice-workflow {
    box-shadow: none !important;
  }
}
</style>
